import {
  actionButton,
  alertBox,
  asArray,
  cleanText,
  clampPercent,
  emptyState,
  ensureControlCentreStyles,
  escapeHtml,
  formatCount,
  formatDate,
  formatDateTime,
  formatMoney,
  icon,
  loadingCards,
  metricCard,
  pageHeading,
  progressBar,
  safeNumber,
  statusBadge,
  statusTone,
} from '../_shared/control-centre-ui.js';
import {
  CONTROL_CENTRE_ASYNC_ACTIONS,
  acknowledgeTutorSupportCase,
  createIncident,
  createTutorAccount,
  createTutorAccountBillingPortal,
  createTutorAccountCheckout,
  elevateOwnerSessionWithPasskey,
  loadControlCentreSection,
  loadOwnerSession,
  loadTutorAccount,
  isControlCentreAsyncFeedbackEnabled,
  performTutorAccountAction,
  registerOwnerPasskey,
  retryOperation,
  resolveIncident,
  runControlCentreActivity,
  savePlanCapacity,
  saveTutorAccountCapacity,
  saveTutorAccountPolicy,
  simulatePlanCapacity,
  simulateTutorAccountCapacity,
  simulateTutorAccountPolicy,
  updateIncident,
} from '../_shared/control-centre-api.js';
import {
  plainAction,
  plainAlertTitle,
  plainBoolean,
  plainEvent,
  plainMethod,
  plainSettingLabel,
  plainSettingValueLabel,
  plainStatus,
  plainSystemMessage,
} from './plain-language.js';
import { createGuideContentEditorState, renderControlCentreGuideEditor, handleControlCentreGuideAction, handleControlCentreGuideInput } from './guide-content-editor.js';
import { installScrollIndicators } from '../../../../src/ui/scroll_indicators.js';
import { installControlCentreSelects, focusControlCentreSelect } from '../_shared/control-centre-select.js';

const ROOT_STATE = new WeakMap();
const THEME_STORAGE_KEY = 'ttoria-control-centre:theme:v1';
const ROUTE_STORAGE_KEY = 'ttoria-control-centre:route:v1';
const AUTO_REFRESH_MS = 60 * 1000;

const NAV_ITEMS = Object.freeze([
  {
    id: 'today',
    label: 'Today',
    title: 'Today',
    detail: 'See what needs your attention across TToria.',
    icon: 'today',
  },
  {
    id: 'accounts',
    label: 'Tutor Accounts',
    title: 'Tutor Accounts',
    detail: 'Create, find, support and safely manage independent tutor accounts.',
    icon: 'accounts',
  },
  {
    id: 'billing',
    label: 'Plans & Billing',
    title: 'Plans & Billing',
    detail: 'Review subscriptions and billing problems without handling card details.',
    icon: 'billing',
  },
  {
    id: 'usage',
    label: 'Activity & Costs',
    title: 'Activity & Costs',
    detail: 'See how accounts use TToria and understand clearly labelled cost estimates.',
    icon: 'usage',
  },
  {
    id: 'operations',
    label: 'Changes & Progress',
    title: 'Changes & Progress',
    detail: 'Follow every requested change from start until each service confirms it.',
    icon: 'operations',
  },
  {
    id: 'incidents',
    label: 'Service Problems',
    title: 'Service Problems',
    detail: 'Find service problems, affected accounts and safe recovery actions.',
    icon: 'incidents',
  },
  {
    id: 'security',
    label: 'Security & Privacy',
    title: 'Security & Privacy',
    detail: 'Protect sign-ins and private information without viewing teaching content.',
    icon: 'security',
  },
  {
    id: 'audit',
    label: 'Activity History',
    title: 'Activity History',
    detail: 'See who requested each change, why they requested it, and what happened.',
    icon: 'audit',
  },
  {
    id: 'settings',
    label: 'Settings',
    title: 'Settings',
    detail: 'Review plans, safety rules, alerts, and owner sign-in protection.',
    icon: 'settings',
  },
]);

const ACCOUNT_TABS = Object.freeze([
  { id: 'overview', label: 'Overview' },
  { id: 'people', label: 'People & Sign-in' },
  { id: 'features', label: 'Features' },
  { id: 'subscription', label: 'Plan & Invoices' },
  { id: 'usage', label: 'Activity & Costs' },
  { id: 'health', label: 'Service Status' },
  { id: 'support', label: 'Help & Recent Changes' },
  { id: 'security', label: 'Security & Privacy' },
  { id: 'audit', label: 'Change History' },
]);

const ACCOUNT_ACTIONS = Object.freeze({
  'ack-support-case': {
    label: 'Mark problem received',
    title: 'Tell the tutor this problem was received?',
    detail: 'TToria will mark this structured problem report as received so the tutor can see that support has started reviewing it.',
    impact: 'This sends only the report reference and status. It never sends or reveals student teaching content.',
    confirmLabel: 'Mark received',
    tone: 'primary',
    icon: 'info',
    requiresReason: true,
    requiresTypedName: false,
  },
  'resend-invitation': {
    label: 'Resend invitation',
    title: 'Send a new activation invitation?',
    detail: 'The previous invitation link will stop working. A new single-use link will be sent.',
    impact: 'The tutor keeps the same pending account. No teaching information changes.',
    confirmLabel: 'Send new invitation',
    tone: 'primary',
    icon: 'mail',
    requiresReason: true,
    requiresTypedName: false,
  },
  'cancel-invitation': {
    label: 'Cancel invitation',
    title: 'Cancel this activation invitation?',
    detail: 'The current activation link will stop working.',
    impact: 'The pending tutor account remains available for a future replacement invitation.',
    confirmLabel: 'Cancel invitation',
    tone: 'danger',
    icon: 'mail',
    requiresReason: true,
    requiresTypedName: false,
  },
  'password-reset': {
    label: 'Send password reset',
    title: 'Send a password-reset link?',
    detail: 'The tutor will receive a secure, time-limited link. Their current password is never shown.',
    impact: 'The tutor stays signed in on existing devices unless you use “Sign out on all devices” separately.',
    confirmLabel: 'Send reset link',
    tone: 'primary',
    icon: 'key',
    requiresReason: true,
    requiresTypedName: false,
  },
  'revoke-sessions': {
    label: 'Sign out on all devices',
    title: 'Sign this tutor out everywhere?',
    detail: 'The tutor will be signed out of Tutor Desk and Student Portal on every device.',
    impact: 'The tutor must sign in again. Their account and teaching information remain unchanged.',
    confirmLabel: 'Sign out on all devices',
    tone: 'danger',
    icon: 'lock',
    requiresReason: true,
    requiresTypedName: false,
  },
  'start-trial': {
    label: 'Start trial',
    title: 'Start a trial for this account?',
    detail: 'Choose the date when the trial should end.',
    impact: 'The account receives trial subscription access until that date. Billing and feature rules still apply.',
    confirmLabel: 'Start trial',
    tone: 'primary',
    icon: 'play',
    requiresReason: true,
    requiresTypedName: false,
    requiresTrialEnd: true,
  },
  'extend-trial': {
    label: 'Extend trial',
    title: 'Extend this account’s trial?',
    detail: 'Choose the new future date when the trial should end.',
    impact: 'The existing trial continues to the reviewed date. This does not create a Stripe charge.',
    confirmLabel: 'Extend trial',
    tone: 'primary',
    icon: 'calendar',
    requiresReason: true,
    requiresTypedName: false,
    requiresTrialEnd: true,
  },
  'end-trial': {
    label: 'End trial',
    title: 'End this account’s trial now?',
    detail: 'TToria will safely end the trial and record the change.',
    impact: 'TToria will recalculate what the account can use. Teaching information is not deleted.',
    confirmLabel: 'End trial',
    tone: 'danger',
    icon: 'pause',
    requiresReason: true,
    requiresTypedName: false,
  },
  'assign-plan': {
    label: 'Assign plan',
    title: 'Assign a different plan?',
    detail: 'Choose an approved plan from TToria’s plan catalogue.',
    impact: 'The account’s available features and student limit may change after TToria finishes checking the update.',
    confirmLabel: 'Assign plan',
    tone: 'primary',
    icon: 'billing',
    requiresReason: true,
    requiresTypedName: false,
    requiresPlan: true,
  },
  'complimentary-period': {
    label: 'Give complimentary access',
    title: 'Give this account complimentary access?',
    detail: 'Choose exactly when the complimentary period should end.',
    impact: 'The account is not charged for this period. Access still follows approved features and security rules.',
    confirmLabel: 'Give complimentary access',
    tone: 'primary',
    icon: 'billing',
    requiresReason: true,
    requiresTypedName: false,
    requiresComplimentaryEnd: true,
  },
  'stripe-checkout': {
    label: 'Start hosted Checkout',
    title: 'Create a secure Stripe Checkout link?',
    detail: 'Stripe will open its own hosted test Checkout page for this tutor account.',
    impact: 'TToria does not see or store card details. Live charging remains disabled until its separate production approval.',
    confirmLabel: 'Open Stripe Checkout',
    tone: 'primary',
    icon: 'external',
    requiresReason: true,
    requiresTypedName: false,
  },
  'stripe-portal': {
    label: 'Open billing portal',
    title: 'Create a secure Stripe Billing Portal link?',
    detail: 'Stripe will open its own short-lived hosted portal for this tutor account.',
    impact: 'This works only after Stripe has created a customer record. TToria does not see or store card details.',
    confirmLabel: 'Open billing portal',
    tone: 'primary',
    icon: 'external',
    requiresReason: true,
    requiresTypedName: false,
  },
  full: {
    label: 'Set full access',
    title: 'Set this account to full service access?',
    detail: 'TToria will ask Tutor Desk and Student Portal to apply full access.',
    impact: 'Subscription, plan, security, and feature rules still apply. An account locked for security review stays protected.',
    confirmLabel: 'Set full access',
    tone: 'primary',
    icon: 'play',
    requiresReason: true,
    requiresTypedName: false,
  },
  grace: {
    label: 'Allow temporary access',
    title: 'Temporarily keep this account working?',
    detail: 'The account continues operating temporarily while a subscription problem is resolved.',
    impact: 'This owner decision is saved permanently in the change history.',
    confirmLabel: 'Allow temporary access',
    tone: 'primary',
    icon: 'calendar',
    requiresReason: true,
    requiresTypedName: false,
  },
  'read-only': {
    label: 'Make view only',
    title: 'Make this account view only?',
    detail: 'The tutor can review historical information but cannot create bookings, payments, edits or AI generations.',
    impact: 'Student Portal will also become view only after every service confirms the change.',
    confirmLabel: 'Make view only',
    tone: 'danger',
    icon: 'lock',
    requiresReason: true,
    requiresTypedName: true,
  },
  suspend: {
    label: 'Suspend account',
    title: 'Temporarily suspend this account?',
    detail: 'Tutor Desk and Student Portal access will become unavailable after every service confirms the change.',
    impact: 'This is reversible. The tutor’s information is retained and can be restored.',
    confirmLabel: 'Suspend account',
    tone: 'danger',
    icon: 'pause',
    requiresReason: true,
    requiresTypedName: true,
  },
  quarantine: {
    label: 'Lock for security review',
    title: 'Lock this account for a security review?',
    detail: 'Active sessions are revoked immediately and access becomes unavailable.',
    impact: 'Use this only for a security concern. The account can be restored after review.',
    confirmLabel: 'Lock account',
    tone: 'danger',
    icon: 'shield',
    requiresReason: true,
    requiresTypedName: true,
  },
  restore: {
    label: 'Restore account',
    title: 'Restore access to this account?',
    detail: 'TToria will restore the account’s allowed access and update Student Portal.',
    impact: 'A receipt will show when each service confirms the change. Billing rules still apply.',
    confirmLabel: 'Restore access',
    tone: 'primary',
    icon: 'play',
    requiresReason: true,
    requiresTypedName: false,
  },
  close: {
    label: 'Schedule closure',
    title: 'Schedule this account for closure?',
    detail: 'TToria waits at least 24 hours and never closes before the Stripe paid-through date. A 30-day restore window follows closure.',
    impact: 'This is not permanent deletion. Cancel the Stripe subscription first when required; teaching information is kept and permanent deletion is turned off.',
    confirmLabel: 'Schedule closure',
    tone: 'danger',
    icon: 'alert',
    requiresReason: true,
    requiresTypedName: true,
  },
  'cancel-closure': {
    label: 'Cancel pending closure',
    title: 'Cancel the pending account closure?',
    detail: 'TToria will stop the closure while it is still in the 24-hour safety waiting period.',
    impact: 'The account returns to an open account stage after all required services confirm the change.',
    confirmLabel: 'Cancel closure',
    tone: 'primary',
    icon: 'retry',
    requiresReason: true,
    requiresTypedName: true,
  },
  export: {
    label: 'Request account export',
    title: 'Prepare a protected account export?',
    detail: 'TToria will request an export through the tutor account’s protected data service.',
    impact: 'The receipt tracks the request. No account access or teaching information is changed.',
    confirmLabel: 'Request export',
    tone: 'primary',
    icon: 'external',
    requiresReason: true,
    requiresTypedName: false,
  },
});

const WIZARD_STEPS = Object.freeze([
  { id: 'business', label: 'Tutor or business' },
  { id: 'owner', label: 'Owner email' },
  { id: 'plan', label: 'Plan or trial' },
  { id: 'features', label: 'Optional features' },
  { id: 'effective', label: 'Start date' },
  { id: 'review', label: 'Check your answers' },
]);

const FEATURE_LABELS = Object.freeze({
  ai_summary: 'AI Summary',
  student_portal: 'Student Portal',
  progress_assessment: 'Progress & Assessment',
  calendar: 'Calendar',
  payments: 'Payments',
  library: 'Library',
  web_inquiries: 'Web Inquiries',
});

function resolveRoot(ctx = {}) {
  if (ctx.root instanceof HTMLElement) return ctx.root;
  if (ctx.mountEl instanceof HTMLElement) return ctx.mountEl;
  return null;
}

function createId(prefix = 'cc') {
  try {
    return `${prefix}-${crypto.randomUUID()}`;
  } catch {
    return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

function defaultReportingRange(days = 30) {
  const now = new Date();
  const to = new Date(now.getTime() - now.getTimezoneOffset() * 60 * 1000);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - Math.max(1, days) + 1);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

function futureDateInput(days = 14) {
  const date = new Date();
  date.setDate(date.getDate() + Math.max(1, safeNumber(days, 14)));
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

function endOfDateIso(value = '') {
  const date = cleanText(value);
  return /^\d{4}-\d{2}-\d{2}$/u.test(date) ? `${date}T23:59:59.000Z` : date;
}

function reportingRangeError(from = '', to = '') {
  const start = new Date(`${cleanText(from)}T00:00:00.000Z`);
  const end = new Date(`${cleanText(to)}T00:00:00.000Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/u.test(cleanText(from))
    || !/^\d{4}-\d{2}-\d{2}$/u.test(cleanText(to))
    || Number.isNaN(start.getTime())
    || Number.isNaN(end.getTime())
  ) return 'Choose a valid start and end date.';
  if (start > end) return 'The start date must be on or before the end date.';
  const days = Math.floor((end - start) / 86400000) + 1;
  if (days > 366) return 'Choose a reporting period of 366 days or less.';
  return '';
}

function escapeSelectorValue(value = '') {
  const text = String(value ?? '');
  try {
    const css = globalThis.CSS;
    if (css && typeof css.escape === 'function') return css.escape(text);
  } catch {}
  return text.replaceAll('\\', '\\\\').replaceAll('"', '\\"');
}

function objectValue(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function firstValue(source = {}, keys = [], fallback = '') {
  const record = objectValue(source);
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return fallback;
}

function reportingPeriodLabel(state, source = {}) {
  const selectedRange = objectValue(source.selectedRange);
  const from = firstValue(selectedRange, ['from'], state.reportingFrom);
  const to = firstValue(selectedRange, ['to'], state.reportingTo);
  return `${formatDate(from)} to ${formatDate(to)}`;
}

function usageCoverageComplete(source = {}) {
  const coverage = objectValue(source.usageCoverage);
  return !Object.keys(coverage).length || coverage.complete === true;
}

function lessonTotalDisplay(source = {}, keys = []) {
  return usageCoverageComplete(source)
    ? formatCount(firstValue(source, keys, 0))
    : 'Unavailable';
}

function firstArray(source = {}, keys = []) {
  const record = objectValue(source);
  for (const key of keys) {
    if (Array.isArray(record[key])) return record[key];
  }
  return [];
}

function effectiveFeatureList(account = {}) {
  const direct = firstArray(account, ['features', 'effectiveFeatures']);
  if (direct.length) return direct;
  const policy = objectValue(account.policy);
  const effective = objectValue(policy.effectiveFeatures);
  const explanations = asArray(policy.explanation).map(cleanText).filter(Boolean);
  return Object.entries(effective).map(([key, enabled]) => ({
    featureId: key,
    key,
    label: FEATURE_LABELS[key] || key.replaceAll('_', ' '),
    enabled: enabled === true,
    explanation: explanations.find((item) => item.toLowerCase().includes(key.replaceAll('_', ' ')))
      || (enabled === true ? 'Included in the account’s current feature settings.' : 'Not included in the account’s current feature settings.'),
    protected: key === 'web_inquiries',
  }));
}

function metricRows(source = {}) {
  return firstArray(source, ['usage', 'metrics']);
}

function metricValue(rows = [], metricId = '', { latest = false } = {}) {
  const matches = rows.filter((row) => cleanText(row?.metricId) === metricId);
  if (!matches.length) return 0;
  if (latest) {
    const sorted = [...matches].sort((left, right) =>
      cleanText(right?.date || right?.freshnessAt).localeCompare(cleanText(left?.date || left?.freshnessAt))
    );
    return safeNumber(sorted[0]?.value, 0);
  }
  return matches.reduce((total, row) => total + safeNumber(row?.value, 0), 0);
}

function friendlySystemCheckStatus(value = '') {
  const normalized = cleanText(value).toLowerCase();
  if (['healthy', 'passed', 'pass', 'success', 'successful', 'ok', 'complete', 'completed'].includes(normalized)) {
    return 'Working';
  }
  if (['failed', 'failure', 'error', 'unhealthy', 'critical'].includes(normalized)) {
    return 'Needs attention';
  }
  if (['running', 'pending', 'checking', 'in_progress'].includes(normalized)) {
    return 'Checking now';
  }
  return 'Not checked yet';
}

function minorUnitsToMajor(value) {
  return safeNumber(value, 0) / 100;
}

function normalizeSectionPayload(payload) {
  const value = objectValue(payload);
  return objectValue(value.data || value.result || value);
}

function themeFromStorage(root) {
  try {
    const stored = root.ownerDocument.defaultView.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return root.ownerDocument.defaultView.matchMedia?.('(prefers-color-scheme: light)')?.matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

function setTheme(state, theme = 'dark', { persist = true } = {}) {
  const normalized = theme === 'light' ? 'light' : 'dark';
  state.theme = normalized;
  const doc = state.root.ownerDocument;
  doc.documentElement.setAttribute('data-ttoria-theme', normalized);
  doc.documentElement.style.colorScheme = normalized;
  if (persist) {
    try {
      doc.defaultView.localStorage.setItem(THEME_STORAGE_KEY, normalized);
    } catch {}
  }
}

function currentNavItem(state) {
  return NAV_ITEMS.find((item) => item.id === state.activeSection) || NAV_ITEMS[0];
}

function deriveCanMutate(ctx = {}) {
  const role = cleanText(ctx?.auth?.role || ctx?.sharedState?.userRole).toLowerCase();
  const recognizedOwner = ['platform_owner', 'owner', 'admin'].includes(role);
  try {
    return Boolean(ctx?.capabilities?.can?.('platform.control.manage', recognizedOwner));
  } catch {
    return recognizedOwner;
  }
}

function initialOwnerSession(ctx = {}) {
  const auth = objectValue(ctx?.auth || ctx?.sharedState?.auth);
  const access = objectValue(auth.access);
  const passkeyCount = safeNumber(firstValue(auth, ['passkeyCount', 'registeredPasskeys'], 0), 0);
  return {
    authenticated: auth.authenticated !== false,
    elevated: auth.elevated === true || access.canMutate === true,
    elevatedUntil: firstValue(auth, ['elevatedUntil'], firstValue(access, ['elevatedUntil'], '')),
    passkeyCount,
    passkeyReadiness: firstValue(
      auth,
      ['passkeyReadiness'],
      passkeyCount >= 2 ? 'ready' : 'second_passkey_required'
    ),
    totpFallbackConfigured: auth.totpFallbackConfigured === true,
    csrfToken: firstValue(auth, ['csrfToken'], ''),
  };
}

function createState(root, ctx) {
  const reportingRange = defaultReportingRange();
  const ownerSession = initialOwnerSession(ctx);
  const defaultRoute = (() => {
    try {
      const stored = root.ownerDocument.defaultView.sessionStorage.getItem(ROUTE_STORAGE_KEY);
      return NAV_ITEMS.some((item) => item.id === stored) ? stored : 'today';
    } catch {
      return 'today';
    }
  })();
  return {
    root,
    ctx,
    asyncFeedbackEnabled: isControlCentreAsyncFeedbackEnabled(ctx),
    theme: themeFromStorage(root),
    activeSection: defaultRoute,
    sectionData: new Map(),
    sectionMeta: new Map(),
    loadingSection: '',
    sectionError: null,
    loadController: null,
    accountController: null,
    selectedAccountId: '',
    accountDetails: null,
    accountLoading: false,
    accountError: null,
    accountTab: 'overview',
    accountQuery: '',
    accountStatus: '',
    accountPlan: '',
    reportingFrom: reportingRange.from,
    reportingTo: reportingRange.to,
    reportingRangeError: '',
    menuOpen: false,
    wizard: null,
    actionDialog: null,
    policyDialog: null,
    capacityDialog: null,
    retryDialog: null,
    incidentDialog: null,
    ownerSecurityDialog: null,
    guideContentEditor: createGuideContentEditorState(),
    ownerSession,
    ownerSessionError: '',
    receipt: null,
    announcement: '',
    toast: '',
    focusPageHeading: false,
    modalReturnFocus: null,
    canMutate: deriveCanMutate(ctx) && ownerSession.elevated === true,
    readOnlyReason: '',
    cleanup: [],
    autoRefreshTimer: 0,
    destroyed: false,
  };
}

function findControlCentreAsyncFeedbackControl(state, selector = '') {
  const normalized = cleanText(selector);
  if (!normalized) return null;
  const control = state.root.querySelector(normalized);
  return control instanceof HTMLElement ? control : null;
}

function emptyControlCentrePendingPresentation() {
  return { restore() {}, focusReplacement() {} };
}

function beginControlCentrePendingPresentation(state, control, {
  controlId = '',
  busyLabel = '',
} = {}) {
  if (!state.asyncFeedbackEnabled) {
    return emptyControlCentrePendingPresentation();
  }
  if (!(control instanceof HTMLElement) || !control.isConnected) {
    throw new Error(`Control Centre async-feedback control '${cleanText(controlId, 'unknown')}' is unavailable.`);
  }
  const labelNode = Array.from(control.children)
    .find((child) => child instanceof HTMLElement && child.tagName === 'SPAN');
  const testId = cleanText(control.getAttribute('data-testid'));
  const action = cleanText(control.getAttribute('data-cc-action'));
  const selector = testId
    ? `[data-testid="${escapeSelectorValue(testId)}"]`
    : action
      ? `[data-cc-action="${escapeSelectorValue(action)}"]`
      : '';
  const record = {
    disabled: 'disabled' in control ? Boolean(control.disabled) : null,
    ariaBusy: control.getAttribute('aria-busy'),
    ariaDisabled: control.getAttribute('aria-disabled'),
    uiState: control.getAttribute('data-kg-ui-state'),
    activityControl: control.getAttribute('data-kg-async-feedback-control'),
    labelText: labelNode instanceof HTMLElement ? labelNode.textContent : null,
  };
  control.setAttribute('aria-busy', 'true');
  control.setAttribute('aria-disabled', 'true');
  control.setAttribute('data-kg-ui-state', 'busy');
  control.setAttribute('data-kg-async-feedback-control', cleanText(controlId, 'control-centre'));
  if (labelNode instanceof HTMLElement && cleanText(busyLabel)) {
    labelNode.textContent = cleanText(busyLabel);
  }
  const restoreAttribute = (name, value) => {
    if (value == null) control.removeAttribute(name);
    else control.setAttribute(name, value);
  };
  return {
    restore() {
      if (!control.isConnected) return;
      if ('disabled' in control && record.disabled != null) control.disabled = record.disabled;
      restoreAttribute('aria-busy', record.ariaBusy);
      restoreAttribute('aria-disabled', record.ariaDisabled);
      restoreAttribute('data-kg-ui-state', record.uiState);
      restoreAttribute('data-kg-async-feedback-control', record.activityControl);
      if (labelNode instanceof HTMLElement && labelNode.isConnected && record.labelText != null) {
        labelNode.textContent = record.labelText;
      }
    },
    focusReplacement() {
      const replacement = findControlCentreAsyncFeedbackControl(state, selector);
      if (!replacement) return;
      try {
        replacement.focus({ preventScroll: true });
      } catch {
        replacement.focus();
      }
    },
  };
}

function pendingSubmitPresentation(state, form, controlId, busyLabel) {
  const control = form?.querySelector?.('button[type="submit"]');
  return beginControlCentrePendingPresentation(state, control, { controlId, busyLabel });
}

function sectionData(state, sectionId = state.activeSection) {
  return normalizeSectionPayload(state.sectionData.get(sectionId));
}

function activeSectionIsStale(state) {
  const meta = state.sectionMeta.get(state.activeSection) || {};
  const data = sectionData(state);
  return meta.stale === true || data.stale === true;
}

function rememberRoute(state) {
  try {
    state.root.ownerDocument.defaultView.sessionStorage.setItem(ROUTE_STORAGE_KEY, state.activeSection);
  } catch {}
}

function announce(state, message = '') {
  state.announcement = cleanText(message);
  const live = state.root.querySelector('[data-cc-live]');
  if (live) live.textContent = state.announcement;
}

function surfaceErrorMessage(error) {
  const code = cleanText(error?.code).toUpperCase();
  const plainErrors = {
    AUTHORITY_UNAVAILABLE: 'The owner sign-in service is unavailable right now.',
    INCIDENT_ALREADY_RESOLVED: 'This service problem is already resolved.',
    INCIDENT_NOT_FOUND: 'This service problem no longer exists.',
    INCIDENT_SEVERITY_INVALID: 'Choose a valid impact level.',
    INCIDENT_SOURCE_REQUIRED: 'Explain how this service problem was found.',
    INCIDENT_STATE_INVALID: 'Choose a valid service problem stage.',
    INCIDENT_TITLE_REQUIRED: 'Describe the service problem using at least five characters.',
    OPERATION_NOT_FOUND: 'This requested change no longer exists.',
    OPERATION_NOT_RETRYABLE: 'This change does not need another attempt.',
    SHADOW_STATUS_INVALID: 'Tutor Desk returned invalid safe-comparison information.',
    SHADOW_STATUS_PRIVACY_BOUNDARY_INVALID: 'Tutor Desk did not confirm that the safe comparison contains totals only.',
    SHADOW_STATUS_SCOPE_MISMATCH: 'Tutor Desk returned safe-comparison information for a different account.',
    SHADOW_STATUS_SCOPE_REQUIRED: 'The account and safe-comparison references are required.',
  };
  if (plainErrors[code]) return plainErrors[code];
  if (code === 'AUTH_SESSION_EXPIRED' || Number(error?.status) === 401) {
    return 'Your secure sign-in expired. Please sign in again.';
  }
  if (code === 'FORBIDDEN' || Number(error?.status) === 403) {
    return 'You do not have permission to view this Control Centre information.';
  }
  if (code === 'OFFLINE' || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
    return 'You appear to be offline. Reconnect and try again.';
  }
  return cleanText(error?.message, 'This information could not be loaded. No changes were made.');
}

function renderShellStatus(state) {
  const meta = state.sectionMeta.get(state.activeSection) || {};
  const stale = meta.stale === true;
  const updatedAt = firstValue(meta, ['updatedAt', 'freshAt', 'generatedAt'], '');
  const label = state.loadingSection === state.activeSection
    ? 'Updating…'
    : stale
      ? `Out of date · ${formatDateTime(updatedAt, 'last update unknown')}`
      : updatedAt
        ? `Updated ${formatDateTime(updatedAt)}`
        : 'Current information';
  return `<span class="cc-freshness" data-stale="${stale ? 'true' : 'false'}" data-testid="control-centre-freshness">${escapeHtml(label)}</span>`;
}

function renderNav(state) {
  const today = sectionData(state, 'today');
  const navCounts = objectValue(today.navCounts || today.counts);
  return NAV_ITEMS.map((item) => {
    const count = safeNumber(navCounts[item.id], 0);
    return `<button
      type="button"
      class="cc-nav__button"
      data-cc-action="navigate"
      data-section="${escapeHtml(item.id)}"
      data-testid="control-centre-nav-${escapeHtml(item.id)}"
      ${item.id === state.activeSection ? 'aria-current="page"' : ''}
    >
      ${icon(item.icon)}
      <span>${escapeHtml(item.label)}</span>
      ${count > 0 ? `<span class="cc-nav__badge" aria-label="${formatCount(count)} items">${formatCount(count)}</span>` : ''}
    </button>`;
  }).join('');
}

function renderAppShell(state) {
  const nav = currentNavItem(state);
  const userEmail = cleanText(state.ctx?.auth?.userEmail || state.ctx?.sharedState?.userEmail, 'Control Centre owner');
  const themeLabel = state.theme === 'dark' ? 'Use light mode' : 'Use dark mode';
  return `
    <a class="cc-skip-link" href="#cc-main-content">Skip to main content</a>
    <div class="cc-app">
      <button type="button" class="cc-mobile-overlay" data-cc-action="close-menu" aria-label="Close menu" tabindex="-1"></button>
      <aside class="cc-sidebar" id="cc-control-centre-navigation" aria-label="Control Centre navigation">
        <div class="cc-brand" data-testid="control-centre-brand">
          <img class="cc-brand__logo" src="/assets/ttoria-wordmark-final-ui-v1.png" alt="TToria">
          <span class="cc-brand__product">Control Centre</span>
        </div>
        <div class="cc-theme-control">
          <span class="cc-theme-control__label">Appearance</span>
          <button
            type="button"
            class="cc-theme-switch"
            role="switch"
            aria-checked="${state.theme === 'dark' ? 'true' : 'false'}"
            data-cc-action="toggle-theme"
            data-testid="control-centre-theme-toggle"
            aria-label="${escapeHtml(themeLabel)}. ${state.theme === 'dark' ? 'Dark' : 'Light'} mode is currently on."
            title="${escapeHtml(themeLabel)}"
          >
            <span class="cc-theme-switch__label">Light</span>
            <span class="cc-theme-switch__track" aria-hidden="true"><span class="cc-theme-switch__thumb"></span></span>
            <span class="cc-theme-switch__label">Dark</span>
          </button>
        </div>
        <p class="cc-nav-label">Manage TToria</p>
        <nav class="cc-nav">${renderNav(state)}</nav>
        <div class="cc-sidebar__bottom">
          <div class="cc-trust-card" data-testid="control-centre-privacy-boundary">
            <strong>${icon('shield')} Private teaching information stays hidden</strong>
            <p>No student names, tutor notes, assessments, lesson content or student payment details are shown here.</p>
          </div>
          <div class="cc-sidebar__footer">
            <div class="cc-owner">
              <strong>Control Centre owner</strong>
              <span title="${escapeHtml(userEmail)}">${escapeHtml(userEmail)}</span>
            </div>
            <button
              type="button"
              class="cc-button cc-button--secondary cc-sidebar__logout"
              data-cc-action="logout"
              data-testid="control-centre-logout"
            >${icon('logout')}<span>Log out</span></button>
          </div>
        </div>
      </aside>
      <main class="cc-main" id="cc-main-content">
        <header class="cc-topbar">
          <div class="cc-topbar__start">
            <button
              type="button"
              class="cc-icon-button cc-menu-button"
              data-cc-action="open-menu"
              aria-label="Open menu"
              aria-controls="cc-control-centre-navigation"
              aria-expanded="${state.menuOpen ? 'true' : 'false'}"
            >${icon('menu')}</button>
            <div class="cc-topbar__title">
              <strong>${escapeHtml(nav.label)}</strong>
              <span>Manage TToria accounts and services</span>
            </div>
          </div>
          <div class="cc-topbar__end">
            ${renderShellStatus(state)}
            ${renderOwnerSecurityControl(state)}
            <button
              type="button"
              class="cc-icon-button"
              data-cc-action="refresh"
              data-testid="control-centre-refresh"
              aria-label="Refresh this page"
              title="Refresh this page"
              ${state.loadingSection === state.activeSection ? 'disabled' : ''}
            >${state.loadingSection === state.activeSection ? '<span class="cc-spinner" aria-hidden="true"></span>' : icon('refresh')}</button>
          </div>
        </header>
        <div class="cc-screen-status">
          ${state.ownerSessionError ? alertBox({
            title: 'Owner security status is unavailable',
            detail: state.ownerSessionError,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
            testId: 'control-centre-owner-session-error',
          }) : ''}
          ${!state.ownerSessionError && state.ownerSession?.elevated !== true ? alertBox({
            title: safeNumber(state.ownerSession?.passkeyCount, 0) < 2
              ? 'Finish owner security setup'
              : 'Sensitive changes are locked',
            detail: safeNumber(state.ownerSession?.passkeyCount, 0) < 2
              ? 'Add a separately protected backup passkey. Account information remains safe to review.'
              : 'You can safely review information. Use “Confirm changes” above when you need to make a change.',
            tone: safeNumber(state.ownerSession?.passkeyCount, 0) < 2 ? 'warn' : 'info',
            iconName: safeNumber(state.ownerSession?.passkeyCount, 0) < 2 ? 'key' : 'lock',
            testId: 'control-centre-owner-read-only-notice',
          }) : ''}
          ${state.readOnlyReason ? alertBox({
            title: 'View-only access',
            detail: plainSystemMessage(state.readOnlyReason),
            tone: 'warn',
            iconName: 'lock',
            testId: 'control-centre-read-only',
          }) : ''}
        </div>
        <section data-cc-screen data-testid="control-centre-screen-${escapeHtml(state.activeSection)}">
          ${renderActiveScreen(state)}
        </section>
      </main>
    </div>
    ${renderModalLayer(state)}
    <div class="cc-visually-hidden" role="status" aria-live="polite" aria-atomic="true" data-cc-live>${escapeHtml(state.announcement)}</div>
  `;
}

function renderLoadingScreen(state) {
  const nav = currentNavItem(state);
  return `
    ${pageHeading({ title: nav.title, detail: nav.detail })}
    ${loadingCards(4)}
    <p class="cc-visually-hidden" role="status">Loading ${escapeHtml(nav.label)}.</p>
  `;
}

function renderErrorScreen(state) {
  const nav = currentNavItem(state);
  const error = state.sectionError;
  return `
    ${pageHeading({ title: nav.title, detail: nav.detail })}
    ${emptyState({
      title: 'This page is not available right now',
      detail: surfaceErrorMessage(error),
      iconName: 'alert',
      actionHtml: actionButton({
        label: 'Try again',
        action: 'refresh',
        iconName: 'retry',
        tone: 'primary',
        testId: 'control-centre-retry',
      }),
      testId: 'control-centre-error-state',
    })}
    ${error?.requestId ? `<p class="cc-code">Reference ${escapeHtml(error.requestId)}</p>` : ''}
  `;
}

function renderStaleNotice(state, data = {}) {
  const meta = state.sectionMeta.get(state.activeSection) || {};
  const stale = meta.stale === true || data.stale === true;
  if (!stale) return '';
  return alertBox({
    title: 'Showing the last safe information',
    detail: 'Fresh data is delayed. Actions that could widen access remain unavailable until the Control Centre reconnects.',
    tone: 'warn',
    iconName: 'alert',
    testId: 'control-centre-stale-state',
  });
}

function renderActiveScreen(state) {
  if (state.loadingSection === state.activeSection && !state.sectionData.has(state.activeSection)) {
    return renderLoadingScreen(state);
  }
  if (state.sectionError && !state.sectionData.has(state.activeSection)) {
    return renderErrorScreen(state);
  }
  switch (state.activeSection) {
    case 'accounts': return renderAccounts(state);
    case 'billing': return renderBilling(state);
    case 'usage': return renderUsage(state);
    case 'operations': return renderOperations(state);
    case 'incidents': return renderIncidents(state);
    case 'security': return renderSecurity(state);
    case 'audit': return renderAudit(state);
    case 'settings': return renderSettings(state);
    default: return renderToday(state);
  }
}

function renderToday(state) {
  const data = sectionData(state, 'today');
  const summary = objectValue(data.summary || data.counts || data.metrics);
  const actionItems = firstArray(data, ['actionItems', 'actionInbox', 'actions', 'inbox']);
  const services = firstArray(data, ['services', 'healthChecks', 'serviceHealth']);
  const warnings = firstArray(data, ['warnings', 'alerts']);
  const activeAccounts = firstValue(summary, ['activeAccounts', 'active'], 0);
  const trialAccounts = firstValue(summary, ['trialAccounts', 'trialing'], 0);
  const attentionAccounts = firstValue(summary, ['attentionAccounts', 'pastDue', 'readOnly'], 0);
  const failedOperations = firstValue(summary, ['failedOperations', 'operationsFailed'], 0);
  return `
    ${pageHeading({
      title: `Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}`,
      detail: 'Start with the few things that need a decision. Everything else can wait.',
      actionsHtml: state.canMutate ? actionButton({
        label: 'Create tutor account',
        action: 'open-wizard',
        tone: 'primary',
        iconName: 'plus',
        testId: 'control-centre-create-account',
      }) : '',
      testId: 'control-centre-today-heading',
    })}
    ${renderStaleNotice(state, data)}
    ${warnings.map((warning) => alertBox({
      title: firstValue(warning, ['title', 'label'], 'Needs attention'),
      detail: firstValue(warning, ['detail', 'message'], ''),
      tone: statusTone(firstValue(warning, ['severity', 'status'], 'warning')) === 'danger' ? 'danger' : 'warn',
      iconName: 'alert',
    })).join('')}
    <section class="cc-grid cc-grid--metrics" aria-label="Platform overview">
      ${metricCard({
        label: 'Active accounts',
        value: formatCount(activeAccounts),
        detail: `${formatCount(trialAccounts)} currently in trial`,
        iconName: 'accounts',
        color: 'var(--cc-green)',
        soft: 'var(--cc-green-soft)',
        testId: 'control-centre-metric-active-accounts',
      })}
      ${metricCard({
        label: 'Need attention',
        value: formatCount(attentionAccounts),
        detail: 'Billing, access, or account setup',
        iconName: 'alert',
        color: 'var(--cc-amber)',
        soft: 'var(--cc-amber-soft)',
        testId: 'control-centre-metric-attention',
      })}
      ${metricCard({
        label: 'Changes that did not finish',
        value: formatCount(failedOperations),
        detail: 'Changes that did not fully finish',
        iconName: 'operations',
        color: failedOperations > 0 ? 'var(--cc-red)' : 'var(--cc-blue)',
        soft: failedOperations > 0 ? 'var(--cc-red-soft)' : 'var(--cc-blue-soft)',
        testId: 'control-centre-metric-operations',
      })}
      ${metricCard({
        label: 'Automatic system check',
        value: friendlySystemCheckStatus(firstValue(summary, ['canaryStatus'], data.canary?.status)),
        detail: firstValue(data.canary, ['lastCheck'], '')
          ? `Last checked ${formatDateTime(firstValue(data.canary, ['lastCheck'], ''))}`
          : 'Uses a safe test account—not customer information',
        iconName: 'shield',
        color: 'var(--cc-purple)',
        soft: 'var(--cc-purple-soft)',
        testId: 'control-centre-metric-canary',
      })}
    </section>
    <section class="cc-grid cc-grid--two cc-section">
      <article class="cc-card cc-card--accent" data-ds-component="ds/card" data-testid="control-centre-action-inbox">
        <div class="cc-card__head">
          <div><h2>What needs attention</h2><p>The most urgent problems affecting tutors appear first.</p></div>
          ${statusBadge(`${actionItems.length} open`, actionItems.length ? 'pending' : 'healthy')}
        </div>
        ${actionItems.length ? `<ul class="cc-list">${actionItems.slice(0, 8).map((item) => renderActionInboxItem(item)).join('')}</ul>` : emptyState({
          title: 'Nothing needs action',
          detail: 'Account, billing, security, and service problems will appear here.',
          iconName: 'check',
          testId: 'control-centre-action-inbox-empty',
        })}
      </article>
      <article class="cc-card" data-ds-component="ds/card" data-testid="control-centre-service-health">
        <div class="cc-card__head">
          <div><h2>TToria service status</h2><p>Each result comes from a specific automatic check.</p></div>
        </div>
        ${services.length ? `<ul class="cc-list">${services.slice(0, 8).map((service) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('activity')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(service, ['name', 'label'], 'Service'))}</strong>
              <span>${escapeHtml(plainSystemMessage(firstValue(service, ['detail', 'reason'], 'No explanation supplied.')))}</span>
            </span>
            ${statusBadge(plainStatus(firstValue(service, ['status', 'state'], 'unknown')), firstValue(service, ['status', 'state'], 'unknown'))}
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'Service checks are not available yet',
          detail: 'No service information was returned. No healthy status is being assumed.',
          iconName: 'activity',
        })}
      </article>
    </section>
  `;
}

function renderActionInboxItem(item = {}) {
  const section = cleanText(firstValue(item, ['section', 'route'], 'operations')).toLowerCase();
  const validSection = NAV_ITEMS.some((nav) => nav.id === section) ? section : 'operations';
  const severity = firstValue(item, ['severity', 'status'], 'warning');
  return `<li class="cc-list-item">
    <span class="cc-list-item__icon" style="color:var(--cc-${statusTone(severity) === 'danger' ? 'red' : 'amber'})">${icon('alert')}</span>
    <span class="cc-list-item__body">
      <strong>${escapeHtml(plainAlertTitle(firstValue(item, ['type', 'alertType', 'title', 'label'], 'Needs attention')))}</strong>
      <span>${escapeHtml(plainSystemMessage(firstValue(item, ['detail', 'message'], 'Review the available information before taking action.')))}</span>
    </span>
    ${actionButton({
      label: 'Review',
      action: 'navigate',
      attrs: `data-section="${escapeHtml(validSection)}"`,
      testId: firstValue(item, ['id'], '') ? `control-centre-action-${escapeHtml(item.id)}` : '',
    })}
  </li>`;
}

function accountsFromData(data = {}) {
  return firstArray(data, ['accounts', 'items', 'tenants']);
}

function accountDisplayName(account = {}) {
  return cleanText(firstValue(account, ['businessName', 'tutorName', 'name', 'displayName']), 'Unnamed tutor account');
}

function accountId(account = {}) {
  return cleanText(firstValue(account, ['accountId', 'tenantId', 'id']));
}

function firstPresent(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function normalizeStudentLimit(value, unlimited = false) {
  const text = cleanText(value).toLowerCase();
  if (unlimited || ['unlimited', 'none', 'no_limit', 'no limit', '∞'].includes(text)) {
    return { known: true, unlimited: true, value: null, label: 'Unlimited' };
  }
  const number = Number(value);
  if (Number.isFinite(number) && number >= 0) {
    return { known: true, unlimited: false, value: Math.floor(number), label: formatCount(Math.floor(number)) };
  }
  return { known: false, unlimited: false, value: null, label: 'Not provided' };
}

function normalizeStudentAllowance(account = {}) {
  const allowance = objectValue(
    account.studentAllowance
    || account.studentCapacity
    || account.studentLimits
    || account.allowance
  );
  const usage = objectValue(account.usage);
  const plan = objectValue(account.planDetails || account.plan);
  const policy = objectValue(account.policy || account.effectivePolicy);
  const planRaw = firstPresent(
    allowance.planAllowance,
    allowance.planLimit,
    allowance.planStudentLimit,
    plan.studentAllowance,
    plan.studentLimit,
    account.planStudentAllowance,
    account.planStudentLimit,
  );
  const overrideRaw = firstPresent(
    allowance.tenantOverride,
    allowance.override,
    allowance.overrideLimit,
    allowance.tenantStudentLimit,
    policy.studentAllowanceOverride,
    policy.studentLimitOverride,
    account.studentAllowanceOverride,
    account.tenantStudentLimit,
  );
  const effectiveRaw = firstPresent(
    allowance.effectiveAllowance,
    allowance.effectiveLimit,
    allowance.limit,
    policy.effectiveStudentAllowance,
    policy.effectiveStudentLimit,
    account.effectiveStudentAllowance,
    account.effectiveStudentLimit,
    overrideRaw,
    planRaw,
  );
  const activeRaw = firstPresent(
    allowance.activeStudents,
    allowance.activeCount,
    allowance.currentUsage,
    usage.activeStudents,
    usage.students,
    account.activeStudentCount,
    account.studentCount,
    0,
  );
  const modeRaw = firstPresent(
    allowance.enforcementMode,
    allowance.mode,
    allowance.enforcementStatus,
    policy.studentAllowanceEnforcement,
    account.studentAllowanceEnforcement,
    account.studentLimitMode,
  );
  const planLimit = normalizeStudentLimit(
    planRaw,
    allowance.planUnlimited === true || plan.studentAllowanceUnlimited === true,
  );
  const overrideLimit = normalizeStudentLimit(
    overrideRaw,
    allowance.overrideUnlimited === true || policy.studentAllowanceUnlimited === true,
  );
  const effectiveLimit = normalizeStudentLimit(
    effectiveRaw,
    allowance.unlimited === true || allowance.effectiveUnlimited === true,
  );
  const mode = cleanText(modeRaw).toLowerCase().replaceAll('-', '_').replaceAll(' ', '_');
  const provided = planLimit.known
    || overrideLimit.known
    || effectiveLimit.known
    || Boolean(mode)
    || Object.keys(allowance).length > 0;
  const active = Math.max(0, Math.floor(safeNumber(activeRaw, 0)));
  const enforced = ['enforced', 'active', 'on'].includes(mode);
  const shadow = ['shadow', 'shadow_only', 'monitoring', 'preview'].includes(mode);
  const overLimit = effectiveLimit.known
    && !effectiveLimit.unlimited
    && active > effectiveLimit.value;
  const atLimit = effectiveLimit.known
    && !effectiveLimit.unlimited
    && active >= effectiveLimit.value;
  return {
    provided,
    active,
    planLimit,
    overrideLimit,
    effectiveLimit,
    mode,
    enforced,
    shadow,
    overLimit,
    atLimit,
  };
}

function studentAllowanceModeLabel(allowance) {
  if (allowance.enforced) return 'Limit is active';
  if (allowance.shadow) return 'Measuring only';
  if (allowance.mode) return plainStatus(allowance.mode, 'general');
  return 'Status not provided';
}

function studentAllowanceSummary(account = {}) {
  const allowance = normalizeStudentAllowance(account);
  if (!allowance.provided) return formatCount(firstValue(account, ['studentCount'], 0));
  return `${formatCount(allowance.active)} of ${allowance.effectiveLimit.label}`;
}

function renderStudentAllowance(account = {}, { context = 'account', canEdit = false } = {}) {
  const allowance = normalizeStudentAllowance(account);
  if (!allowance.provided) return '';
  const planContext = cleanText(context).includes('plan');
  const testBase = context === 'account'
    ? 'control-centre-student-allowance'
    : `control-centre-student-allowance-${cleanText(context).replaceAll('_', '-')}`;
  const overrideLabel = allowance.overrideLimit.known
    ? allowance.overrideLimit.label
    : 'No account-specific limit';
  const modeLabel = studentAllowanceModeLabel(allowance);
  const warningDetail = planContext
    ? 'Tutor accounts using this plan start with this number. Account-specific limits stay unchanged, existing students are never removed, and each account controls when its limit becomes active.'
    : allowance.overLimit
    ? `This account has ${formatCount(allowance.active)} active students, which is above its current limit of ${allowance.effectiveLimit.label}. No students are deleted. ${allowance.enforced
      ? 'Adding a new student or reactivating an inactive student is blocked until the account is below its limit.'
      : 'Because the limit is not active, nothing is blocked yet. Adding or reactivating is blocked only after the limit is turned on.'}`
    : allowance.atLimit
      ? `This account has reached its current active student limit. No students are deleted. ${allowance.enforced
        ? 'Adding a new student or reactivating an inactive student is blocked until space is available.'
        : 'Adding or reactivating is blocked only after the limit is turned on.'}`
      : allowance.shadow
        ? 'TToria is measuring this limit without blocking anything. Adding or reactivating is blocked only after the limit is turned on.'
        : 'Reducing a limit never deletes students. When an active limit is reached, only adding a new student or reactivating an inactive student is blocked.';
  return `
    <article class="cc-card ${context === 'account' ? '' : 'cc-section'}" data-testid="${escapeHtml(testBase)}">
      <div class="cc-card__head">
        <div>
          <h3>Active student limit</h3>
          <p>${planContext ? 'The starting active student limit for accounts using this plan.' : 'How many active students this tutor account may have.'}</p>
        </div>
        <span class="cc-page-actions">
          ${statusBadge(modeLabel, allowance.enforced ? 'active' : allowance.shadow ? 'pending' : 'unknown')}
          ${canEdit ? actionButton({
            label: 'Change student limit',
            action: 'open-capacity',
            iconName: 'settings',
            testId: 'control-centre-capacity-open',
          }) : ''}
        </span>
      </div>
      <p class="cc-eyebrow" data-testid="${escapeHtml(`${testBase}-active`)}">${planContext
        ? `Plan limit: ${escapeHtml(allowance.effectiveLimit.label)} active students for each account using this plan`
        : `Active students: ${formatCount(allowance.active)} of ${escapeHtml(allowance.effectiveLimit.label)}`}</p>
      <dl class="cc-kv cc-section">
        <div><dt>Plan limit</dt><dd data-testid="${escapeHtml(`${testBase}-plan`)}">${escapeHtml(allowance.planLimit.label)}</dd></div>
        <div><dt>${planContext ? 'Account-specific limits' : 'Account-specific limit'}</dt><dd data-testid="${escapeHtml(`${testBase}-override`)}">${planContext ? 'Kept separately for each account' : escapeHtml(overrideLabel)}</dd></div>
        <div><dt>Current limit</dt><dd>${escapeHtml(allowance.effectiveLimit.label)}</dd></div>
        <div><dt>${planContext ? 'When limits apply' : 'Current status'}</dt><dd data-testid="${escapeHtml(`${testBase}-enforcement`)}">${planContext ? 'Controlled separately for each account' : escapeHtml(modeLabel)}</dd></div>
      </dl>
      ${alertBox({
        title: planContext
          ? 'How plan changes work'
          : allowance.overLimit
          ? 'Active students are above the limit'
          : allowance.atLimit
            ? 'Active students have reached the limit'
            : allowance.shadow
              ? 'Measuring only; nothing is blocked'
              : 'What happens when the student limit changes',
        detail: warningDetail,
        tone: allowance.overLimit || allowance.atLimit ? 'warn' : 'info',
        iconName: allowance.overLimit || allowance.atLimit ? 'alert' : 'info',
        testId: `${testBase}-warning`,
      })}
    </article>
  `;
}

function renderReportingRangeFields(state) {
  return `
    <label class="cc-field">
      <span>Activity from</span>
      <input
        type="date"
        name="reportingFrom"
        value="${escapeHtml(state.reportingFrom)}"
        data-cc-field="reportingFrom"
        data-testid="control-centre-reporting-from"
        ${state.reportingRangeError ? 'aria-invalid="true"' : ''}
      >
    </label>
    <label class="cc-field">
      <span>Activity to</span>
      <input
        type="date"
        name="reportingTo"
        value="${escapeHtml(state.reportingTo)}"
        data-cc-field="reportingTo"
        data-testid="control-centre-reporting-to"
        ${state.reportingRangeError ? 'aria-invalid="true"' : ''}
      >
    </label>
  `;
}

function renderReportingRangeForm(state, {
  detail = 'Lesson and activity totals use these dates. The active student count always shows the current number.',
} = {}) {
  return `<section class="cc-card cc-section">
    <form class="cc-toolbar" data-cc-form="reporting-range" aria-label="Choose dates">
      ${renderReportingRangeFields(state)}
      ${actionButton({
        label: 'Apply dates',
        type: 'submit',
        iconName: 'calendar',
        testId: 'control-centre-reporting-apply',
      })}
    </form>
    ${state.reportingRangeError ? `<p class="cc-field__error" role="alert">${escapeHtml(state.reportingRangeError)}</p>` : ''}
    <p class="cc-field__hint">${escapeHtml(detail)}</p>
  </section>`;
}

function renderAccounts(state) {
  if (state.selectedAccountId) return renderAccount360(state);
  const data = sectionData(state, 'accounts');
  const accounts = accountsFromData(data);
  const statuses = firstArray(data, ['statusOptions']);
  const plans = firstArray(data, ['planOptions', 'plans']);
  const summary = objectValue(data.summary || data.counts);
  return `
    ${pageHeading({
      title: 'Tutor Accounts',
      detail: 'Manage independent tutor accounts without entering their teaching workspaces.',
      actionsHtml: state.canMutate ? actionButton({
        label: 'Create tutor account',
        action: 'open-wizard',
        tone: 'primary',
        iconName: 'plus',
        testId: 'control-centre-create-account',
      }) : '',
      testId: 'control-centre-accounts-heading',
    })}
    ${renderStaleNotice(state, data)}
    <section class="cc-grid cc-grid--metrics" aria-label="Tutor account summary">
      ${metricCard({
        label: 'Total accounts',
        value: formatCount(firstValue(summary, ['total', 'totalAccounts'], accounts.length)),
        detail: 'Independent tutor businesses',
        iconName: 'accounts',
      })}
      ${metricCard({
        label: 'Active',
        value: formatCount(firstValue(summary, ['active', 'activeAccounts'], accounts.filter((item) => cleanText(firstValue(item, ['serviceAccessState', 'accessState'])) === 'full').length)),
        detail: 'Can use their allowed features',
        iconName: 'check',
        color: 'var(--cc-green)',
        soft: 'var(--cc-green-soft)',
      })}
      ${metricCard({
        label: 'Waiting for tutor',
        value: formatCount(firstValue(summary, ['onboarding', 'invitationsPending'], 0)),
        detail: 'Waiting for activation',
        iconName: 'mail',
        color: 'var(--cc-purple)',
        soft: 'var(--cc-purple-soft)',
      })}
      ${metricCard({
        label: 'Limited access',
        value: formatCount(firstValue(summary, ['restricted', 'suspended', 'readOnly'], 0)),
        detail: 'View only, suspended, or locked for security review',
        iconName: 'lock',
        color: 'var(--cc-amber)',
        soft: 'var(--cc-amber-soft)',
      })}
    </section>
    <section class="cc-card cc-section" data-ds-component="ds/card">
      <form class="cc-toolbar" data-cc-form="account-search" role="search" aria-label="Find tutor accounts">
        <label class="cc-field">
          <span>Search</span>
          <input
            type="search"
            name="accountQuery"
            value="${escapeHtml(state.accountQuery)}"
            placeholder="Tutor, business, email or account ID"
            data-cc-field="accountQuery"
            data-testid="control-centre-account-search"
          >
        </label>
        <label class="cc-field">
          <span>Access status</span>
          <select name="accountStatus" data-cc-field="accountStatus" data-testid="control-centre-account-filter">
            <option value="">All statuses</option>
            ${(statuses.length ? statuses : ['active', 'onboarding', 'read_only', 'suspended', 'security_quarantine']).map((option) => {
              const value = cleanText(option?.value || option?.id || option);
              const context = value === 'onboarding' ? 'lifecycle' : value === 'active' ? 'general' : 'access';
              const label = plainStatus(option?.label || option, context);
              return `<option value="${escapeHtml(value)}" ${state.accountStatus === value ? 'selected' : ''}>${escapeHtml(label)}</option>`;
            }).join('')}
          </select>
        </label>
        <label class="cc-field">
          <span>Plan</span>
          <select name="accountPlan" data-cc-field="accountPlan">
            <option value="">All plans</option>
            ${plans.map((option) => {
              const value = cleanText(option?.value || option?.planId || option?.id || option);
              const label = cleanText(option?.label || option?.name || value);
              return `<option value="${escapeHtml(value)}" ${state.accountPlan === value ? 'selected' : ''}>${escapeHtml(label)}</option>`;
            }).join('')}
          </select>
        </label>
        ${renderReportingRangeFields(state)}
        ${actionButton({
          label: 'Find accounts',
          type: 'submit',
          iconName: 'search',
          testId: 'control-centre-account-search-submit',
        })}
      </form>
      ${state.reportingRangeError ? `<p class="cc-field__error" role="alert">${escapeHtml(state.reportingRangeError)}</p>` : ''}
      <p class="cc-field__hint" data-testid="control-centre-account-reporting-period">
        Lesson counts cover ${escapeHtml(reportingPeriodLabel(state, data))}, including both dates. “Active students now” always shows the current number and does not change with the lesson period.
      </p>
      ${accounts.length ? renderAccountsTable(state, accounts) : emptyState({
        title: 'No tutor accounts match',
        detail: state.accountQuery || state.accountStatus || state.accountPlan
          ? 'Try a different search or clear a filter.'
          : 'Create the first tutor account when you are ready.',
        iconName: 'accounts',
        actionHtml: state.canMutate ? actionButton({
          label: 'Create tutor account',
          action: 'open-wizard',
          tone: 'primary',
          iconName: 'plus',
        }) : '',
        testId: 'control-centre-accounts-empty',
      })}
    </section>
  `;
}

function renderAccountsTable(state, accounts = []) {
  const period = reportingPeriodLabel(state);
  return `<div class="cc-table-wrap" data-testid="control-centre-account-list">
    <table class="cc-table">
      <caption class="cc-visually-hidden">Tutor accounts and their current status</caption>
      <thead><tr>
        <th scope="col">Tutor account</th>
        <th scope="col">Plan</th>
        <th scope="col">Access</th>
        <th scope="col">Service status</th>
        <th scope="col">Last activity</th>
        <th scope="col">Active students now</th>
        <th scope="col">Lessons booked</th>
        <th scope="col"><span class="cc-visually-hidden">Actions</span></th>
      </tr></thead>
      <tbody>
        ${accounts.map((account) => {
          const id = accountId(account);
          return `<tr data-account-id="${escapeHtml(id)}">
            <td>
              <span class="cc-table__primary">${escapeHtml(accountDisplayName(account))}</span>
              <span class="cc-table__secondary">${escapeHtml(firstValue(account, ['ownerEmail', 'email'], 'Owner email unavailable'))}</span>
            </td>
            <td>
              <span class="cc-table__primary">${escapeHtml(firstValue(account, ['planName', 'planId', 'plan'], 'No plan'))}</span>
              <span class="cc-table__secondary">${escapeHtml(plainStatus(firstValue(account, ['subscriptionState', 'subscriptionStatus'], 'none'), 'subscription'))}</span>
            </td>
            <td>${statusBadge(plainStatus(firstValue(account, ['serviceAccessState', 'accessState', 'serviceAccess'], 'unknown'), 'access'), firstValue(account, ['serviceAccessState', 'accessState', 'serviceAccess'], 'unknown'))}</td>
            <td>${statusBadge(plainStatus(firstValue(account, ['healthState', 'healthStatus', 'health'], 'unknown')), firstValue(account, ['healthState', 'healthStatus', 'health'], 'unknown'))}</td>
            <td>
              <span class="cc-table__primary">${escapeHtml(formatDateTime(firstValue(account, ['lastSuccessfulActionAt', 'lastActivityAt'], ''), 'No activity'))}</span>
              <span class="cc-table__secondary">${escapeHtml(formatDateTime(firstValue(account, ['lastSignInAt'], ''), 'No sign-in recorded'))}</span>
            </td>
            <td>
              <span class="cc-table__primary">${escapeHtml(studentAllowanceSummary(account))}</span>
              <span class="cc-table__secondary">current number, not date-based</span>
            </td>
            <td>
              <span class="cc-table__primary">${escapeHtml(lessonTotalDisplay(account, ['bookedLessons', 'lessonCount', 'lessonsBooked']))}</span>
              <span class="cc-table__secondary">${usageCoverageComplete(account) ? escapeHtml(period) : 'Earlier lesson information is updating'}</span>
            </td>
            <td>${actionButton({
              label: 'View account',
              action: 'open-account',
              attrs: `data-account-id="${escapeHtml(id)}"`,
              testId: id ? `control-centre-account-${escapeHtml(id)}` : '',
              disabled: !id,
            })}</td>
          </tr>`;
        }).join('')}
      </tbody>
    </table>
  </div>`;
}

function renderAccount360(state) {
  if (state.accountLoading && !state.accountDetails) {
    return `
      ${pageHeading({
        title: 'Opening tutor account',
        detail: 'Loading account information without showing private teaching content.',
        actionsHtml: actionButton({ label: 'Back to accounts', action: 'close-account', iconName: 'back' }),
      })}
      ${loadingCards(4)}
    `;
  }
  if (state.accountError && !state.accountDetails) {
    return `
      ${pageHeading({
        title: 'Tutor account',
        detail: 'The account details could not be opened.',
        actionsHtml: actionButton({ label: 'Back to accounts', action: 'close-account', iconName: 'back' }),
      })}
      ${emptyState({
        title: 'Account details are unavailable',
        detail: surfaceErrorMessage(state.accountError),
        iconName: 'alert',
        actionHtml: actionButton({
          label: 'Try again',
          action: 'reload-account',
          tone: 'primary',
          iconName: 'retry',
          testId: 'control-centre-account-retry',
        }),
      })}
    `;
  }
  const account = normalizeSectionPayload(state.accountDetails);
  const id = accountId(account) || state.selectedAccountId;
  const accessState = firstValue(account, ['serviceAccessState', 'accessState', 'serviceAccess'], 'unknown');
  const lifecycleState = firstValue(account, ['lifecycleState', 'lifecycle'], 'unknown');
  const subscription = objectValue(account.subscription);
  const health = objectValue(account.health);
  return `
    ${pageHeading({
      eyebrow: 'Tutor account',
      title: accountDisplayName(account),
      detail: `Account reference for support: ${id || 'unavailable'} · No teaching content is visible.`,
      actionsHtml: actionButton({ label: 'Back to accounts', action: 'close-account', iconName: 'back' }),
      testId: 'control-centre-account-360-heading',
    })}
    <section class="cc-grid cc-grid--metrics" aria-label="Account status">
      ${metricCard({
        label: 'Account access',
        value: plainStatus(accessState, 'access'),
        detail: plainSystemMessage(firstValue(account, ['accessExplanation'], 'The access currently allowed for this account.')),
        iconName: 'lock',
        color: statusTone(accessState) === 'danger' ? 'var(--cc-red)' : statusTone(accessState) === 'warn' ? 'var(--cc-amber)' : 'var(--cc-green)',
        soft: statusTone(accessState) === 'danger' ? 'var(--cc-red-soft)' : statusTone(accessState) === 'warn' ? 'var(--cc-amber-soft)' : 'var(--cc-green-soft)',
      })}
      ${metricCard({
        label: 'Account stage',
        value: plainStatus(lifecycleState, 'lifecycle'),
        detail: `Created ${formatDate(firstValue(account, ['createdAt'], ''), 'date unavailable')}`,
        iconName: 'operations',
      })}
      ${metricCard({
        label: 'Subscription',
        value: plainStatus(firstValue(account, ['subscriptionState', 'subscriptionStatus'], firstValue(subscription, ['state', 'status'], 'none')), 'subscription'),
        detail: cleanText(firstValue(account, ['planName', 'planId', 'plan'], 'No paid plan')),
        iconName: 'billing',
        color: 'var(--cc-purple)',
        soft: 'var(--cc-purple-soft)',
      })}
      ${metricCard({
        label: 'Service status',
        value: plainStatus(firstValue(account, ['healthState', 'healthStatus'], firstValue(health, ['state', 'status'], 'unknown'))),
        detail: plainSystemMessage(firstValue(account, ['healthExplanation'], firstValue(health, ['explanation'], 'Based on specific automatic checks.'))),
        iconName: 'activity',
        color: 'var(--cc-cyan)',
        soft: 'var(--cc-blue-soft)',
      })}
    </section>
    <section class="cc-card cc-section" data-testid="control-centre-account-360">
      <div class="cc-card__head">
        <div><h2>Account management</h2><p>Use safe, reversible actions. TToria records every request.</p></div>
        ${statusBadge(plainStatus(accessState, 'access'), accessState)}
      </div>
      ${state.canMutate ? renderAccountActionBar(account) : alertBox({
        title: 'View-only mode',
        detail: 'Your current sign-in can review information but cannot request account changes.',
        tone: 'warn',
        iconName: 'lock',
      })}
      <div class="cc-divider"></div>
      <div class="cc-tabs" role="tablist" aria-label="Tutor account information">
        ${ACCOUNT_TABS.map((tab) => `<button
          type="button"
          class="cc-tab"
          role="tab"
          aria-selected="${state.accountTab === tab.id ? 'true' : 'false'}"
          data-cc-action="account-tab"
          data-tab="${escapeHtml(tab.id)}"
          data-testid="control-centre-account-tab-${escapeHtml(tab.id)}"
        >${escapeHtml(tab.label)}</button>`).join('')}
      </div>
      <div role="tabpanel" data-testid="control-centre-account-panel-${escapeHtml(state.accountTab)}">
        ${renderAccountTab(state, account)}
      </div>
    </section>
  `;
}

function renderAccountActionBar(account = {}) {
  const access = cleanText(firstValue(account, ['serviceAccessState', 'accessState', 'serviceAccess'])).toLowerCase();
  const lifecycle = cleanText(firstValue(account, ['lifecycleState'])).toLowerCase();
  const invitation = cleanText(firstValue(account, ['invitationStatus', 'activationState'], firstValue(objectValue(account.invitation), ['status']))).toLowerCase();
  const subscription = cleanText(firstValue(account, ['subscriptionState', 'subscriptionStatus'], firstValue(objectValue(account.subscription), ['state', 'status']))).toLowerCase();
  const invitationActions = [];
  if (['pending', 'accepted', 'expired', 'bounced', 'waiting', 'queued', 'sent', 'provider_accepted'].includes(invitation)) {
    invitationActions.push('resend-invitation', 'cancel-invitation');
  }
  const accessActions = [
    ...(access !== 'full' ? ['full'] : []),
    ...(access !== 'grace' ? ['grace'] : []),
    ...(access !== 'read_only' ? ['read-only'] : []),
    ...(!['suspended', 'closed'].includes(access) ? ['suspend'] : []),
    ...(access !== 'security_quarantine' ? ['quarantine'] : []),
    ...(['suspended', 'security_quarantine', 'closed', 'read_only'].includes(access) ? ['restore'] : []),
  ];
  const subscriptionActions = [
    'assign-plan',
    ...(subscription === 'trialing' ? ['extend-trial', 'end-trial'] : ['start-trial']),
    'complimentary-period',
  ];
  const supportActions = [
    ...invitationActions,
    'password-reset',
    'revoke-sessions',
    'export',
  ];
  const lifecycleActions = lifecycle === 'closure_pending'
    ? ['cancel-closure']
    : !['retained', 'purged'].includes(lifecycle) ? ['close'] : [];
  const group = (title, detail, actions) => actions.length ? `<article class="cc-card">
    <div class="cc-card__head"><div><h3>${escapeHtml(title)}</h3><p>${escapeHtml(detail)}</p></div></div>
    <div class="cc-page-actions" style="justify-content:flex-start">
      ${actions.map((actionId) => {
        const config = ACCOUNT_ACTIONS[actionId];
        return actionButton({
          label: config.label,
          action: 'open-account-action',
          tone: config.tone === 'danger' ? 'danger' : '',
          iconName: config.icon,
          attrs: `data-account-action="${escapeHtml(actionId)}"`,
          testId: `control-centre-account-action-${escapeHtml(actionId)}`,
        });
      }).join('')}
    </div>
  </article>` : '';
  return `<div class="cc-grid cc-grid--two">
    ${group('Access & security', 'Choose the exact level of account access or sign-in protection.', accessActions)}
    ${group('Plan & subscription', 'Manage the approved plan, trial or complimentary period.', subscriptionActions)}
    ${group('Account help & data', 'Help the tutor sign in, manage invitations or request an export.', supportActions)}
    ${group('Account closure', 'Closure has a safety waiting period and a restore window; permanent deletion is turned off.', lifecycleActions)}
  </div>`;
}

function renderAccountTab(state, account = {}) {
  switch (state.accountTab) {
    case 'people': return renderAccountPeople(account);
    case 'features': return renderAccountFeatures(state, account);
    case 'subscription': return renderAccountSubscription(state, account);
    case 'usage': return renderAccountUsage(state, account);
    case 'health': return renderAccountHealth(state, account);
    case 'support': return renderAccountSupport(account, state);
    case 'security': return renderAccountSecurity(account);
    case 'audit': return renderAccountAudit(account);
    default: return renderAccountOverview(state, account);
  }
}

function renderAccountOverview(state, account = {}) {
  const alerts = firstArray(account, ['alerts', 'openAlerts']);
  const recentOperations = firstArray(account, ['recentOperations', 'operations']);
  const period = reportingPeriodLabel(state, account);
  const coverageComplete = usageCoverageComplete(account);
  return `
    ${renderStudentAllowance(account, { context: 'overview', canEdit: state.canMutate })}
    <div class="cc-grid cc-grid--two">
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Account details and activity</h3><p>Students show the current number; lesson totals cover ${escapeHtml(period)}, including both dates.</p></div></div>
        <dl class="cc-kv">
          <div><dt>Owner email</dt><dd>${escapeHtml(firstValue(account, ['ownerEmail', 'email'], 'Not available'))}</dd></div>
          <div><dt>Account ID</dt><dd>${escapeHtml(accountId(account) || 'Not available')}</dd></div>
          <div><dt>Created</dt><dd>${escapeHtml(formatDateTime(firstValue(account, ['createdAt'], '')))}</dd></div>
          <div><dt>Last finished change</dt><dd>${escapeHtml(formatDateTime(firstValue(account, ['lastSuccessfulActionAt', 'lastActivityAt'], ''), 'No change recorded'))}</dd></div>
          <div><dt>Active students now</dt><dd>${formatCount(firstValue(account, ['studentCount'], 0))}</dd></div>
          <div><dt>Lessons booked</dt><dd>${escapeHtml(lessonTotalDisplay(account, ['bookedLessons', 'lessonsBooked', 'lessonCount']))}</dd></div>
          <div><dt>Lessons completed</dt><dd>${escapeHtml(lessonTotalDisplay(account, ['completedLessons', 'lessonsCompleted']))}</dd></div>
          <div><dt>Lesson period</dt><dd>${coverageComplete ? escapeHtml(period) : 'Earlier lesson information is updating'}</dd></div>
        </dl>
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Problems needing attention</h3><p>Account or service problems that may need a decision.</p></div></div>
        ${alerts.length ? alerts.map((item) => alertBox({
          title: plainAlertTitle(firstValue(item, ['type', 'alertType', 'title', 'label'], 'Account alert')),
          detail: plainSystemMessage(firstValue(item, ['detail', 'message'], '')),
          tone: statusTone(firstValue(item, ['severity', 'status'])) === 'danger' ? 'danger' : 'warn',
          iconName: 'alert',
        })).join('') : alertBox({
          title: 'No open account alerts',
          detail: 'No unresolved account or service problem was returned.',
          tone: 'info',
          iconName: 'check',
        })}
      </article>
    </div>
    <article class="cc-card cc-section">
      <div class="cc-card__head"><div><h3>Recent changes</h3><p>Every requested account change receives a permanent tracking reference.</p></div></div>
      ${recentOperations.length ? renderOperationList(recentOperations) : emptyState({
        title: 'No account changes yet',
        detail: 'Invitations, access changes and support actions will appear here.',
        iconName: 'operations',
      })}
    </article>
  `;
}

function renderAccountPeople(account = {}) {
  const people = firstArray(account, ['people', 'members']);
  const sessions = firstArray(account, ['sessions', 'activeSessions']);
  const sessionInventoryReturned = Array.isArray(account.sessions) || Array.isArray(account.activeSessions);
  const invitation = objectValue(account.invitation);
  return `
    <div class="cc-grid cc-grid--two">
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>People and account setup</h3><p>Who owns the account and whether the invitation was accepted.</p></div></div>
        <dl class="cc-kv">
          <div><dt>Owner</dt><dd>${escapeHtml(firstValue(account, ['ownerEmail', 'email'], 'Not available'))}</dd></div>
          <div><dt>Invitation</dt><dd>${escapeHtml(plainStatus(firstValue(invitation, ['status'], firstValue(account, ['invitationStatus'], 'not_reported')), 'invitation'))}</dd></div>
          <div><dt>Email delivery</dt><dd>${escapeHtml(plainStatus(firstValue(invitation, ['deliveryStatus'], 'unknown'), 'invitation'))}</dd></div>
          <div><dt>Last sign-in</dt><dd>${escapeHtml(formatDateTime(firstValue(account, ['lastSignInAt'], ''), 'No sign-in recorded'))}</dd></div>
        </dl>
        ${people.length ? `<ul class="cc-list cc-section">${people.map((person) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('users')}</span>
            <span class="cc-list-item__body"><strong>${escapeHtml(firstValue(person, ['name', 'email'], 'Account member'))}</strong><span>${escapeHtml(firstValue(person, ['role'], 'member'))}</span></span>
            ${statusBadge(plainStatus(firstValue(person, ['status'], 'unknown')), firstValue(person, ['status'], 'unknown'))}
          </li>
        `).join('')}</ul>` : ''}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Signed-in devices</h3><p>Sign-in information that does not expose private teaching content.</p></div>${statusBadge(`${sessions.length} signed in`, sessions.length ? 'active' : 'unknown')}</div>
        ${sessions.length ? `<ul class="cc-list">${sessions.map((session) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('lock')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(session, ['deviceLabel', 'device'], 'Signed-in device'))}</strong>
              <span>Last used ${escapeHtml(formatDateTime(firstValue(session, ['lastUsedAt', 'updatedAt'], ''), 'time unavailable'))}</span>
            </span>
            ${statusBadge(plainStatus(firstValue(session, ['status'], 'active')), firstValue(session, ['status'], 'active'))}
          </li>
        `).join('')}</ul>` : emptyState({
          title: sessionInventoryReturned ? 'No signed-in devices' : 'Signed-in device information is unavailable',
          detail: sessionInventoryReturned
            ? 'The sign-in system returned no active sessions.'
            : 'The account did not return sign-in records, so TToria cannot claim that no devices are signed in.',
          iconName: 'lock',
        })}
      </article>
    </div>
  `;
}

function renderAccountFeatures(state, account = {}) {
  const features = effectiveFeatureList(account);
  const policy = objectValue(account.policy);
  const explanationText = asArray(policy.explanation).map(cleanText).filter(Boolean).join(' ');
  return `
    ${alertBox({
      title: 'How feature access is decided',
      detail: explanationText || 'Security blocks and account safety rules come first. TToria then checks account restrictions, plan features, owner-approved extras, and each user’s permissions.',
      tone: 'info',
      iconName: 'info',
      testId: 'control-centre-feature-explanation',
    })}
    ${renderStudentAllowance(account, { context: 'features', canEdit: state.canMutate })}
    <div class="cc-grid cc-grid--two cc-section">
      <article class="cc-card">
        <div class="cc-card__head">
          <div><h3>Available features</h3><p>Exactly what this tutor account can use now.</p></div>
          ${state.canMutate ? actionButton({
            label: 'Preview a change',
            action: 'open-policy',
            iconName: 'settings',
            testId: 'control-centre-policy-open',
          }) : ''}
        </div>
        ${features.length ? `<ul class="cc-list">${features.map((feature) => {
          const enabled = feature.enabled === true || cleanText(feature.status).toLowerCase() === 'enabled';
          return `<li class="cc-list-item">
            <span class="cc-list-item__icon" style="color:${enabled ? 'var(--cc-green)' : 'var(--cc-muted)'}">${icon(enabled ? 'check' : 'lock')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(feature, ['label', 'name', 'featureId'], 'Feature'))}</strong>
              <span>${escapeHtml(plainSystemMessage(firstValue(feature, ['reason', 'explanation'], enabled ? 'Included in the account’s current feature settings.' : 'Not included in the account’s current feature settings.')))}</span>
            </span>
            ${statusBadge(enabled ? 'Enabled' : 'Disabled', enabled ? 'active' : 'read_only')}
          </li>`;
        }).join('')}</ul>` : emptyState({
          title: 'Feature information is unavailable',
          detail: 'No feature information was returned, so TToria is not assuming that any feature is available.',
          iconName: 'settings',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Feature setting history</h3><p>Review and restore earlier feature settings safely.</p></div></div>
        ${renderPolicyVersions(firstArray(account, ['policyVersions', 'featureVersions']))}
      </article>
    </div>
  `;
}

function renderPolicyVersions(versions = []) {
  if (!versions.length) {
    return emptyState({
      title: 'No feature setting history returned',
      detail: 'Saved feature changes will appear here with their reason and author.',
      iconName: 'audit',
    });
  }
  return `<ul class="cc-timeline">${versions.slice(0, 10).map((version) => `
    <li class="cc-timeline__item">
      <span class="cc-timeline__dot"></span>
      <span class="cc-timeline__content">
        <strong>${escapeHtml(firstValue(version, ['label'], `Version ${firstValue(version, ['version'], '?')}`))}</strong>
        <span>${escapeHtml(firstValue(version, ['reason', 'detail'], 'No reason supplied.'))}</span>
        <time>${escapeHtml(formatDateTime(firstValue(version, ['createdAt', 'effectiveAt'], '')))}</time>
      </span>
    </li>
  `).join('')}</ul>`;
}

function renderAccountSubscription(state, account = {}) {
  const subscription = objectValue(account.subscription);
  const invoices = firstArray(account, ['invoices']);
  const subscriptionState = firstValue(
    subscription,
    ['state', 'status'],
    firstValue(account, ['subscriptionState', 'subscriptionStatus'], 'none')
  );
  return `
    <div class="cc-grid cc-grid--two">
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Subscription</h3><p>Stripe handles subscription payments.</p></div>${statusBadge(plainStatus(subscriptionState, 'subscription'), subscriptionState)}</div>
        <dl class="cc-kv">
          <div><dt>Plan</dt><dd>${escapeHtml(firstValue(subscription, ['planName'], firstValue(account, ['planName', 'planId', 'plan'], 'None')))}</dd></div>
          <div><dt>Status</dt><dd>${escapeHtml(plainStatus(subscriptionState, 'subscription'))}</dd></div>
          <div><dt>Trial ends</dt><dd>${escapeHtml(formatDate(firstValue(subscription, ['trialEndsAt'], ''), 'No trial'))}</dd></div>
          <div><dt>Paid through</dt><dd>${escapeHtml(formatDate(firstValue(subscription, ['paidThroughAt', 'currentPeriodEndsAt', 'currentPeriodEnd'], ''), 'Not available'))}</dd></div>
          <div><dt>Temporary access ends</dt><dd>${escapeHtml(formatDate(firstValue(subscription, ['graceEndsAt'], ''), 'Not applicable'))}</dd></div>
          <div><dt>Currency</dt><dd>${escapeHtml(firstValue(subscription, ['currency'], 'CAD').toUpperCase())}</dd></div>
        </dl>
        ${state.canMutate ? `<div class="cc-page-actions cc-section" style="justify-content:flex-start">
          ${actionButton({
            label: 'Start hosted Checkout',
            action: 'open-account-action',
            tone: 'primary',
            iconName: 'external',
            attrs: 'data-account-action="stripe-checkout"',
            testId: 'control-centre-account-billing-checkout',
          })}
          ${actionButton({
            label: 'Open billing portal',
            action: 'open-account-action',
            iconName: 'external',
            attrs: 'data-account-action="stripe-portal"',
            testId: 'control-centre-account-billing-portal',
          })}
        </div>` : alertBox({
          title: 'Billing changes are locked',
          detail: 'Confirm your identity with a registered passkey before creating a secure billing link.',
          tone: 'info',
          iconName: 'lock',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Billing safety</h3><p>TToria never stores payment-card details.</p></div>${icon('shield')}</div>
        ${alertBox({
          title: 'Hosted billing',
          detail: 'Checkout, payment methods and the customer billing portal are hosted by Stripe.',
          tone: 'info',
          iconName: 'external',
        })}
        ${alertBox({
          title: 'Latest confirmed billing information',
          detail: firstValue(subscription, ['reconciledAt'], '')
            ? `Last checked ${formatDateTime(subscription.reconciledAt)}.`
            : plainSystemMessage(firstValue(subscription, ['reconciliationDetail'], 'No last-checked time was returned for this subscription.')),
          tone: firstValue(subscription, ['reconciledAt'], '') ? 'info' : 'warn',
          iconName: 'receipt',
        })}
      </article>
    </div>
    <article class="cc-card cc-section">
      <div class="cc-card__head"><div><h3>Invoices</h3><p>Subscription invoices only; student payments are never shown here.</p></div></div>
      ${invoices.length ? `<div class="cc-table-wrap"><table class="cc-table">
        <caption class="cc-visually-hidden">TToria subscription invoices</caption>
        <thead><tr><th scope="col">Invoice</th><th scope="col">Date</th><th scope="col">Amount</th><th scope="col">Status</th></tr></thead>
        <tbody>${invoices.map((invoice) => `<tr>
          <td><span class="cc-table__primary">${escapeHtml(firstValue(invoice, ['number', 'id'], 'Invoice'))}</span></td>
          <td>${escapeHtml(formatDate(firstValue(invoice, ['createdAt', 'date'], '')))}</td>
          <td>${escapeHtml(formatMoney(firstValue(invoice, ['amount', 'amountDue'], 0), firstValue(invoice, ['currency'], 'CAD')))}</td>
          <td>${statusBadge(plainStatus(firstValue(invoice, ['status'], 'unknown')), firstValue(invoice, ['status'], 'unknown'))}</td>
        </tr>`).join('')}</tbody>
      </table></div>` : emptyState({
        title: 'Invoice list is not available here',
        detail: 'This account did not return an invoice list. This does not prove that no Stripe invoice exists.',
        iconName: 'receipt',
      })}
    </article>
  `;
}

function renderAccountUsage(state, account = {}) {
  const selectedRange = objectValue(account.selectedRange);
  const rangeFrom = firstValue(selectedRange, ['from'], state.reportingFrom);
  const rangeTo = firstValue(selectedRange, ['to'], state.reportingTo);
  const usage = metricRows(account).filter((item) => {
    const date = cleanText(item?.date);
    return !date || (date >= rangeFrom && date <= rangeTo);
  });
  const costs = firstArray(account, ['costAllocations', 'costs']);
  const latestCostPeriod = costs.reduce(
    (latest, item) => cleanText(item?.periodEnd) > latest ? cleanText(item?.periodEnd) : latest,
    ''
  );
  const latestCosts = latestCostPeriod
    ? costs.filter((item) => cleanText(item?.periodEnd) === latestCostPeriod)
    : [];
  const estimatedAllocation = latestCosts.reduce(
    (total, item) => total + minorUnitsToMajor(item?.estimatedTenantMinorUnits),
    0
  );
  const currency = firstValue(latestCosts[0], ['currency'], 'CAD');
  const metricPeriod = `${formatDate(rangeFrom)} to ${formatDate(rangeTo)}`;
  const coverageComplete = usageCoverageComplete(account);
  return `
    ${renderReportingRangeForm(state)}
    ${alertBox({
      title: coverageComplete ? 'Selected dates' : 'Earlier activity information is updating',
      detail: coverageComplete
        ? `${metricPeriod}. The tutor account calculated the lesson totals below for this period.`
        : `${metricPeriod}. TToria will not display a partial lesson total while this account's full reporting coverage is being refreshed.`,
      tone: coverageComplete ? 'info' : 'warn',
      iconName: 'calendar',
    })}
    <div class="cc-grid cc-grid--metrics">
      ${metricCard({ label: 'Active students now', value: formatCount(firstValue(account, ['studentCount'], metricValue(usage, 'tenant.active_students', { latest: true }))), detail: 'Current number; changing the period does not change it', iconName: 'users' })}
      ${metricCard({ label: 'Lessons booked', value: lessonTotalDisplay(account, ['bookedLessons', 'lessonsBooked', 'lessonCount']), detail: coverageComplete ? metricPeriod : 'Earlier activity information is updating', iconName: 'calendar' })}
      ${metricCard({ label: 'Active days', value: coverageComplete ? formatCount(metricValue(usage, 'tenant.active_day')) : 'Unavailable', detail: coverageComplete ? 'Days with successful activity during the selected dates—not working hours' : 'Earlier activity information is updating', iconName: 'activity' })}
      ${metricCard({ label: 'Estimated account cost', value: formatMoney(estimatedAllocation, currency), detail: latestCostPeriod ? `Latest period ending ${formatDate(latestCostPeriod)}` : 'No estimate returned', iconName: 'usage', color: 'var(--cc-purple)', soft: 'var(--cc-purple-soft)' })}
    </div>
    <article class="cc-card cc-section">
      <div class="cc-card__head"><div><h3>Lesson totals</h3><p>Totals for the selected period without showing private lesson details.</p></div></div>
      <dl class="cc-kv">
        <div><dt>Booked</dt><dd>${escapeHtml(lessonTotalDisplay(account, ['bookedLessons', 'lessonsBooked', 'lessonCount']))}</dd></div>
        <div><dt>Completed</dt><dd>${escapeHtml(lessonTotalDisplay(account, ['completedLessons', 'lessonsCompleted']))}</dd></div>
        <div><dt>Cancelled</dt><dd>${escapeHtml(lessonTotalDisplay(account, ['cancelledLessons', 'lessonsCancelled']))}</dd></div>
        <div><dt>Selected period</dt><dd>${escapeHtml(metricPeriod)}</dd></div>
      </dl>
    </article>
    <article class="cc-card cc-section">
      <div class="cc-card__head"><div><h3>How estimated costs work</h3><p>Actual connected-service totals stay separate from each tutor account’s estimated share.</p></div></div>
      ${costs.length ? `<ul class="cc-list">${costs.map((cost) => `
        <li class="cc-list-item">
          <span class="cc-list-item__icon">${icon(firstValue(cost, ['icon'], 'usage'))}</span>
          <span class="cc-list-item__body">
            <strong>${escapeHtml(firstValue(cost, ['label', 'provider'], 'Estimated cost'))}</strong>
            <span>${escapeHtml(plainMethod(firstValue(cost, ['allocationMethod', 'method'], '')))} · ${escapeHtml(plainStatus(firstValue(cost, ['confidence'], 'unknown'), 'confidence'))} · period ending ${escapeHtml(formatDate(firstValue(cost, ['periodEnd'], ''), 'unknown'))}</span>
          </span>
          <strong>${escapeHtml(formatMoney(minorUnitsToMajor(firstValue(cost, ['estimatedTenantMinorUnits'], 0)), firstValue(cost, ['currency'], 'CAD')))}</strong>
        </li>
      `).join('')}</ul>` : emptyState({
        title: 'No cost estimate returned',
        detail: 'TToria will never invent or use a missing estimate for billing or suspension.',
        iconName: 'usage',
      })}
    </article>
  `;
}

function renderAccountHealth(state, account = {}) {
  const suppliedSignals = firstArray(account, ['healthSignals', 'healthChecks', 'services']);
  const projectionSignals = firstArray(account, ['projections']).map((projection) => ({
    name: `${firstValue(projection, ['resource'], 'Service')} account update`,
    detail: firstValue(projection, ['errorMessage'], '')
      || `Expected update ${firstValue(projection, ['expectedVersion'], 0)}; service confirmed update ${firstValue(projection, ['acknowledgedVersion'], 0)}.`,
    status: firstValue(projection, ['divergenceState'], 'unknown'),
  }));
  const healthExplanation = asArray(account.healthExplanation).map(cleanText).filter(Boolean).map((detail) => ({
    name: 'Account status check',
    detail,
    status: firstValue(account, ['healthState', 'healthStatus'], 'unknown'),
  }));
  const signals = suppliedSignals.length ? suppliedSignals : [...healthExplanation, ...projectionSignals];
  const jobs = firstArray(account, ['backgroundJobs', 'jobs']).length
    ? firstArray(account, ['backgroundJobs', 'jobs'])
    : firstArray(account, ['recentOperations']);
  return `
    <div class="cc-grid cc-grid--two">
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>What the service status uses</h3><p>Every result comes from a named check, not a hidden AI score.</p></div></div>
        ${signals.length ? `<ul class="cc-list">${signals.map((signal) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('activity')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(signal, ['name', 'label'], 'Service check'))}</strong>
              <span>${escapeHtml(firstValue(signal, ['detail', 'explanation'], 'No explanation supplied.'))}</span>
            </span>
            ${statusBadge(plainStatus(firstValue(signal, ['status'], 'unknown')), firstValue(signal, ['status'], 'unknown'))}
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'No service checks returned',
          detail: 'The Control Centre does not assume the account is working when no check was returned.',
          iconName: 'activity',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Work happening in the background</h3><p>Account update checks, email, and feature-setting changes.</p></div></div>
        ${jobs.length ? renderOperationList(jobs) : emptyState({
          title: 'No background work to show',
          detail: 'Background work will appear when the account has a change to finish or check.',
          iconName: 'operations',
        })}
      </article>
    </div>
  `;
}

function renderAccountSupport(account = {}, state = null) {
  const cases = firstArray(account, ['supportCases', 'cases']);
  const suppliedDiagnostics = objectValue(account.diagnostics);
  const projections = firstArray(account, ['projections']);
  const diagnostics = Object.keys(suppliedDiagnostics).length ? suppliedDiagnostics : {
    authentication: firstValue(account, ['lastSignInAt'], '') ? `Last sign-in ${formatDateTime(account.lastSignInAt)}` : 'No sign-in recorded',
    email: firstValue(objectValue(account.invitation), ['deliveryStatus'], 'Not reported'),
    studentPortal: firstValue(account, ['studentPortalStatus'], 'Not reported'),
    policy: projections.length
      ? `${projections.filter((item) => cleanText(item?.divergenceState) !== 'current').length} account update(s) are not current`
      : 'No account update records returned',
  };
  return `
    <div class="cc-grid cc-grid--two">
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Tutor help requests</h3><p>Problems reported by tutors and the updates shared with them.</p></div></div>
        ${cases.length ? `<ul class="cc-list">${cases.map((supportCase) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('info')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(supportCase, ['title', 'reference'], 'Tutor help request'))}</strong>
              <span>${escapeHtml(firstValue(supportCase, ['category', 'detail'], 'No category'))}</span>
            </span>
            <span>
              ${statusBadge(plainStatus(firstValue(supportCase, ['status'], 'open')), firstValue(supportCase, ['status'], 'open'))}
              ${
                state?.canMutate
                && cleanText(firstValue(supportCase, ['status', 'state'], 'open')).toLowerCase() === 'open'
                  ? actionButton({
                      label: 'Mark received',
                      action: 'open-account-action',
                      tone: 'primary',
                      iconName: 'info',
                      attrs: `data-account-action="ack-support-case" data-support-case-id="${escapeHtml(firstValue(supportCase, ['caseId', 'id']))}"`,
                    })
                  : ''
              }
            </span>
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'No open tutor help requests',
          detail: 'Tutor-reported problems will appear here without private teaching content.',
          iconName: 'info',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Safe troubleshooting information</h3><p>Settings and connected-service results only.</p></div></div>
        <dl class="cc-kv">
          <div><dt>Sign-in</dt><dd>${escapeHtml(firstValue(diagnostics, ['authentication'], 'Not checked'))}</dd></div>
          <div><dt>Email delivery</dt><dd>${escapeHtml(firstValue(diagnostics, ['email'], 'Not checked'))}</dd></div>
          <div><dt>Student Portal</dt><dd>${escapeHtml(firstValue(diagnostics, ['studentPortal'], 'Not checked'))}</dd></div>
          <div><dt>Feature settings update</dt><dd>${escapeHtml(firstValue(diagnostics, ['policy'], 'Not checked'))}</dd></div>
        </dl>
        <p class="cc-field__hint cc-section">Student names, lesson notes, assessments and payment details are excluded.</p>
      </article>
    </div>
  `;
}

function renderAccountSecurity(account = {}) {
  const events = [
    ...firstArray(account, ['securityEvents', 'privacyEvents']),
    ...firstArray(account, ['privacyRequests']),
  ];
  const privacy = objectValue(account.privacy);
  const holds = firstArray(account, ['legalHolds']);
  const closure = objectValue(account.closure);
  const closureSteps = firstArray(closure, ['steps']);
  return `
    <div class="cc-grid cc-grid--two">
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Security status</h3><p>Sign-in, account access, and recovery information.</p></div></div>
        <dl class="cc-kv">
          <div><dt>Extra sign-in protection</dt><dd>${escapeHtml(plainStatus(firstValue(account, ['mfaStatus'], 'unknown')))}</dd></div>
          <div><dt>Signed-in devices</dt><dd>${Object.prototype.hasOwnProperty.call(account, 'activeSessionCount') ? `${formatCount(account.activeSessionCount)} active` : 'Information not returned'}</dd></div>
          <div><dt>Suspicious activity</dt><dd>${escapeHtml(firstValue(account, ['suspiciousActivityStatus'], 'None reported'))}</dd></div>
          <div><dt>Deletion blocked for legal reasons</dt><dd>${escapeHtml(firstValue(privacy, ['legalHoldStatus'], holds.some((item) => !cleanText(item?.released_at || item?.releasedAt)) ? 'Yes' : holds.length ? 'No longer blocked' : 'No record returned'))}</dd></div>
          <div><dt>Export</dt><dd>${escapeHtml(firstValue(privacy, ['exportStatus'], 'Not requested'))}</dd></div>
          <div><dt>Closure</dt><dd>${escapeHtml(firstValue(privacy, ['closureStatus'], firstValue(closure, ['state'], 'Not scheduled')))}</dd></div>
          <div><dt>Close no earlier than</dt><dd>${closure.cooldownEndsAt ? escapeHtml(formatDateTime(closure.cooldownEndsAt)) : 'Not scheduled'}</dd></div>
          <div><dt>Paid through</dt><dd>${closure.paidThroughAt ? escapeHtml(formatDateTime(closure.paidThroughAt)) : 'Not applicable'}</dd></div>
          <div><dt>Restore until</dt><dd>${closure.restoreUntil ? escapeHtml(formatDateTime(closure.restoreUntil)) : 'Available after closure'}</dd></div>
          <div><dt>Permanent deletion</dt><dd>Disabled</dd></div>
        </dl>
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Recent security and privacy events</h3><p>Sanitized evidence only.</p></div></div>
        ${events.length ? renderTimeline(events) : emptyState({
          title: 'No security or privacy events',
          detail: 'No recent event was returned for this account.',
          iconName: 'shield',
        })}
      </article>
    </div>
    ${closureSteps.length ? `
      <article class="cc-card cc-section" data-testid="control-centre-closure-evidence">
        <div class="cc-card__head">
          <div>
            <h3>Account closure progress</h3>
            <p>Each safe step is shown separately and can be reversed during the allowed period. No teaching content is included.</p>
          </div>
          ${statusBadge(plainStatus(firstValue(closure, ['state'], 'not_reported'), 'lifecycle'), firstValue(closure, ['state'], 'unknown'))}
        </div>
        <ul class="cc-list">
          ${closureSteps.map((step) => `
            <li class="cc-list-item">
              <span class="cc-list-item__icon">${icon('shield')}</span>
              <span class="cc-list-item__body">
                <strong>${escapeHtml(plainAction(step?.key || 'closure step'))}</strong>
                <span>Updated ${escapeHtml(formatDateTime(firstValue(step, ['updatedAt', 'recordedAt'])))}</span>
              </span>
              ${statusBadge(plainStatus(firstValue(step, ['state'], 'unknown'), 'operation'), firstValue(step, ['state'], 'unknown'))}
            </li>
          `).join('')}
        </ul>
      </article>
    ` : ''}
    ${alertBox({
      title: 'Permanent deletion is turned off',
      detail: 'Information cannot be permanently deleted until the privacy, backup, legal, recovery, and proof checks pass.',
      tone: 'warn',
      iconName: 'lock',
      testId: 'control-centre-purge-disabled',
    })}
  `;
}

function renderAccountAudit(account = {}) {
  const entries = firstArray(account, ['auditEntries', 'auditTimeline', 'events', 'audit']);
  return `
    <div class="cc-card__head"><div><h3>Account change history</h3><p>Who requested each change, why, what changed, the result, and its tracking reference.</p></div></div>
    ${entries.length ? renderTimeline(entries) : emptyState({
      title: 'No change history returned',
      detail: 'The permanent history did not return an event for this account.',
      iconName: 'audit',
    })}
  `;
}

function renderBilling(state) {
  const data = sectionData(state, 'billing');
  const summary = objectValue(data.summary || data.counts);
  const subscriptions = firstArray(data, ['subscriptions', 'accounts', 'items']);
  const providerEvents = firstArray(data, ['providerEvents']);
  const suppliedIssues = firstArray(data, ['issues', 'billingIssues']);
  const derivedIssues = providerEvents
    .filter((event) => cleanText(event?.state).toLowerCase() === 'failed' || cleanText(event?.error))
    .map((event) => ({
      title: firstValue(event, ['type', 'id'], 'Stripe event needs review'),
      detail: firstValue(event, ['error'], 'The connected-service update did not finish.'),
      status: 'past_due',
    }));
  const issues = suppliedIssues.length ? suppliedIssues : derivedIssues;
  return `
    ${pageHeading({
      title: 'Plans & Billing',
      detail: 'Stripe handles subscription payments. TToria shows the latest confirmed billing information.',
      testId: 'control-centre-billing-heading',
    })}
    ${renderStaleNotice(state, data)}
    ${alertBox({
      title: `Billing environment: ${cleanText(firstValue(data, ['mode'], 'unknown')) === 'test' ? 'Test payments' : 'Live payments'}`,
      detail: `Automatic access restrictions are ${plainStatus(firstValue(data, ['enforcement'], 'unknown'))}. This information comes from the billing system.`,
      tone: cleanText(firstValue(data, ['mode'])).toLowerCase() === 'test' ? 'warn' : 'info',
      iconName: 'billing',
    })}
    <section class="cc-grid cc-grid--metrics">
      ${metricCard({ label: 'Active subscriptions', value: formatCount(firstValue(summary, ['activeSubscriptions', 'active'], 0)), detail: 'Latest confirmed billing information', iconName: 'billing', color: 'var(--cc-green)', soft: 'var(--cc-green-soft)' })}
      ${metricCard({ label: 'Trials', value: formatCount(firstValue(summary, ['trials', 'trialing'], 0)), detail: 'Accounts evaluating TToria', iconName: 'calendar', color: 'var(--cc-purple)', soft: 'var(--cc-purple-soft)' })}
      ${metricCard({ label: 'Payment overdue', value: formatCount(firstValue(summary, ['pastDue'], 0)), detail: 'Inside or beyond the temporary-access period', iconName: 'alert', color: 'var(--cc-amber)', soft: 'var(--cc-amber-soft)' })}
      ${metricCard({ label: 'Billing information differences', value: formatCount(firstValue(summary, ['reconciliationIssues', 'mismatches'], issues.length)), detail: 'Stripe and TToria show different information', iconName: 'operations', color: issues.length ? 'var(--cc-red)' : 'var(--cc-blue)', soft: issues.length ? 'var(--cc-red-soft)' : 'var(--cc-blue-soft)' })}
    </section>
    <section class="cc-grid cc-grid--two cc-section">
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Billing problems needing attention</h2><p>Every access restriction requires clear evidence and review.</p></div>${statusBadge(`${issues.length} open`, issues.length ? 'past_due' : 'healthy')}</div>
        ${issues.length ? `<ul class="cc-list">${issues.map((issue) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon" style="color:var(--cc-amber)">${icon('alert')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(issue, ['title', 'accountName'], 'Billing issue'))}</strong>
              <span>${escapeHtml(firstValue(issue, ['detail', 'message'], 'Review the verified Stripe evidence.'))}</span>
            </span>
            ${statusBadge(plainStatus(firstValue(issue, ['status'], 'past_due'), 'subscription'), firstValue(issue, ['status'], 'past_due'))}
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'No billing problem needs action',
          detail: 'Stripe and TToria currently show the same confirmed subscription information.',
          iconName: 'check',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Billing boundaries</h2><p>What this page does and does not control.</p></div></div>
        ${alertBox({ title: 'No card details in TToria', detail: 'Payment-card information stays in Stripe-hosted Checkout and Customer Portal.', tone: 'info', iconName: 'shield' })}
        ${alertBox({ title: 'No student payment details', detail: 'This page shows TToria subscriptions only—not money tutors collect from students.', tone: 'info', iconName: 'billing' })}
        ${alertBox({ title: 'Staging is isolated', detail: 'Test-mode billing can never change production account access.', tone: 'info', iconName: 'lock' })}
      </article>
    </section>
    <article class="cc-card cc-section" data-testid="control-centre-subscription-list">
      <div class="cc-card__head"><div><h2>Subscriptions</h2><p>Current TToria subscription and service-access state by tutor account.</p></div></div>
      ${subscriptions.length ? `<div class="cc-table-wrap"><table class="cc-table">
        <caption class="cc-visually-hidden">Tutor account subscriptions</caption>
        <thead><tr><th scope="col">Tutor account</th><th scope="col">Plan</th><th scope="col">Subscription</th><th scope="col">Access</th><th scope="col">Paid through</th><th scope="col">Last checked</th></tr></thead>
        <tbody>${subscriptions.map((item) => `<tr>
          <td><span class="cc-table__primary">${escapeHtml(accountDisplayName(item))}</span><span class="cc-table__secondary">${escapeHtml(firstValue(item, ['ownerEmail', 'email'], ''))}</span></td>
          <td>${escapeHtml(firstValue(item, ['planName', 'planId', 'plan'], 'No plan'))}</td>
          <td>${statusBadge(plainStatus(firstValue(item, ['subscriptionState', 'subscriptionStatus', 'state', 'status'], 'none'), 'subscription'), firstValue(item, ['subscriptionState', 'subscriptionStatus', 'state', 'status'], 'none'))}</td>
          <td>${statusBadge(plainStatus(firstValue(item, ['serviceAccessState', 'accessState'], 'unknown'), 'access'), firstValue(item, ['serviceAccessState', 'accessState'], 'unknown'))}</td>
          <td>${escapeHtml(formatDate(firstValue(item, ['paidThroughAt', 'periodEndsAt', 'currentPeriodEnd'], ''), 'Not available'))}</td>
          <td>${firstValue(item, ['reconciledAt'], '')
            ? `<span title="${escapeHtml(formatDateTime(item.reconciledAt))}">${statusBadge('Confirmed')}</span>`
            : statusBadge(plainStatus(firstValue(item, ['reconciliationStatus'], item.reconciled === false ? 'warning' : 'not_reported')), firstValue(item, ['reconciliationStatus'], 'unknown'))}</td>
        </tr>`).join('')}</tbody>
      </table></div>` : emptyState({
        title: 'No subscription information',
        detail: 'No confirmed Stripe subscription information was returned.',
        iconName: 'billing',
      })}
    </article>
  `;
}

function renderUsage(state) {
  const data = sectionData(state, 'usage');
  const metrics = firstArray(data, ['metrics']);
  const coverage = objectValue(data.usageCoverage);
  const coverageComplete = !Object.keys(coverage).length || coverage.complete === true;
  const costRows = firstArray(data, ['costs', 'providers', 'providerTotals']);
  const derivedTotals = {
    actualProviderCost: costRows.reduce(
      (total, item) => total + minorUnitsToMajor(item?.actualPlatformTotalMinorUnits),
      0
    ),
    allocatedEstimate: costRows.reduce(
      (total, item) => total + minorUnitsToMajor(item?.estimatedTenantMinorUnits),
      0
    ),
    unallocatedSharedCost: costRows.reduce(
      (total, item) => total + minorUnitsToMajor(item?.unallocatedSharedMinorUnits),
      0
    ),
    activeAccounts: metrics.reduce(
      (maximum, item) => Math.max(maximum, safeNumber(item?.accountCount, 0)),
      0
    ),
  };
  const totals = Object.keys(objectValue(data.totals || data.summary)).length
    ? objectValue(data.totals || data.summary)
    : derivedTotals;
  const allocations = firstArray(data, ['allocations', 'accountAllocations', 'items']);
  const providers = costRows;
  const currency = firstValue(providers[0], ['currency'], firstValue(data, ['currency'], 'CAD'));
  return `
    ${pageHeading({
      title: 'Activity & Costs',
      detail: `Actual connected-service totals and tutor account estimates are kept separate · ${cleanText(firstValue(data, ['period'], 'Reporting period unavailable'))}.`,
      testId: 'control-centre-usage-heading',
    })}
    ${renderStaleNotice(state, data)}
    ${renderReportingRangeForm(state, {
      detail: 'Activity totals and overlapping cost-estimate periods are recalculated for these dates.',
    })}
    ${coverageComplete ? '' : alertBox({
      title: 'Earlier activity information is updating',
      detail: `${formatCount(firstValue(coverage, ['completeAccounts'], 0))} of ${formatCount(firstValue(coverage, ['totalAccounts'], 0))} tutor accounts have complete coverage for this period. TToria is hiding partial platform usage totals until every account is complete.`,
      tone: 'warn',
      iconName: 'calendar',
      testId: 'control-centre-usage-coverage-warning',
    })}
    ${alertBox({
      title: 'Cost estimates are not bills',
      detail: 'No estimate on this page can charge, suspend or automatically punish a tutor account.',
      tone: 'info',
      iconName: 'info',
      testId: 'control-centre-cost-disclaimer',
    })}
    <section class="cc-grid cc-grid--metrics cc-section">
      ${metricCard({ label: 'Connected-service total', value: formatMoney(firstValue(totals, ['actualProviderCost'], 0), currency), detail: 'Actual invoice total for TToria', iconName: 'billing', color: 'var(--cc-green)', soft: 'var(--cc-green-soft)' })}
      ${metricCard({ label: 'Estimated account share', value: formatMoney(firstValue(totals, ['allocatedEstimate'], 0), currency), detail: 'Across tutor accounts', iconName: 'usage' })}
      ${metricCard({ label: 'Shared cost not assigned to accounts', value: formatMoney(firstValue(totals, ['unallocatedSharedCost'], 0), currency), detail: 'Kept outside tutor account estimates', iconName: 'database', color: 'var(--cc-purple)', soft: 'var(--cc-purple-soft)' })}
      ${metricCard({ label: 'Active accounts', value: coverageComplete ? formatCount(firstValue(totals, ['activeAccounts'], 0)) : 'Unavailable', detail: coverageComplete ? 'During the selected dates' : 'Earlier activity information is updating', iconName: 'accounts', color: 'var(--cc-cyan)', soft: 'var(--cc-blue-soft)' })}
    </section>
    <section class="cc-grid cc-grid--two cc-section">
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Actual connected-service totals</h2><p>Platform-wide totals reported by each connected service.</p></div></div>
        ${providers.length ? `<ul class="cc-list">${providers.map((provider) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon(firstValue(provider, ['icon'], 'database'))}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(provider, ['name', 'provider'], 'Connected service'))}</strong>
              <span>Actual TToria total from the connected service’s billing information; tutor account estimates are listed separately · updated ${escapeHtml(formatDateTime(firstValue(provider, ['freshnessAt', 'updatedAt'], ''), 'unknown'))}</span>
            </span>
            <strong>${escapeHtml(formatMoney(
              Object.prototype.hasOwnProperty.call(provider, 'actualPlatformTotalMinorUnits')
                ? minorUnitsToMajor(provider.actualPlatformTotalMinorUnits)
                : firstValue(provider, ['actualCost', 'amount'], 0),
              firstValue(provider, ['currency'], currency)
            ))}</strong>
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'Connected-service totals are unavailable',
          detail: 'TToria does not guess or present a missing value as an actual total.',
          iconName: 'database',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>When activity was last updated</h2><p>Out-of-date or missing measurements are always clearly shown.</p></div></div>
        ${renderMetricFreshness(metrics)}
      </article>
    </section>
    <article class="cc-card cc-section" data-testid="control-centre-cost-allocation-list">
      <div class="cc-card__head"><div><h2>Estimated cost by tutor account</h2><p>How it was calculated, estimate quality, and last update are shown for every amount.</p></div></div>
      ${allocations.length ? `<div class="cc-table-wrap"><table class="cc-table">
        <caption class="cc-visually-hidden">Estimated tutor account costs</caption>
        <thead><tr><th scope="col">Tutor account</th><th scope="col">Estimate</th><th scope="col">Dates</th><th scope="col">How it was calculated</th><th scope="col">Estimate quality</th><th scope="col">Calculation rules</th><th scope="col">Last updated</th></tr></thead>
        <tbody>${allocations.map((item) => `<tr>
          <td><span class="cc-table__primary">${escapeHtml(accountDisplayName(item))}</span><span class="cc-table__secondary">${escapeHtml(accountId(item))}</span></td>
          <td>${escapeHtml(formatMoney(
            Object.prototype.hasOwnProperty.call(item, 'estimatedTenantMinorUnits')
              ? minorUnitsToMajor(item.estimatedTenantMinorUnits)
              : firstValue(item, ['estimatedCost', 'amount'], 0),
            firstValue(item, ['currency'], currency)
          ))}</td>
          <td>${escapeHtml(formatDate(firstValue(item, ['periodStart'], ''), 'Unknown'))} – ${escapeHtml(formatDate(firstValue(item, ['periodEnd'], ''), 'Unknown'))}</td>
          <td>${escapeHtml(plainMethod(firstValue(item, ['allocationMethod', 'method'], '')))}</td>
          <td>${statusBadge(plainStatus(firstValue(item, ['confidence'], 'unknown'), 'confidence'), firstValue(item, ['confidence'], 'unknown'))}</td>
          <td>${escapeHtml(firstValue(item, ['modelVersion'], 'Not supplied'))}</td>
          <td>${escapeHtml(formatDateTime(firstValue(item, ['freshnessAt', 'updatedAt'], ''), 'Unknown'))}</td>
        </tr>`).join('')}</tbody>
      </table></div>` : emptyState({
        title: 'No tutor account cost estimates',
        detail: 'Actual connected-service totals may still be available above.',
        iconName: 'usage',
      })}
    </article>
  `;
}

function renderMetricFreshness(metrics = []) {
  if (!metrics.length) {
    return emptyState({
      title: 'No last-updated information',
      detail: 'The measurement system did not return update times.',
      iconName: 'activity',
    });
  }
  const aggregationLabel = (metric) => {
    const method = cleanText(firstValue(metric, ['aggregationMethod'], '')).toLowerCase();
    if (['latest_snapshot', 'latest', 'snapshot'].includes(method)) return 'Latest recorded value during the selected dates';
    if (['period_sum', 'sum', 'total'].includes(method)) return 'Sum of activity in the selected period';
    if (['period_max', 'max'].includes(method)) return 'Highest value in the selected period';
    return plainMethod(method);
  };
  return `<ul class="cc-list">${metrics.slice(0, 10).map((metric) => `
    <li class="cc-list-item">
      <span class="cc-list-item__icon">${icon('activity')}</span>
      <span class="cc-list-item__body">
        <strong>${escapeHtml(firstValue(metric, ['label', 'name'], 'Measurement'))}</strong>
        <span>${formatCount(firstValue(metric, ['value'], 0))} ${escapeHtml(firstValue(metric, ['unit'], ''))} · ${escapeHtml(aggregationLabel(metric))} · updated ${escapeHtml(formatDateTime(firstValue(metric, ['freshnessAt', 'updatedAt'], ''), 'unknown'))}</span>
      </span>
      ${statusBadge(plainStatus(firstValue(metric, ['freshnessStatus', 'status'], firstValue(metric, ['freshnessAt'], '') ? 'reported' : 'unknown')), firstValue(metric, ['freshnessStatus', 'status'], 'unknown'))}
    </li>
  `).join('')}</ul>`;
}

function renderOperations(state) {
  const data = sectionData(state, 'operations');
  const summary = objectValue(data.summary || data.counts);
  const operations = firstArray(data, ['operations', 'items']);
  const suppliedInterventions = firstArray(data, ['manualInterventions', 'needsIntervention']);
  const interventions = suppliedInterventions.length
    ? suppliedInterventions
    : operations.filter((operation) => cleanText(operation?.state).toLowerCase() === 'failed');
  const inProgress = operations.filter((operation) =>
    ['accepted', 'running'].includes(cleanText(operation?.state).toLowerCase())
  ).length;
  const completedToday = operations.filter((operation) => {
    if (cleanText(operation?.state).toLowerCase() !== 'completed') return false;
    return cleanText(operation?.completedAt).slice(0, 10) === new Date().toISOString().slice(0, 10);
  }).length;
  return `
    ${pageHeading({
      title: 'Changes & Progress',
      detail: 'Requested changes stay visible until every required service confirms the result.',
      testId: 'control-centre-operations-heading',
    })}
    ${renderStaleNotice(state, data)}
    <section class="cc-grid cc-grid--metrics">
      ${metricCard({ label: 'In progress', value: formatCount(firstValue(summary, ['inProgress'], inProgress)), detail: 'Changes still being applied', iconName: 'operations' })}
      ${metricCard({ label: 'Waiting', value: formatCount(firstValue(summary, ['waiting'], 0)), detail: 'Waiting for a connected service to confirm', iconName: 'activity', color: 'var(--cc-amber)', soft: 'var(--cc-amber-soft)' })}
      ${metricCard({ label: 'Need your decision', value: formatCount(firstValue(summary, ['needsIntervention', 'failed'], interventions.length)), detail: 'A safe owner decision is required', iconName: 'alert', color: 'var(--cc-red)', soft: 'var(--cc-red-soft)' })}
      ${metricCard({ label: 'Finished today', value: formatCount(firstValue(summary, ['completedToday'], completedToday)), detail: 'Receipts are available', iconName: 'check', color: 'var(--cc-green)', soft: 'var(--cc-green-soft)' })}
    </section>
    ${interventions.length ? `<section class="cc-card cc-section">
      <div class="cc-card__head"><div><h2>Needs your decision</h2><p>Try a change again only after reviewing why it is safe.</p></div>${statusBadge(`${interventions.length} items`, 'warning')}</div>
      <ul class="cc-list">${interventions.map((operation) => renderOperationItem(operation, state, true)).join('')}</ul>
    </section>` : ''}
    <section class="cc-card cc-section" data-testid="control-centre-operation-list">
      <div class="cc-card__head"><div><h2>All requested changes</h2><p>See who requested each change, why, its progress, service confirmations, and permanent tracking reference.</p></div></div>
      ${operations.length ? renderOperationList(operations, state) : emptyState({
        title: 'No changes to show',
        detail: 'Account creation, access changes and support actions will appear here.',
        iconName: 'operations',
      })}
    </section>
  `;
}

function renderOperationList(operations = [], state = null) {
  return `<ul class="cc-list">${operations.slice(0, 30).map((operation) => renderOperationItem(operation, state, false)).join('')}</ul>`;
}

function renderOperationItem(operation = {}, state = null, intervention = false) {
  const id = cleanText(firstValue(operation, ['operationId', 'id', 'reference']));
  const status = firstValue(operation, ['status', 'state'], 'unknown');
  const stepCount = safeNumber(firstValue(operation, ['stepCount'], 0), 0);
  const completedStepCount = safeNumber(firstValue(operation, ['completedStepCount'], 0), 0);
  const derivedProgress = status === 'completed'
    ? 100
    : stepCount > 0
      ? (completedStepCount / stepCount) * 100
      : 0;
  const progress = clampPercent(firstValue(operation, ['progressPercent', 'progress'], derivedProgress));
  const canRetry = operation.canRetry !== false && (operation.canRetry === true || ['failed', 'waiting', 'running'].includes(cleanText(status).toLowerCase()))
    && state?.canMutate;
  const progressLabel = firstValue(
    operation,
    ['currentStep'],
    stepCount > 0 ? `${formatCount(completedStepCount)} of ${formatCount(stepCount)} service steps confirmed` : 'Waiting for the change to start'
  );
  return `<li class="cc-list-item" data-operation-id="${escapeHtml(id)}">
    <span class="cc-list-item__icon" style="color:${statusTone(status) === 'danger' ? 'var(--cc-red)' : 'var(--cc-blue)'}">${icon('operations')}</span>
    <span class="cc-list-item__body">
      <strong>${escapeHtml(plainAction(operation.operationType === 'account_password_reset' ? operation.operationType : firstValue(operation, ['title', 'operationType', 'action'], ''), operation))}</strong>
      <span>${escapeHtml(firstValue(operation, ['accountName', 'detail', 'reason'], 'No account detail'))}${id ? ` · ${escapeHtml(id)}` : ''}</span>
      ${operation.deliveryState === 'uncertain' ? `<span role="status">${escapeHtml(operation.explanation || 'Email delivery is unconfirmed. Check the inbox before requesting another link.')}</span>` : ''}
      <span style="margin-top:8px">${progressBar(progress, progressLabel)}</span>
    </span>
    <span style="display:grid;gap:7px;justify-items:end">
      ${statusBadge(plainStatus(status, 'operation'), status)}
      ${canRetry ? actionButton({
        label: intervention ? 'Review and try again' : 'Try again',
        action: 'retry-operation',
        attrs: `data-operation-id="${escapeHtml(id)}"`,
        testId: id ? `control-centre-operation-retry-${escapeHtml(id)}` : '',
        disabled: !id,
      }) : ''}
    </span>
  </li>`;
}

function renderIncidents(state) {
  const data = sectionData(state, 'incidents');
  const summary = objectValue(data.summary || data.counts);
  const incidents = firstArray(data, ['incidents', 'items']);
  const alerts = firstArray(data, ['alerts']);
  const canary = objectValue(data.syntheticCanary || data.canary);
  const providerHealth = objectValue(data.providerHealth);
  const accountOptions = firstArray(data, ['accountOptions']);
  const providers = firstArray(data, ['providerStatus', 'providers']);
  const openIncidents = incidents.filter((item) => cleanText(item?.state || item?.status).toLowerCase() !== 'resolved');
  const affectedAccounts = openIncidents.reduce(
    (total, item) => total + safeNumber(firstValue(item, ['affectedAccountCount', 'affectedTenantCount'], 0), 0),
    0
  );
  return `
    ${pageHeading({
      title: 'Service Problems',
      detail: 'Find problems, understand who is affected, restore service, and share updates without exposing teaching content.',
      actions: state.canMutate ? actionButton({
        label: 'Record service problem',
        action: 'open-incident-create',
        tone: 'primary',
        iconName: 'plus',
        testId: 'control-centre-incident-create-open',
      }) : '',
      testId: 'control-centre-incidents-heading',
    })}
    ${renderStaleNotice(state, data)}
    <section class="cc-grid cc-grid--metrics">
      ${metricCard({ label: 'Open service problems', value: formatCount(firstValue(summary, ['open'], openIncidents.length)), detail: 'Currently being managed', iconName: 'incidents', color: 'var(--cc-red)', soft: 'var(--cc-red-soft)' })}
      ${metricCard({ label: 'Affected accounts', value: formatCount(firstValue(summary, ['affectedAccounts'], affectedAccounts)), detail: 'Total only; no private teaching information', iconName: 'accounts', color: 'var(--cc-amber)', soft: 'var(--cc-amber-soft)' })}
      ${metricCard({ label: 'Automatic system check', value: friendlySystemCheckStatus(firstValue(canary, ['state', 'status'])), detail: formatDateTime(firstValue(canary, ['completedAt', 'lastCheckedAt'], ''), 'Not checked yet'), iconName: 'shield', color: 'var(--cc-green)', soft: 'var(--cc-green-soft)' })}
      ${metricCard({ label: 'Average time to restore service', value: cleanText(firstValue(summary, ['meanRecoveryTime'], 'Not available')), detail: 'Service problems resolved during this period', iconName: 'activity', color: 'var(--cc-purple)', soft: 'var(--cc-purple-soft)' })}
    </section>
    <section class="cc-grid cc-grid--two cc-section">
      <article class="cc-card" data-testid="control-centre-incident-list">
        <div class="cc-card__head"><div><h2>Service problem centre</h2><p>Impact level, who is affected, what is happening now, recovery, and updates.</p></div></div>
        ${incidents.length ? `<ul class="cc-list">${incidents.map((incident) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon" style="color:${statusTone(firstValue(incident, ['severity', 'state'])) === 'danger' ? 'var(--cc-red)' : 'var(--cc-amber)'}">${icon('incidents')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(incident, ['title', 'reference'], 'Service problem'))}</strong>
              <span>${escapeHtml(firstValue(incident, ['containment', 'recovery', 'followUp', 'detail', 'currentAction'], 'No current action supplied.'))} · ${formatCount(firstValue(incident, ['affectedAccountCount', 'affectedTenantCount'], 0))} accounts</span>
              ${state.canMutate ? `<span class="cc-inline-actions">
                ${actionButton({
                  label: 'Update',
                  action: 'open-incident-update',
                  attrs: `data-incident-id="${escapeHtml(incident.id)}"`,
                  testId: `control-centre-incident-update-${escapeHtml(incident.id)}`,
                })}
                ${cleanText(incident.state).toLowerCase() !== 'resolved' ? actionButton({
                  label: 'Resolve',
                  action: 'open-incident-resolve',
                  tone: 'primary',
                  attrs: `data-incident-id="${escapeHtml(incident.id)}"`,
                  testId: `control-centre-incident-resolve-${escapeHtml(incident.id)}`,
                }) : ''}
              </span>` : ''}
            </span>
            ${statusBadge(plainStatus(firstValue(incident, ['state', 'status'], 'open'), 'incident'), firstValue(incident, ['state', 'status'], 'open'))}
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'No open service problem',
          detail: 'The service problem record returned no current problem.',
          iconName: 'check',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Connected services and automatic checks</h2><p>Independent information used to check whether TToria is working.</p></div></div>
        ${providers.length ? `<ul class="cc-list">${providers.map((provider) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('activity')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(provider, ['name', 'provider'], 'Connected service'))}</strong>
              <span>${escapeHtml(plainSystemMessage(firstValue(provider, ['detail', 'reason'], 'No connected-service detail')))}</span>
            </span>
            ${statusBadge(plainStatus(firstValue(provider, ['status'], 'unknown')), firstValue(provider, ['status'], 'unknown'))}
          </li>
        `).join('')}</ul>` : alertBox({
          title: `Connected-service information: ${plainStatus(firstValue(providerHealth, ['state'], 'unknown'))}`,
          detail: plainSystemMessage(firstValue(providerHealth, ['explanation'], 'No connected service is being assumed to be working.')),
          tone: safeNumber(firstValue(providerHealth, ['openAlertCount'], 0), 0) > 0 ? 'warn' : 'info',
          iconName: 'activity',
        })}
        <div class="cc-divider"></div>
        ${alertBox({
          title: `Core account-access test: ${friendlySystemCheckStatus(firstValue(canary, ['state', 'status']))}`,
          detail: plainSystemMessage(firstValue(canary, ['explanation', 'skipReason'], 'This safe automatic check uses a test account, never a tutor’s information.')),
          tone: statusTone(firstValue(canary, ['state', 'status'])) === 'danger' ? 'danger' : statusTone(firstValue(canary, ['state', 'status'])) === 'warn' ? 'warn' : 'info',
          iconName: 'shield',
        })}
      </article>
    </section>
    ${alerts.length ? `<section class="cc-card cc-section">
      <div class="cc-card__head"><div><h2>Other problems needing attention</h2><p>Automatic checks that may point to a service problem.</p></div>${statusBadge(`${alerts.length} open`, 'warning')}</div>
      <ul class="cc-list">${alerts.map((alert) => `<li class="cc-list-item">
        <span class="cc-list-item__icon">${icon('alert')}</span>
        <span class="cc-list-item__body">
          <strong>${escapeHtml(plainAlertTitle(firstValue(alert, ['type', 'id'], 'Platform alert')))}</strong>
          <span>${escapeHtml(plainSystemMessage(firstValue(alert, ['explanation'], 'No explanation supplied.')))}${firstValue(alert, ['runbook'], '') ? ` · Help guide: ${escapeHtml(alert.runbook)}` : ''}</span>
        </span>
        ${statusBadge(plainStatus(firstValue(alert, ['severity', 'state'], 'warning'), 'severity'), firstValue(alert, ['severity', 'state'], 'warning'))}
      </li>`).join('')}</ul>
    </section>` : ''}
    <span hidden data-incident-account-options="${escapeHtml(JSON.stringify(accountOptions))}"></span>
  `;
}

function renderSecurity(state) {
  const data = sectionData(state, 'security');
  const privacyRequests = firstArray(data, ['privacyRequests', 'requests']);
  const legalHolds = firstArray(data, ['legalHolds']);
  const closures = firstArray(data, ['closures']);
  const authEvents = firstArray(data, ['recentAuthenticationEvents']);
  const auditIntegrity = objectValue(data.auditIntegrity);
  const privacyBoundary = objectValue(data.privacyBoundary);
  const purge = objectValue(data.permanentPurge);
  const openPrivacy = privacyRequests.filter((item) =>
    !['completed', 'closed'].includes(cleanText(item?.state).toLowerCase())
  );
  const activeHolds = legalHolds.filter((item) => !cleanText(item?.released_at || item?.releasedAt));
  const activeClosures = closures.filter((item) =>
    !['cancelled', 'restored'].includes(cleanText(item?.state).toLowerCase())
  );
  return `
    ${pageHeading({
      title: 'Security & Privacy',
      detail: 'Protect owner sign-ins, keep tutor accounts separate, and handle privacy, recovery, and deletion safely.',
      testId: 'control-centre-security-heading',
    })}
    ${renderStaleNotice(state, data)}
    <section class="cc-grid cc-grid--metrics">
      ${metricCard({ label: 'Recorded security changes', value: formatCount(firstValue(auditIntegrity, ['eventCount'], 0)), detail: firstValue(auditIntegrity, ['appendOnly'], false) ? 'Permanent history is available' : 'History protection not confirmed', iconName: 'shield', color: 'var(--cc-green)', soft: 'var(--cc-green-soft)' })}
      ${metricCard({ label: 'Deletion blocks for legal reasons', value: formatCount(activeHolds.length), detail: 'Permanent deletion must stay blocked', iconName: 'lock', color: 'var(--cc-purple)', soft: 'var(--cc-purple-soft)' })}
      ${metricCard({ label: 'Account closures', value: formatCount(activeClosures.length), detail: 'Waiting period, closed, or information kept', iconName: 'database', color: 'var(--cc-blue)', soft: 'var(--cc-blue-soft)' })}
      ${metricCard({ label: 'Privacy requests', value: formatCount(openPrivacy.length), detail: 'Open protected requests', iconName: 'audit', color: 'var(--cc-amber)', soft: 'var(--cc-amber-soft)' })}
    </section>
    <section class="cc-grid cc-grid--two cc-section">
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Recent owner sign-ins</h2><p>Owner sign-in history without private teaching information.</p></div></div>
        ${authEvents.length ? renderTimeline(authEvents) : emptyState({
          title: 'No sign-in history returned',
          detail: 'TToria does not invent sign-in results when none were returned.',
          iconName: 'shield',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Permanent history protection</h2><p>Whether recorded events are protected from unnoticed changes.</p></div>${statusBadge(firstValue(auditIntegrity, ['tamperEvident'], false) ? 'Protected' : 'Not confirmed')}</div>
        <dl class="cc-kv">
          <div><dt>Past records cannot be edited</dt><dd>${firstValue(auditIntegrity, ['appendOnly'], false) ? 'Confirmed' : 'Not confirmed'}</dd></div>
          <div><dt>Recorded events</dt><dd>${formatCount(firstValue(auditIntegrity, ['eventCount'], 0))}</dd></div>
          <div><dt>Latest event</dt><dd>${escapeHtml(formatDateTime(firstValue(auditIntegrity, ['latestEventAt'], ''), 'Not available'))}</dd></div>
          <div><dt>Permanent deletion</dt><dd>${firstValue(purge, ['enabled'], false) ? 'Turned on' : 'Turned off'}</dd></div>
        </dl>
        ${alertBox({
          title: 'Backup results are not available on this page',
          detail: 'Missing backup information does not mean that a backup or recovery test succeeded.',
          tone: 'warn',
          iconName: 'database',
        })}
      </article>
    </section>
    <section class="cc-grid cc-grid--two cc-section">
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Privacy requests</h2><p>Tracked separately from account support.</p></div></div>
        ${privacyRequests.length ? renderTimeline(privacyRequests) : emptyState({
          title: 'No open privacy request',
          detail: 'Exports, corrections and deletion requests will appear here.',
          iconName: 'audit',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>What owners can and cannot see</h2><p>Private teaching information stays outside the Control Centre.</p></div></div>
        ${alertBox({ title: 'You can see', detail: asArray(privacyBoundary.visible).map(cleanText).filter(Boolean).join(', ') || 'Tutor account identity, plan, totals, service status, settings, and change records.', tone: 'info', iconName: 'check' })}
        ${alertBox({ title: 'You cannot see', detail: asArray(privacyBoundary.neverVisible).map(cleanText).filter(Boolean).join(', ') || 'Student names, tutor notes, assessments, subjects, lesson content or student payment details.', tone: 'info', iconName: 'lock' })}
        ${alertBox({ title: 'Permanent deletion stays off', detail: plainSystemMessage(firstValue(purge, ['reason'], 'Permanent deletion is unavailable until the independent legal, privacy, backup, and recovery checks pass.')), tone: 'warn', iconName: 'alert' })}
      </article>
    </section>
  `;
}

function renderAudit(state) {
  const data = sectionData(state, 'audit');
  const entries = firstArray(data, ['entries', 'events', 'items']);
  const summary = objectValue(data.summary || data.counts);
  return `
    ${pageHeading({
      title: 'Activity History',
      detail: 'A permanent history of owner and system changes that is protected from unnoticed editing.',
      testId: 'control-centre-audit-heading',
    })}
    ${renderStaleNotice(state, data)}
    <section class="cc-grid cc-grid--metrics">
      ${metricCard({ label: 'Recorded today', value: formatCount(firstValue(summary, ['eventsToday'], entries.length)), detail: 'Owner and system activity', iconName: 'audit' })}
      ${metricCard({ label: 'Failed actions', value: formatCount(firstValue(summary, ['failedActions'], 0)), detail: 'Requests that did not finish', iconName: 'alert', color: 'var(--cc-red)', soft: 'var(--cc-red-soft)' })}
      ${metricCard({ label: 'Changes undone', value: formatCount(firstValue(summary, ['reversals'], 0)), detail: 'Changes linked to an earlier saved version', iconName: 'retry', color: 'var(--cc-purple)', soft: 'var(--cc-purple-soft)' })}
      ${metricCard({ label: 'Saved-history status', value: plainStatus(firstValue(summary, ['archiveStatus'], 'unknown')), detail: 'Evidence that permanent history is protected', iconName: 'database', color: 'var(--cc-green)', soft: 'var(--cc-green-soft)' })}
    </section>
    <section class="cc-card cc-section" data-testid="control-centre-audit-list">
      <div class="cc-card__head"><div><h2>Complete activity history</h2><p>Who acted, how they signed in, why, what changed, the result, and the tracking reference.</p></div></div>
      ${entries.length ? renderTimeline(entries) : emptyState({
        title: 'No activity history returned',
        detail: 'TToria does not invent history. Refresh or review the history service status.',
        iconName: 'audit',
      })}
    </section>
  `;
}

function renderSettings(state) {
  const data = sectionData(state, 'settings');
  const owner = objectValue(data.security);
  const ownerSession = objectValue(state.ownerSession);
  const registeredPasskeys = firstPresent(ownerSession.passkeyCount, owner.registeredPasskeys);
  const elevated = ownerSession.elevated === true;
  const elevatedUntil = firstPresent(ownerSession.elevatedUntil, owner.elevatedUntil);
  const settings = firstArray(data, ['settings']);
  const plans = firstArray(data, ['plans']);
  const immutableDefaults = objectValue(data.immutableDefaults);
  const formatSettingValue = (setting = {}) => {
    const value = setting.value;
    const meaningful = Object.entries(objectValue(value)).filter(([, item]) =>
      item !== undefined && item !== null && item !== ''
    );
    if (!meaningful.length) return 'No value recorded';
    return meaningful.map(([key, item]) => {
      const label = plainSettingValueLabel(key);
      const display = typeof item === 'boolean'
        ? plainBoolean(item)
        : ['status', 'mode'].includes(cleanText(key).toLowerCase())
          ? plainStatus(item)
          : cleanText(item);
      return `${label}: ${display}`;
    }).join(' · ');
  };
  return `
    ${pageHeading({
      title: 'Settings',
      detail: 'Review owner sign-in protection, plans, and safety rules that cannot be weakened here.',
      testId: 'control-centre-settings-heading',
    })}
    ${renderStaleNotice(state, data)}
    ${renderControlCentreGuideEditor(state)}
    <section class="cc-grid cc-grid--two">
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Owner sign-in protection</h2><p>Security information for the current owner.</p></div>${statusBadge(elevated ? 'Changes unlocked' : 'Changes locked', elevated ? 'active' : 'read_only')}</div>
        <dl class="cc-kv">
          <div><dt>Normal sign-in method</dt><dd>${firstValue(owner, ['normalAuthentication'], '') === 'passkey' ? 'Passkey' : 'Not returned'}</dd></div>
          <div><dt>Registered passkeys</dt><dd>${registeredPasskeys !== undefined ? formatCount(registeredPasskeys) : 'Not returned'}</dd></div>
          <div><dt>Authenticator app backup</dt><dd>${ownerSession.totpFallbackConfigured === true ? 'Set up' : 'Not set up'}</dd></div>
          <div><dt>Sensitive changes</dt><dd>${elevated ? `Unlocked until ${escapeHtml(formatDateTime(elevatedUntil, 'end time not returned'))}` : 'Locked'}</dd></div>
          <div><dt>Unused recovery codes</dt><dd>Not returned on this page</dd></div>
          <div><dt>Emergency owner sign-in test</dt><dd>Not returned on this page</dd></div>
        </dl>
        <p class="cc-field__hint cc-section">To make a sensitive change, confirm with a passkey and write a reason. Changes stay unlocked for 15 minutes.</p>
        <div class="cc-page-actions cc-section">
          ${actionButton({
            label: safeNumber(registeredPasskeys, 0) < 2 ? 'Add required backup passkey' : 'Add another passkey',
            action: 'open-owner-passkey',
            iconName: 'key',
            testId: 'control-centre-settings-add-passkey',
          })}
          ${!elevated ? actionButton({
            label: 'Confirm sensitive changes',
            action: 'open-owner-elevation',
            tone: 'primary',
            iconName: 'shield',
            testId: 'control-centre-settings-elevate',
          }) : ''}
        </div>
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Safety rules</h2><p>These protections cannot be weakened from this page.</p></div>${statusBadge('Protected', 'active')}</div>
        <dl class="cc-kv">
          <div><dt>Public inquiry form in Tutor Desk</dt><dd>${immutableDefaults.webInquiries === false ? 'Turned off' : 'Not confirmed'}</dd></div>
          <div><dt>Permanent deletion</dt><dd>${immutableDefaults.permanentPurge === false ? 'Turned off' : 'Not confirmed'}</dd></div>
          <div><dt>Cost estimates can restrict access</dt><dd>${immutableDefaults.estimatedCostsCanEnforceAccess === false ? 'Never' : 'Not confirmed'}</dd></div>
          <div><dt>A second owner must approve changes</dt><dd>${immutableDefaults.artificialTwoPersonApproval === false ? 'No' : 'Not confirmed'}</dd></div>
          <div><dt>Activity measurements</dt><dd>${Object.prototype.hasOwnProperty.call(data, 'metricDefinitionCount') ? formatCount(data.metricDefinitionCount) : 'Not returned'}</dd></div>
        </dl>
        ${alertBox({
          title: 'Missing information is never guessed',
          detail: 'Update times, last known working information, recovery goals, and backup results appear only when their source systems return them.',
          tone: 'info',
          iconName: 'shield',
        })}
      </article>
    </section>
    <section class="cc-grid cc-grid--two cc-section">
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Plans and active student limits</h2><p>Current plan starting values returned by the plan system.</p></div></div>
        ${plans.length ? `<div class="cc-grid cc-section">${plans.map((plan) => `
          <article class="cc-card">
            <div class="cc-card__head">
              <div>
                <h3>${escapeHtml(firstValue(plan, ['name', 'id'], 'Plan'))}</h3>
                <p>${escapeHtml(firstValue(plan, ['description'], 'No plan description'))}</p>
              </div>
              <span class="cc-page-actions">
                ${statusBadge(plan.active === true ? 'Active' : 'Inactive', plan.active === true ? 'active' : 'read_only')}
                ${state.canMutate && plan.active === true ? actionButton({
                  label: 'Change plan student limit',
                  action: 'open-plan-capacity',
                  iconName: 'settings',
                  testId: `control-centre-plan-capacity-open-${cleanText(plan.id || 'plan')}`,
                  attrs: `data-plan-id="${escapeHtml(firstValue(plan, ['id'], ''))}"`,
                }) : ''}
              </span>
            </div>
            <dl class="cc-kv">
              <div><dt>Plan reference</dt><dd>${escapeHtml(firstValue(plan, ['id'], 'Not returned'))}</dd></div>
              <div><dt>Trial</dt><dd>${formatCount(firstValue(plan, ['trialDays'], 0))} days</dd></div>
              <div><dt>Temporary access after a late payment</dt><dd>${formatCount(firstValue(plan, ['pastDueGraceDays'], 0))} days</dd></div>
              <div><dt>Update number</dt><dd>${formatCount(firstValue(plan, ['version'], 0))}</dd></div>
            </dl>
            ${renderStudentAllowance({ studentCapacity: objectValue(plan.studentCapacity) }, { context: `settings-plan-${cleanText(plan.id || 'plan')}` })}
          </article>
        `).join('')}</div>` : emptyState({
          title: 'No plans returned',
          detail: 'No plan or student-limit value is being guessed.',
          iconName: 'billing',
        })}
        ${!state.canMutate ? alertBox({
          title: 'Plan changes are locked',
          detail: 'Unlock sensitive changes first. You must then review what will happen before changing a plan’s student limit.',
          tone: 'info',
          iconName: 'lock',
        }) : ''}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h2>Protected Control Centre settings</h2><p>Saved settings, their update number, and when they were last changed.</p></div></div>
        ${settings.length ? `<ul class="cc-list">${settings.map((setting) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('settings')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(plainSettingLabel(setting.key))}</strong>
              <span>${escapeHtml(formatSettingValue(setting))}</span>
              <span>Update number ${formatCount(firstValue(setting, ['version'], 0))} · last changed ${escapeHtml(formatDateTime(firstValue(setting, ['updatedAt'], ''), 'time not returned'))}</span>
            </span>
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'No protected settings returned',
          detail: 'Control Centre settings cannot be confirmed from this response.',
          iconName: 'settings',
        })}
        <div class="cc-section">
          <h3>What this Control Centre does not do</h3>
          <ul class="cc-list">
            ${[
              ['Independent tutors only', 'Company managers, commissions, and payroll are not included yet.'],
              ['Owners cannot enter a tutor’s account', 'Tutors can choose to share safe troubleshooting information instead.'],
              ['Owners invite new accounts', 'Tutors cannot create their own public accounts yet.'],
              ['No billing based on activity', 'A cost estimate can never create a customer charge.'],
              ['AI cannot punish an account', 'AI cannot suspend, delete, or repair an account by itself.'],
            ].map(([title, detail]) => `<li class="cc-list-item">
              <span class="cc-list-item__icon">${icon('check')}</span>
              <span class="cc-list-item__body"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(detail)}</span></span>
            </li>`).join('')}
          </ul>
        </div>
      </article>
    </section>
  `;
}

function renderTimeline(entries = []) {
  return `<ul class="cc-timeline">${entries.slice(0, 40).map((entry) => {
    const status = firstValue(entry, ['status', 'state', 'result', 'severity', 'result_state'], 'info');
    const rawEvent = firstValue(entry, ['action', 'eventType', 'event_type', 'request_type', 'reference'], '');
    const title = plainEvent(firstValue(entry, ['title'], rawEvent || 'Recorded activity'));
    const actor = firstValue(entry, ['actorEmail', 'actor', 'actorId'], '');
    const trackingReference = firstValue(entry, ['correlationId', 'operationId'], '');
    return `<li class="cc-timeline__item">
      <span class="cc-timeline__dot" style="background:var(--cc-${statusTone(status) === 'danger' ? 'red' : statusTone(status) === 'warn' ? 'amber' : statusTone(status) === 'ok' ? 'green' : 'blue'})"></span>
      <span class="cc-timeline__content">
        <strong>${escapeHtml(title)}</strong>
        <span>${escapeHtml(firstValue(entry, ['detail', 'reason', 'message', 'failureDetails', 'failure_details'], 'No additional explanation supplied.'))}</span>
        <time>${escapeHtml(formatDateTime(firstValue(entry, ['createdAt', 'occurredAt', 'timestamp', 'received_at', 'placed_at', 'event_at'], ''), 'Time unavailable'))}${actor ? ` · by ${escapeHtml(actor)}` : ''}${trackingReference ? ` · tracking reference ${escapeHtml(trackingReference)}` : ''}</time>
      </span>
    </li>`;
  }).join('')}</ul>`;
}

function createWizardState() {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60 * 1000).toISOString().slice(0, 10);
  return {
    step: 0,
    values: {
      businessName: '',
      ownerEmail: '',
      accessChoice: 'trial',
      planId: '',
      trialDays: '14',
      features: ['progress_assessment'],
      effectiveDate: localDate,
      reason: 'Create an independent tutor account.',
    },
    errors: {},
    busy: false,
    submitError: '',
    idempotencyKey: createId('create-account'),
  };
}

function createOwnerSecurityDialog(state, mode = 'elevation') {
  return {
    mode: mode === 'passkey' ? 'passkey' : 'elevation',
    deviceName: 'Backup owner passkey',
    reason: '',
    busy: false,
    error: '',
    validationError: false,
    success: null,
    passkeyCount: safeNumber(state.ownerSession?.passkeyCount, 0),
  };
}

function renderOwnerSecurityModal(state) {
  const dialog = state.ownerSecurityDialog;
  const session = objectValue(state.ownerSession);
  const isPasskey = dialog.mode === 'passkey';
  const needsSecondPasskey = safeNumber(session.passkeyCount, 0) < 2
    || cleanText(session.passkeyReadiness) === 'second_passkey_required';
  const title = isPasskey ? 'Add a backup owner passkey' : 'Confirm sensitive changes';
  if (dialog.success) {
    return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
      <section
        class="cc-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cc-owner-security-success-title"
        data-cc-modal
        data-testid="control-centre-owner-security-success"
      >
        <header class="cc-modal__head">
          <div><h2 id="cc-owner-security-success-title">${escapeHtml(dialog.success.title)}</h2><p>Owner sign-in protection</p></div>
          ${modalCloseButton(false)}
        </header>
        <div class="cc-modal__body">
          ${alertBox({
            title: dialog.success.title,
            detail: dialog.success.detail,
            tone: 'info',
            iconName: 'check',
            role: 'status',
          })}
          <dl class="cc-review cc-section">
            ${dialog.success.passkeyCount !== undefined
              ? `<div class="cc-review__row"><dt>Registered passkeys</dt><dd>${formatCount(dialog.success.passkeyCount)}</dd></div>`
              : ''}
            ${dialog.success.elevatedUntil
              ? `<div class="cc-review__row"><dt>Sensitive changes confirmed until</dt><dd>${escapeHtml(formatDateTime(dialog.success.elevatedUntil))}</dd></div>`
              : ''}
            <div class="cc-review__row"><dt>What happens next</dt><dd>${escapeHtml(dialog.success.nextStep)}</dd></div>
          </dl>
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">This security event is saved in the permanent sign-in history.</span>
          ${actionButton({ label: 'Continue', action: 'close-modal', tone: 'primary', iconName: 'check' })}
        </footer>
      </section>
    </div>`;
  }
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-owner-security-title"
      data-cc-modal
      data-testid="control-centre-owner-${isPasskey ? 'passkey' : 'elevation'}-modal"
    >
      <header class="cc-modal__head">
        <div><h2 id="cc-owner-security-title">${escapeHtml(title)}</h2><p>Owner sign-in protection</p></div>
        ${modalCloseButton(dialog.busy)}
      </header>
      <form data-cc-form="${isPasskey ? 'owner-passkey' : 'owner-elevation'}" novalidate>
        <div class="cc-modal__body">
          ${dialog.error ? alertBox({
            title: isPasskey ? 'The passkey was not added' : 'Sensitive changes were not confirmed',
            detail: dialog.error,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
          }) : ''}
          ${isPasskey ? `
            ${alertBox({
              title: needsSecondPasskey ? 'A second passkey is required' : 'Add a separately protected passkey',
              detail: 'Use another device, hardware security key, or separately protected password manager so one lost device cannot lock you out.',
              tone: needsSecondPasskey ? 'warn' : 'info',
              iconName: 'key',
            })}
            <label class="cc-field cc-section">
              <span>Passkey name</span>
              <input
                type="text"
                name="ownerPasskeyName"
                maxlength="100"
                value="${escapeHtml(dialog.deviceName)}"
                autocomplete="off"
                data-cc-field="ownerPasskeyName"
                data-testid="control-centre-owner-passkey-name"
                ${dialog.validationError && cleanText(dialog.deviceName).length < 3 ? 'aria-invalid="true"' : ''}
              >
              ${dialog.validationError && cleanText(dialog.deviceName).length < 3
                ? '<span class="cc-field__error">Enter a clear name with at least 3 characters.</span>'
                : '<span class="cc-field__hint">For example: Mac Studio passkey or backup security key.</span>'}
            </label>
            ${alertBox({
              title: 'Your browser opens the secure passkey prompt',
              detail: 'TToria never sees biometric information. Your device confirms the passkey and returns only a signed result.',
              tone: 'info',
              iconName: 'shield',
            })}
          ` : needsSecondPasskey ? `
            ${alertBox({
              title: 'Add the required backup passkey first',
              detail: 'Sensitive changes remain locked until two separately protected passkeys are registered.',
              tone: 'warn',
              iconName: 'key',
            })}
          ` : `
            <label class="cc-field">
              <span>Why do you need to make sensitive changes?</span>
              <textarea
                name="ownerElevationReason"
                rows="3"
                maxlength="500"
                placeholder="For example: Review and restore the affected tutor account."
                data-cc-field="ownerElevationReason"
                data-testid="control-centre-owner-elevation-reason"
                ${dialog.validationError && cleanText(dialog.reason).length < 10 ? 'aria-invalid="true"' : ''}
              >${escapeHtml(dialog.reason)}</textarea>
              ${dialog.validationError && cleanText(dialog.reason).length < 10
                ? '<span class="cc-field__error">Enter at least 10 characters explaining the reason.</span>'
                : '<span class="cc-field__hint">The reason is saved in the permanent sign-in history.</span>'}
            </label>
            ${alertBox({
              title: 'Passkey confirmation',
              detail: 'Sensitive account changes always require a registered passkey. The authenticator app is only a fallback for signing in.',
              tone: 'info',
              iconName: 'shield',
            })}
            ${alertBox({
              title: 'Confirmation lasts 15 minutes',
              detail: 'After it expires, TToria locks sensitive changes again and asks for fresh confirmation before another one.',
              tone: 'info',
              iconName: 'today',
            })}
          `}
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">${isPasskey ? 'No account data changes during passkey registration.' : 'No tutor account changes in this step.'}</span>
          <span class="cc-modal__actions">
            ${actionButton({ label: 'Cancel', action: 'close-modal', disabled: dialog.busy })}
            ${!isPasskey && needsSecondPasskey ? actionButton({
              label: 'Add backup passkey',
              action: 'owner-security-passkey',
              tone: 'primary',
              iconName: 'key',
              disabled: dialog.busy,
            }) : actionButton({
              label: dialog.busy
                ? isPasskey ? 'Waiting for passkey…' : 'Confirming…'
                : isPasskey ? 'Create passkey' : 'Confirm for 15 minutes',
              type: 'submit',
              tone: 'primary',
              iconName: dialog.busy ? '' : isPasskey ? 'key' : 'shield',
              disabled: dialog.busy,
              testId: isPasskey
                ? 'control-centre-owner-passkey-submit'
                : 'control-centre-owner-elevation-submit',
            })}
          </span>
        </footer>
      </form>
    </section>
  </div>`;
}

function renderModalLayer(state) {
  if (state.receipt) return renderReceiptModal(state);
  if (state.ownerSecurityDialog) return renderOwnerSecurityModal(state);
  if (state.wizard) return renderAccountWizard(state);
  if (state.actionDialog) return renderAccountActionModal(state);
  if (state.policyDialog) return renderPolicyModal(state);
  if (state.capacityDialog) return renderCapacityModal(state);
  if (state.retryDialog) return renderRetryModal(state);
  if (state.incidentDialog) return renderIncidentModal(state);
  return '';
}

function renderOwnerSecurityControl(state) {
  const session = objectValue(state.ownerSession);
  if (cleanText(session.passkeyReadiness) === 'second_passkey_required' || safeNumber(session.passkeyCount, 0) < 2) {
    return actionButton({
      label: 'Add backup passkey',
      action: 'open-owner-passkey',
      iconName: 'key',
      testId: 'control-centre-owner-passkey-open',
    });
  }
  if (session.elevated !== true) {
    return actionButton({
      label: 'Confirm changes',
      action: 'open-owner-elevation',
      iconName: 'shield',
      tone: 'primary',
      testId: 'control-centre-owner-elevation-open',
    });
  }
  return statusBadge(
    session.elevatedUntil
      ? `Confirmed until ${formatDateTime(session.elevatedUntil, 'session expiry')}`
      : 'Sensitive changes confirmed',
    'active'
  );
}

function modalCloseButton(disabled = false) {
  return `<button
    type="button"
    class="cc-icon-button"
    data-cc-action="close-modal"
    aria-label="Close"
    ${disabled ? 'disabled' : ''}
  >${icon('close')}</button>`;
}

function renderAccountWizard(state) {
  const wizard = state.wizard;
  const step = WIZARD_STEPS[wizard.step] || WIZARD_STEPS[0];
  const isReview = step.id === 'review';
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-wizard-title"
      data-testid="control-centre-create-wizard"
      data-cc-modal
    >
      <header class="cc-modal__head">
        <div>
          <h2 id="cc-wizard-title">Create a tutor account</h2>
          <p>Step ${wizard.step + 1} of ${WIZARD_STEPS.length}: ${escapeHtml(step.label)}</p>
        </div>
        ${modalCloseButton(wizard.busy)}
      </header>
      <form data-cc-form="account-wizard" novalidate>
        <div class="cc-modal__body">
          <div class="cc-steps" aria-label="Account creation progress">
            ${WIZARD_STEPS.map((item, index) => `<span
              class="cc-step"
              data-complete="${index < wizard.step ? 'true' : 'false'}"
              data-current="${index === wizard.step ? 'true' : 'false'}"
              title="${escapeHtml(item.label)}"
            ></span>`).join('')}
          </div>
          ${wizard.submitError ? alertBox({
            title: 'The account was not created',
            detail: wizard.submitError,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
            testId: 'control-centre-wizard-error',
          }) : ''}
          ${renderWizardStep(state)}
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">Your answers stay here if something goes wrong.</span>
          <span class="cc-modal__actions">
            ${wizard.step > 0 ? actionButton({
              label: 'Back',
              action: 'wizard-back',
              iconName: 'back',
              disabled: wizard.busy,
              testId: 'control-centre-wizard-back',
            }) : actionButton({
              label: 'Cancel',
              action: 'close-modal',
              disabled: wizard.busy,
            })}
            ${isReview ? actionButton({
              label: wizard.busy ? 'Creating account…' : 'Create and send invitation',
              type: 'submit',
              tone: 'primary',
              iconName: wizard.busy ? '' : 'mail',
              disabled: wizard.busy,
              testId: 'control-centre-wizard-submit',
            }) : actionButton({
              label: 'Continue',
              action: 'wizard-next',
              tone: 'primary',
              iconName: 'chevron',
              disabled: wizard.busy,
              testId: 'control-centre-wizard-next',
            })}
          </span>
        </footer>
      </form>
    </section>
  </div>`;
}

function renderWizardStep(state) {
  const wizard = state.wizard;
  const stepId = WIZARD_STEPS[wizard.step]?.id || 'business';
  const values = wizard.values;
  const errors = wizard.errors;
  if (stepId === 'business') {
    return `
      <span class="cc-eyebrow">Who is this account for?</span>
      <h3>Enter the tutor or business name</h3>
      <p class="cc-field__hint">This is the name you will use to find and support the account later.</p>
      <label class="cc-field cc-section">
        <span>Tutor or business name</span>
        <input
          type="text"
          name="businessName"
          value="${escapeHtml(values.businessName)}"
          autocomplete="organization"
          maxlength="120"
          data-cc-field="businessName"
          data-testid="control-centre-wizard-business-name"
          ${errors.businessName ? 'aria-invalid="true" aria-describedby="cc-business-error"' : ''}
        >
        ${errors.businessName ? `<span class="cc-field__error" id="cc-business-error">${escapeHtml(errors.businessName)}</span>` : '<span class="cc-field__hint">Example: Maya Chen Tutoring</span>'}
      </label>
    `;
  }
  if (stepId === 'owner') {
    return `
      <span class="cc-eyebrow">Who will own it?</span>
      <h3>Enter the tutor’s email</h3>
      <p class="cc-field__hint">TToria will send a secure activation invitation to this address.</p>
      <label class="cc-field cc-section">
        <span>Owner email</span>
        <input
          type="email"
          name="ownerEmail"
          value="${escapeHtml(values.ownerEmail)}"
          autocomplete="email"
          inputmode="email"
          maxlength="254"
          data-cc-field="ownerEmail"
          data-testid="control-centre-wizard-owner-email"
          ${errors.ownerEmail ? 'aria-invalid="true" aria-describedby="cc-email-error"' : ''}
        >
        ${errors.ownerEmail ? `<span class="cc-field__error" id="cc-email-error">${escapeHtml(errors.ownerEmail)}</span>` : '<span class="cc-field__hint">The tutor will confirm this address during activation.</span>'}
      </label>
      ${alertBox({
        title: 'Existing TToria user?',
        detail: 'TToria will offer a safe membership-acceptance flow instead of creating a duplicate identity.',
        tone: 'info',
        iconName: 'info',
      })}
    `;
  }
  if (stepId === 'plan') return renderWizardPlanStep(state);
  if (stepId === 'features') return renderWizardFeaturesStep(state);
  if (stepId === 'effective') {
    return `
      <span class="cc-eyebrow">When should it begin?</span>
      <h3>Choose the start date</h3>
      <p class="cc-field__hint">The account stays in setup until the tutor accepts the invitation.</p>
      <label class="cc-field cc-section">
        <span>Start date</span>
        <input
          type="date"
          name="effectiveDate"
          value="${escapeHtml(values.effectiveDate)}"
          data-cc-field="effectiveDate"
          data-testid="control-centre-wizard-effective-date"
          ${errors.effectiveDate ? 'aria-invalid="true" aria-describedby="cc-effective-error"' : ''}
        >
        ${errors.effectiveDate ? `<span class="cc-field__error" id="cc-effective-error">${escapeHtml(errors.effectiveDate)}</span>` : '<span class="cc-field__hint">You can safely schedule a future start date.</span>'}
      </label>
      <label class="cc-field cc-section">
        <span>Why are you creating this account?</span>
        <textarea
          name="reason"
          rows="3"
          maxlength="500"
          placeholder="Example: Approved independent tutor joining the TToria trial."
          data-cc-field="reason"
          data-testid="control-centre-wizard-reason"
          ${errors.reason ? 'aria-invalid="true" aria-describedby="cc-create-reason-error"' : ''}
        >${escapeHtml(values.reason)}</textarea>
        ${errors.reason
          ? `<span class="cc-field__error" id="cc-create-reason-error">${escapeHtml(errors.reason)}</span>`
          : '<span class="cc-field__hint">Required for the permanent change history. Enter at least 10 characters.</span>'}
      </label>
      ${alertBox({
        title: 'Invitation safety',
        detail: 'The activation link is single-use, expires after 72 hours by default, and is not consumed by an email scanner.',
        tone: 'info',
        iconName: 'shield',
      })}
    `;
  }
  return renderWizardReview(state);
}

function availablePlanOptions(state) {
  const accountsData = sectionData(state, 'accounts');
  const billingData = sectionData(state, 'billing');
  return firstArray(accountsData, ['planOptions', 'plans']).length
    ? firstArray(accountsData, ['planOptions', 'plans'])
    : firstArray(billingData, ['planOptions', 'plans']);
}

function renderWizardPlanStep(state) {
  const wizard = state.wizard;
  const values = wizard.values;
  const errors = wizard.errors;
  const planOptions = availablePlanOptions(state);
  return `
    <span class="cc-eyebrow">Plan or trial</span>
    <h3>How should this account start?</h3>
    <p class="cc-field__hint">This can be changed later. Only plans already set up in TToria appear here.</p>
    <div class="cc-choice-grid cc-section">
      ${[
        ['trial', 'Start a trial', 'Default 14-day evaluation with full allowed access.'],
        ['plan', 'Assign an approved plan', 'Choose from the plans already available in TToria.'],
        ['none', 'No subscription yet', 'Create the account in setup mode. Access stays limited until a plan is assigned.'],
      ].map(([value, title, detail]) => `<label class="cc-choice">
        <input type="radio" name="accessChoice" value="${value}" data-cc-field="accessChoice" ${values.accessChoice === value ? 'checked' : ''}>
        <span><strong>${escapeHtml(title)}</strong><small>${escapeHtml(detail)}</small></span>
      </label>`).join('')}
    </div>
    ${values.accessChoice === 'trial' ? `<label class="cc-field cc-section">
      <span>Trial length in days</span>
      <input type="number" min="1" max="60" step="1" name="trialDays" value="${escapeHtml(values.trialDays)}" data-cc-field="trialDays" data-testid="control-centre-wizard-trial-days">
      ${errors.trialDays ? `<span class="cc-field__error">${escapeHtml(errors.trialDays)}</span>` : '<span class="cc-field__hint">Default: 14 days. Maximum in this screen: 60 days.</span>'}
    </label>` : ''}
    ${values.accessChoice === 'plan' ? `<label class="cc-field cc-section">
      <span>Approved plan</span>
      <select name="planId" data-cc-field="planId" data-testid="control-centre-wizard-plan">
        <option value="">Choose a plan</option>
        ${planOptions.map((plan) => {
          const id = cleanText(plan?.planId || plan?.id || plan?.value);
          const label = cleanText(plan?.label || plan?.name || id);
          return `<option value="${escapeHtml(id)}" ${values.planId === id ? 'selected' : ''}>${escapeHtml(label)}</option>`;
        }).join('')}
      </select>
      ${errors.planId ? `<span class="cc-field__error">${escapeHtml(errors.planId)}</span>` : planOptions.length ? '' : '<span class="cc-field__error">No approved plan catalogue is available. Choose a trial, complimentary access or no subscription.</span>'}
    </label>` : ''}
  `;
}

function featureCatalogForWizard(state) {
  const accountsData = sectionData(state, 'accounts');
  const catalog = firstArray(accountsData, ['featureCatalog', 'optionalFeatures']);
  if (catalog.length) return catalog;
  return [
    {
      featureId: 'ai_summary',
      label: 'AI Summary',
      detail: 'Protected learning summaries for tutors and students.',
    },
    {
      featureId: 'student_portal',
      label: 'Student Portal',
      detail: 'The tutor-managed portal experience for students.',
    },
    {
      featureId: 'progress_assessment',
      label: 'Progress & Assessment',
      detail: 'Goals, progress checks and goal-linked lesson ratings. This can stay optional for Basic releases.',
    },
    {
      featureId: 'calendar',
      label: 'Calendar',
      detail: 'Lesson scheduling and calendar views.',
    },
    {
      featureId: 'payments',
      label: 'Payments',
      detail: 'Tutor-owned student payment tracking.',
    },
    {
      featureId: 'library',
      label: 'Library',
      detail: 'Tutor teaching resources and materials.',
    },
  ];
}

function renderWizardFeaturesStep(state) {
  const values = state.wizard.values;
  const features = featureCatalogForWizard(state);
  return `
    <span class="cc-eyebrow">Optional capabilities</span>
    <h3>Choose extra features</h3>
    <p class="cc-field__hint">Core security, logout, privacy requests, accessibility and account help cannot be disabled.</p>
    <div class="cc-choice-grid cc-section">
      ${features.map((feature) => {
        const id = cleanText(feature?.featureId || feature?.id || feature?.value);
        const checked = values.features.includes(id);
        return `<label class="cc-choice">
          <input type="checkbox" name="features" value="${escapeHtml(id)}" data-cc-field="features" ${checked ? 'checked' : ''}>
          <span><strong>${escapeHtml(firstValue(feature, ['label', 'name'], id))}</strong><small>${escapeHtml(firstValue(feature, ['detail', 'description'], 'Optional account capability.'))}</small></span>
        </label>`;
      }).join('')}
    </div>
    ${alertBox({
      title: 'Web Inquiries stays off',
      detail: 'The website-specific Web Inquiries module is not available in TToria SaaS.',
      tone: 'info',
      iconName: 'lock',
    })}
  `;
}

function wizardAccessSummary(values = {}) {
  if (values.accessChoice === 'trial') return `${safeNumber(values.trialDays, 14)}-day trial`;
  if (values.accessChoice === 'plan') return `Approved plan: ${cleanText(values.planId, 'not selected')}`;
  return 'No subscription yet';
}

function renderWizardReview(state) {
  const values = state.wizard.values;
  const catalog = featureCatalogForWizard(state);
  const featureLabels = values.features.map((id) => firstValue(
    catalog.find((feature) => cleanText(feature?.featureId || feature?.id || feature?.value) === id),
    ['label', 'name'],
    id
  ));
  return `
    <span class="cc-eyebrow">Check your answers</span>
    <h3>Make sure everything is correct</h3>
    <p class="cc-field__hint">Nothing is created until you choose “Create and send invitation.”</p>
    <dl class="cc-review cc-section">
      <div class="cc-review__row"><dt>Tutor or business</dt><dd>${escapeHtml(values.businessName)}</dd></div>
      <div class="cc-review__row"><dt>Owner email</dt><dd>${escapeHtml(values.ownerEmail)}</dd></div>
      <div class="cc-review__row"><dt>Plan or trial</dt><dd>${escapeHtml(wizardAccessSummary(values))}</dd></div>
      <div class="cc-review__row"><dt>Optional features</dt><dd>${escapeHtml(featureLabels.join(', ') || 'None')}</dd></div>
      <div class="cc-review__row"><dt>Start date</dt><dd>${escapeHtml(formatDate(values.effectiveDate))}</dd></div>
      <div class="cc-review__row"><dt>Reason saved in history</dt><dd>${escapeHtml(values.reason)}</dd></div>
      <div class="cc-review__row"><dt>Invitation</dt><dd>Secure single-use activation email, normally valid for 72 hours</dd></div>
    </dl>
    ${alertBox({
      title: 'What happens next',
      detail: 'TToria creates the account, sends the invitation, and shows a receipt while each required service finishes its part.',
      tone: 'info',
      iconName: 'operations',
    })}
  `;
}

function renderAccountActionModal(state) {
  const dialog = state.actionDialog;
  const config = ACCOUNT_ACTIONS[dialog.actionId] || ACCOUNT_ACTIONS.suspend;
  const account = normalizeSectionPayload(state.accountDetails);
  const name = accountDisplayName(account);
  const planOptions = availablePlanOptions(state);
  const typedMatches = !config.requiresTypedName || cleanText(dialog.typedConfirmation) === name;
  const reasonReady = !config.requiresReason || cleanText(dialog.reason).length >= 10;
  const planReady = !config.requiresPlan || Boolean(cleanText(dialog.planId));
  const trialEndReady = !config.requiresTrialEnd
    || new Date(endOfDateIso(dialog.trialEndsAt)).getTime() > Date.now();
  const complimentaryEndReady = !config.requiresComplimentaryEnd
    || new Date(endOfDateIso(dialog.endsAt)).getTime() > Date.now();
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="cc-action-title"
      aria-describedby="cc-action-detail"
      data-testid="control-centre-account-action-modal"
      data-cc-modal
    >
      <header class="cc-modal__head">
        <div>
          <h2 id="cc-action-title">${escapeHtml(config.title)}</h2>
          <p>${escapeHtml(name)}</p>
        </div>
        ${modalCloseButton(dialog.busy)}
      </header>
      <form data-cc-form="account-action" novalidate>
        <div class="cc-modal__body">
          ${dialog.error ? alertBox({
            title: 'The request was not submitted',
            detail: dialog.error,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
          }) : ''}
          <div class="cc-alert ${config.tone === 'danger' ? 'cc-alert--warn' : 'cc-alert--info'}">
            <span class="cc-alert__icon">${icon(config.icon)}</span>
            <span><strong>${escapeHtml(config.detail)}</strong><span id="cc-action-detail">${escapeHtml(config.impact)}</span></span>
          </div>
          ${config.requiresPlan ? `<label class="cc-field cc-section">
            <span>Approved plan</span>
            <select
              name="actionPlanId"
              data-cc-field="actionPlanId"
              data-testid="control-centre-account-action-plan"
              ${dialog.validationError && !planReady ? 'aria-invalid="true"' : ''}
            >
              <option value="">Choose a plan</option>
              ${planOptions.map((plan) => {
                const id = cleanText(plan?.planId || plan?.id || plan?.value);
                const label = cleanText(plan?.label || plan?.name || id);
                return `<option value="${escapeHtml(id)}" ${dialog.planId === id ? 'selected' : ''}>${escapeHtml(label)}</option>`;
              }).join('')}
            </select>
            ${dialog.validationError && !planReady
              ? '<span class="cc-field__error">Choose an approved plan.</span>'
              : planOptions.length
                ? '<span class="cc-field__hint">Only plans returned by TToria’s current catalogue are available.</span>'
                : '<span class="cc-field__error">No approved plan catalogue is available. Refresh Tutor Accounts and try again.</span>'}
          </label>` : ''}
          ${config.requiresTrialEnd ? `<label class="cc-field cc-section">
            <span>Trial ends</span>
            <input
              type="date"
              name="actionTrialEndsAt"
              min="${escapeHtml(futureDateInput(1))}"
              value="${escapeHtml(dialog.trialEndsAt)}"
              data-cc-field="actionTrialEndsAt"
              data-testid="control-centre-account-action-trial-end"
              ${dialog.validationError && !trialEndReady ? 'aria-invalid="true"' : ''}
            >
            ${dialog.validationError && !trialEndReady
              ? '<span class="cc-field__error">Choose a future trial end date.</span>'
              : '<span class="cc-field__hint">TToria updates the account safely through every required service; this does not create a charge.</span>'}
          </label>` : ''}
          ${config.requiresComplimentaryEnd ? `<label class="cc-field cc-section">
            <span>Complimentary access ends</span>
            <input
              type="date"
              name="actionComplimentaryEndsAt"
              min="${escapeHtml(futureDateInput(1))}"
              value="${escapeHtml(dialog.endsAt)}"
              data-cc-field="actionComplimentaryEndsAt"
              data-testid="control-centre-account-action-complimentary-end"
              ${dialog.validationError && !complimentaryEndReady ? 'aria-invalid="true"' : ''}
            >
            ${dialog.validationError && !complimentaryEndReady
              ? '<span class="cc-field__error">Choose a future end date.</span>'
              : '<span class="cc-field__hint">Complimentary access always has a clear expiry date.</span>'}
          </label>` : ''}
          ${config.requiresReason ? `<label class="cc-field cc-section">
            <span>Why are you doing this?</span>
            <textarea
              name="actionReason"
              rows="3"
              maxlength="500"
              placeholder="Write a short, clear reason for the permanent history."
              data-cc-field="actionReason"
              data-testid="control-centre-account-action-reason"
              ${dialog.validationError && !reasonReady ? 'aria-invalid="true"' : ''}
            >${escapeHtml(dialog.reason)}</textarea>
            ${dialog.validationError && !reasonReady ? '<span class="cc-field__error">Enter at least 10 characters explaining the reason.</span>' : '<span class="cc-field__hint">The tutor may see an appropriate customer-facing explanation later.</span>'}
          </label>` : ''}
          ${dialog.actionId === 'close' ? alertBox({
            title: 'At least 24 hours, and never before paid-through',
            detail: 'The server chooses the later of the 24-hour safety period and the Stripe paid-through date. This screen cannot shorten or bypass that protection.',
            tone: 'warn',
            iconName: 'alert',
          }) : ''}
          ${config.requiresTypedName ? `<label class="cc-field cc-section">
            <span>Type <strong>${escapeHtml(name)}</strong> to confirm</span>
            <input
              type="text"
              name="typedConfirmation"
              value="${escapeHtml(dialog.typedConfirmation)}"
              autocomplete="off"
              data-cc-field="typedConfirmation"
              data-testid="control-centre-account-action-confirmation"
              ${dialog.validationError && !typedMatches ? 'aria-invalid="true"' : ''}
            >
            ${dialog.validationError && !typedMatches ? '<span class="cc-field__error">The account name does not match.</span>' : ''}
          </label>` : ''}
          ${alertBox({
            title: 'You may need to confirm with your passkey',
            detail: 'Sensitive changes are accepted only while changes are unlocked for 15 minutes. TToria always performs the final security check.',
            tone: 'info',
            iconName: 'key',
          })}
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">Every request receives a permanent tracking reference.</span>
          <span class="cc-modal__actions">
            ${actionButton({ label: 'Cancel', action: 'close-modal', disabled: dialog.busy })}
            ${actionButton({
              label: dialog.busy ? 'Submitting…' : config.confirmLabel,
              type: 'submit',
              tone: config.tone,
              iconName: dialog.busy ? '' : config.icon,
              disabled: dialog.busy,
              testId: 'control-centre-account-action-submit',
            })}
          </span>
        </footer>
      </form>
    </section>
  </div>`;
}

function createPolicyDialog(account = {}) {
  const features = effectiveFeatureList(account);
  const existingOverrides = new Map(
    firstArray(account, ['featureOverrides']).map((override) => [
      cleanText(override?.key),
      {
        decision: cleanText(override?.decision).toLowerCase(),
        expiresAt: cleanText(override?.expiresAt),
      },
    ])
  );
  const featureMap = {};
  for (const feature of features) {
    const id = cleanText(feature?.featureId || feature?.id || feature?.key);
    if (!id) continue;
    const enabled = feature.enabled === true || cleanText(feature.status).toLowerCase() === 'enabled';
    const existingOverride = existingOverrides.get(id) || {};
    featureMap[id] = {
      id,
      label: firstValue(feature, ['label', 'name'], id),
      detail: firstValue(feature, ['reason', 'explanation', 'detail'], 'Optional account capability.'),
      enabled,
      originalEnabled: enabled,
      changed: false,
      existingOverrideDecision: existingOverride.decision || '',
      existingOverrideExpiresAt: existingOverride.expiresAt || '',
      protected: feature.protected === true,
    };
  }
  if (!Object.keys(featureMap).length) {
    featureMap.progress_assessment = {
      id: 'progress_assessment',
      label: 'Progress & Assessment',
      detail: 'Goals, progress checks and goal-linked lesson ratings.',
      enabled: false,
      originalEnabled: false,
      changed: false,
      existingOverrideDecision: '',
      existingOverrideExpiresAt: '',
      protected: false,
    };
  }
  return {
    features: featureMap,
    reason: '',
    expiresAt: '',
    preview: null,
    busy: false,
    error: '',
    validationError: false,
    idempotencyKey: createId('policy'),
  };
}

function renderPolicyModal(state) {
  const dialog = state.policyDialog;
  const account = normalizeSectionPayload(state.accountDetails);
  const features = Object.values(dialog.features);
  const reasonReady = cleanText(dialog.reason).length >= 10;
  const hasChanges = features.some((feature) => feature.changed === true);
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal cc-modal--wide"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-policy-title"
      data-testid="control-centre-policy-modal"
      data-cc-modal
    >
      <header class="cc-modal__head">
        <div>
          <h2 id="cc-policy-title">${dialog.preview ? 'Check the feature impact' : 'Preview a feature change'}</h2>
          <p>${escapeHtml(accountDisplayName(account))}</p>
        </div>
        ${modalCloseButton(dialog.busy)}
      </header>
      <form data-cc-form="policy" novalidate>
        <div class="cc-modal__body">
          ${dialog.error ? alertBox({
            title: dialog.preview ? 'The change was not applied' : 'The preview could not be created',
            detail: dialog.error,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
          }) : ''}
          ${renderStudentAllowance(account, { context: 'policy' })}
          ${dialog.preview ? renderPolicyPreview(dialog.preview, dialog.features) : `
            <p class="cc-field__hint">Choose the highest feature access this tutor account should have. Individual user permissions can allow less, but never more.</p>
            <div class="cc-choice-grid cc-section">
              ${features.map((feature) => `<label class="cc-choice">
                <input
                  type="checkbox"
                  name="policyFeature"
                  value="${escapeHtml(feature.id)}"
                  data-cc-field="policyFeature"
                  ${feature.enabled ? 'checked' : ''}
                  ${feature.protected ? 'disabled' : ''}
                >
                <span>
                  <strong>${escapeHtml(feature.label)}</strong>
                  <small>${escapeHtml(feature.detail)}${feature.protected ? ' This safety feature cannot be disabled.' : ''}</small>
                </span>
              </label>`).join('')}
            </div>
            <div class="cc-grid cc-grid--two cc-section">
              <label class="cc-field">
                <span>Reason for the change</span>
                <textarea
                  name="policyReason"
                  rows="3"
                  maxlength="500"
                  placeholder="Explain why this account needs a different feature."
                  data-cc-field="policyReason"
                  data-testid="control-centre-policy-reason"
                  ${dialog.validationError && !reasonReady ? 'aria-invalid="true"' : ''}
                >${escapeHtml(dialog.reason)}</textarea>
                ${dialog.validationError && !reasonReady ? '<span class="cc-field__error">Enter at least 10 characters.</span>' : '<span class="cc-field__hint">Required for the permanent change history.</span>'}
              </label>
              <label class="cc-field">
                <span>Temporary exception ends (optional)</span>
                <input type="date" name="policyExpiresAt" value="${escapeHtml(dialog.expiresAt)}" data-cc-field="policyExpiresAt">
                <span class="cc-field__hint">Temporary feature access is safer. Features included in the plan do not need an end date.</span>
              </label>
            </div>
            ${alertBox({
              title: 'Essential protections stay on',
              detail: 'Sign-in, sign-out, accessibility, privacy requests, account export, and security notices cannot be switched off here.',
              tone: 'info',
              iconName: 'shield',
            })}
            ${!hasChanges ? '<p class="cc-field__hint">Change at least one optional feature to create an impact preview.</p>' : ''}
          `}
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">${dialog.preview ? 'Nothing changes until you apply this reviewed preview.' : 'The preview does not change the account.'}</span>
          <span class="cc-modal__actions">
            ${dialog.preview ? actionButton({
              label: 'Change choices',
              action: 'policy-edit',
              iconName: 'back',
              disabled: dialog.busy,
            }) : actionButton({
              label: 'Cancel',
              action: 'close-modal',
              disabled: dialog.busy,
            })}
            ${dialog.preview ? actionButton({
              label: dialog.busy ? 'Applying…' : 'Apply reviewed change',
              type: 'submit',
              tone: 'primary',
              iconName: dialog.busy ? '' : 'check',
              disabled: dialog.busy,
              testId: 'control-centre-policy-apply',
            }) : actionButton({
              label: dialog.busy ? 'Checking impact…' : 'Preview the impact',
              type: 'submit',
              tone: 'primary',
              iconName: dialog.busy ? '' : 'search',
              disabled: dialog.busy,
              testId: 'control-centre-policy-simulate',
            })}
          </span>
        </footer>
      </form>
    </section>
  </div>`;
}

function renderPolicyPreview(preview = {}, currentFeatures = {}) {
  const changes = firstArray(preview, ['changes', 'featureChanges']).length
    ? firstArray(preview, ['changes', 'featureChanges'])
    : Object.entries(objectValue(preview.proposedFeatures)).map(([featureId, enabled]) => ({
        featureId,
        label: FEATURE_LABELS[featureId] || featureId.replaceAll('_', ' '),
        before: Object.prototype.hasOwnProperty.call(currentFeatures, featureId)
          ? currentFeatures[featureId]?.originalEnabled === true ? 'Enabled' : 'Disabled'
          : 'Not returned',
        after: enabled === true ? 'Enabled' : 'Disabled',
      }));
  const effects = firstArray(preview, ['effects', 'impact']).length
    ? firstArray(preview, ['effects', 'impact'])
    : asArray(preview.explanations).map((message) => ({
        title: 'Feature-setting explanation',
        detail: cleanText(message),
      }));
  const warnings = asArray(preview.warnings).map(cleanText).filter(Boolean);
  return `
    ${renderStudentAllowance(preview, { context: 'preview' })}
    ${alertBox({
      title: firstValue(preview, ['title'], 'Impact preview ready'),
      detail: plainSystemMessage(firstValue(preview, ['summary', 'detail'], 'Review what the account will be able to use before applying it.')),
      tone: firstValue(preview, ['safe'], true) === false ? 'warn' : 'info',
      iconName: firstValue(preview, ['safe'], true) === false ? 'alert' : 'shield',
      testId: 'control-centre-policy-preview',
    })}
    ${warnings.map((warning) => alertBox({
      title: 'Safety note',
      detail: warning,
      tone: 'info',
      iconName: 'shield',
    })).join('')}
    <div class="cc-grid cc-grid--two cc-section">
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Feature changes</h3><p>What is available before and after the change.</p></div></div>
        ${changes.length ? `<ul class="cc-list">${changes.map((change) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('settings')}</span>
            <span class="cc-list-item__body">
              <strong>${escapeHtml(firstValue(change, ['label', 'featureId'], 'Feature'))}</strong>
              <span>${escapeHtml(firstValue(change, ['before'], 'unknown'))} → ${escapeHtml(firstValue(change, ['after'], 'unknown'))}</span>
            </span>
            ${statusBadge(firstValue(change, ['after'], 'unknown'))}
          </li>
        `).join('')}</ul>` : emptyState({
          title: 'No feature change',
          detail: 'The selected features match the account’s current settings.',
          iconName: 'check',
        })}
      </article>
      <article class="cc-card">
        <div class="cc-card__head"><div><h3>Expected impact</h3><p>What the tutor and Student Portal will experience.</p></div></div>
        ${effects.length ? `<ul class="cc-list">${effects.map((effect) => `
          <li class="cc-list-item">
            <span class="cc-list-item__icon">${icon('info')}</span>
            <span class="cc-list-item__body"><strong>${escapeHtml(firstValue(effect, ['title', 'surface'], 'Impact'))}</strong><span>${escapeHtml(firstValue(effect, ['detail', 'message'], ''))}</span></span>
          </li>
        `).join('')}</ul>` : alertBox({
          title: 'No additional impact reported',
          detail: 'Only the feature states shown on the left are expected to change.',
          tone: 'info',
          iconName: 'info',
        })}
      </article>
    </div>
    <dl class="cc-review cc-section">
      <div class="cc-review__row"><dt>Reason</dt><dd>${escapeHtml(firstValue(preview, ['reason'], 'Recorded with request'))}</dd></div>
      <div class="cc-review__row"><dt>Current saved version</dt><dd>${escapeHtml(firstValue(preview, ['currentVersion', 'expectedVersion', 'version'], 'Not available'))}</dd></div>
      <div class="cc-review__row"><dt>Undo option</dt><dd>${escapeHtml(plainSystemMessage(firstValue(preview, ['rollbackDetail'], 'The previous feature settings remain available to restore.')))}</dd></div>
    </dl>
  `;
}

function createCapacityDialog(authority = {}, {
  scope = 'account',
  targetId = '',
  targetName = '',
} = {}) {
  const allowance = normalizeStudentAllowance(authority);
  const isPlan = scope === 'plan';
  const today = new Date();
  const localDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
  return {
    scope: isPlan ? 'plan' : 'account',
    targetId: cleanText(targetId),
    targetName: cleanText(targetName),
    currentAuthority: authority,
    mode: isPlan
      ? allowance.effectiveLimit.unlimited ? 'unlimited' : 'custom'
      : allowance.overrideLimit.known
        ? allowance.overrideLimit.unlimited ? 'unlimited' : 'custom'
        : 'inherit',
    maxActiveStudents: isPlan && allowance.effectiveLimit.known && !allowance.effectiveLimit.unlimited
      ? String(allowance.effectiveLimit.value)
      : allowance.overrideLimit.known && !allowance.overrideLimit.unlimited
        ? String(allowance.overrideLimit.value)
        : '',
    enforcement: allowance.enforced ? 'enforced' : 'shadow',
    effectiveAt: localDate,
    reason: '',
    preview: null,
    busy: false,
    error: '',
    validationError: false,
    idempotencyKey: createId('capacity'),
  };
}

function capacityPayload(dialog = {}) {
  return {
    inheritPlan: dialog.mode === 'inherit',
    maxActiveStudents: dialog.mode === 'inherit'
      ? 'inherit'
      : dialog.mode === 'unlimited'
        ? 'unlimited'
        : safeNumber(dialog.maxActiveStudents, -1),
    enforcement: dialog.enforcement,
    effectiveAt: cleanText(dialog.effectiveAt),
    reason: cleanText(dialog.reason),
  };
}

function renderCapacityModal(state) {
  const dialog = state.capacityDialog;
  const account = normalizeSectionPayload(state.accountDetails);
  const isPlan = dialog.scope === 'plan';
  const targetName = isPlan ? cleanText(dialog.targetName, 'Plan') : accountDisplayName(account);
  const currentAuthority = isPlan ? objectValue(dialog.currentAuthority) : account;
  const currentAllowance = normalizeStudentAllowance(currentAuthority);
  const reasonReady = cleanText(dialog.reason).length >= 10;
  const customLimitReady = dialog.mode !== 'custom'
    || (
      Number.isInteger(Number(dialog.maxActiveStudents))
      && Number(dialog.maxActiveStudents) >= 0
      && Number(dialog.maxActiveStudents) <= 100000
    );
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal cc-modal--wide"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-capacity-title"
      data-testid="control-centre-capacity-modal"
      data-cc-modal
    >
      <header class="cc-modal__head">
        <div>
          <h2 id="cc-capacity-title">${dialog.preview
            ? `Check the ${isPlan ? 'plan ' : ''}student limit impact`
            : `Change ${isPlan ? 'plan ' : ''}student limit`}</h2>
          <p>${escapeHtml(isPlan ? `${targetName} plan` : targetName)}</p>
        </div>
        ${modalCloseButton(dialog.busy)}
      </header>
      <form data-cc-form="capacity" novalidate>
        <div class="cc-modal__body">
          ${dialog.error ? alertBox({
            title: dialog.preview ? 'The student limit was not changed' : 'The preview could not be created',
            detail: dialog.error,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
          }) : ''}
          ${dialog.preview ? renderCapacityPreview(dialog.preview, dialog) : `
            ${isPlan ? alertBox({
              title: `Current plan student limit: ${currentAllowance.effectiveLimit.label}`,
              detail: 'Accounts with their own limit stay unchanged. Accounts using the plan limit will receive the reviewed number.',
              tone: 'info',
              iconName: 'users',
              testId: 'control-centre-plan-capacity-current',
            }) : renderStudentAllowance(account, { context: 'capacity-current' })}
            <fieldset class="cc-field cc-section">
              <legend>${isPlan ? 'What should the plan student limit be?' : 'Which student limit should this account use?'}</legend>
              <div class="cc-choice-grid">
                ${(isPlan ? [
                  ['custom', 'Use a set number', 'Set the maximum active students for accounts using this plan limit.'],
                  ['unlimited', 'No student limit', 'The plan will not limit active students.'],
                ] : [
                  ['inherit', 'Use the plan student limit', 'Future plan changes will apply to this account.'],
                  ['custom', 'Use a custom number', 'Set a limit for this tutor account only.'],
                  ['unlimited', 'No student limit', 'Allow any number of active students.'],
                ]).map(([value, title, detail]) => `<label class="cc-choice">
                  <input type="radio" name="capacityMode" value="${value}" data-cc-field="capacityMode" ${dialog.mode === value ? 'checked' : ''}>
                  <span><strong>${escapeHtml(title)}</strong><small>${escapeHtml(detail)}</small></span>
                </label>`).join('')}
              </div>
            </fieldset>
            ${dialog.mode === 'custom' ? `<label class="cc-field cc-section">
              <span>Maximum active students</span>
              <input
                type="number"
                min="0"
                max="100000"
                step="1"
                name="capacityLimit"
                value="${escapeHtml(dialog.maxActiveStudents)}"
                data-cc-field="capacityLimit"
                data-testid="control-centre-capacity-limit"
                ${dialog.validationError && !customLimitReady ? 'aria-invalid="true"' : ''}
              >
              ${dialog.validationError && !customLimitReady
                ? '<span class="cc-field__error">Enter a whole number from 0 to 100,000.</span>'
                : `<span class="cc-field__hint">Existing students are never deleted, archived or hidden.${isPlan ? ' Account-specific overrides stay unchanged.' : ''}</span>`}
            </label>` : ''}
            <div class="${isPlan ? 'cc-section' : 'cc-grid cc-grid--two cc-section'}">
              ${isPlan ? alertBox({
                title: 'Each account keeps its own limit setting',
                detail: 'This plan change updates only the shared number. Each tutor account keeps its own choice between measuring only and blocking new additions at the limit.',
                tone: 'info',
                iconName: 'shield',
              }) : `<label class="cc-field">
                <span>How should the limit work?</span>
                <select name="capacityEnforcement" data-cc-field="capacityEnforcement">
                  <option value="shadow" ${dialog.enforcement === 'shadow' ? 'selected' : ''}>Measure only (nothing blocked)</option>
                  <option value="enforced" ${dialog.enforcement === 'enforced' ? 'selected' : ''}>Block new additions and reactivations at the limit</option>
                </select>
              </label>`}
              <label class="cc-field">
                <span>Start date</span>
                <input type="date" name="capacityEffectiveAt" value="${escapeHtml(dialog.effectiveAt)}" data-cc-field="capacityEffectiveAt">
              </label>
            </div>
            <label class="cc-field cc-section">
              <span>Why are you changing this ${isPlan ? 'plan ' : ''}student limit?</span>
              <textarea
                name="capacityReason"
                rows="3"
                maxlength="500"
                data-cc-field="capacityReason"
                data-testid="control-centre-capacity-reason"
                ${dialog.validationError && !reasonReady ? 'aria-invalid="true"' : ''}
              >${escapeHtml(dialog.reason)}</textarea>
              ${dialog.validationError && !reasonReady
                ? '<span class="cc-field__error">Enter at least 10 characters.</span>'
                : '<span class="cc-field__hint">Required for the permanent change history.</span>'}
            </label>
          `}
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">${dialog.preview
            ? 'Nothing changes until you apply this reviewed preview.'
            : `The preview does not change the ${isPlan ? 'plan or any tutor account' : 'account'}.`}</span>
          <span class="cc-modal__actions">
            ${dialog.preview
              ? actionButton({ label: 'Change choices', action: 'capacity-edit', iconName: 'back', disabled: dialog.busy })
              : actionButton({ label: 'Cancel', action: 'close-modal', disabled: dialog.busy })}
            ${actionButton({
              label: dialog.busy
                ? dialog.preview ? 'Applying…' : 'Checking impact…'
                : dialog.preview ? 'Apply reviewed student limit' : 'Preview the impact',
              type: 'submit',
              tone: 'primary',
              iconName: dialog.busy ? '' : dialog.preview ? 'check' : 'search',
              disabled: dialog.busy,
              testId: dialog.preview ? 'control-centre-capacity-apply' : 'control-centre-capacity-simulate',
            })}
          </span>
        </footer>
      </form>
    </section>
  </div>`;
}

function renderCapacityPreview(preview = {}, dialog = {}) {
  if (dialog.scope === 'plan' || preview.plan) {
    const proposed = objectValue(preview.proposed);
    const impact = objectValue(preview.impact);
    const proposedLimit = proposed.unlimited === true || proposed.maxActiveStudents == null
      ? 'Unlimited'
      : formatCount(proposed.maxActiveStudents);
    const explanations = asArray(preview.explanations).map(cleanText).filter(Boolean);
    return `
      ${alertBox({
        title: `Proposed plan student limit: ${proposedLimit}`,
        detail: 'This is a preview. No plan or tutor account has changed.',
        tone: 'info',
        iconName: 'users',
        testId: 'control-centre-capacity-preview',
      })}
      <section class="cc-grid cc-grid--metrics cc-section" aria-label="Plan student limit impact">
        ${metricCard({ label: 'Accounts using the plan limit', value: formatCount(impact.inheritingAccountCount), detail: 'Will receive this student limit', iconName: 'accounts' })}
        ${metricCard({ label: 'At or over proposed limit', value: formatCount(impact.atOrOverLimitCount), detail: 'May be unable to add or reactivate', iconName: 'alert', color: 'var(--cc-amber)', soft: 'var(--cc-amber-soft)' })}
        ${metricCard({ label: 'Already over proposed limit', value: formatCount(impact.overLimitCount), detail: 'Existing records remain available', iconName: 'shield', color: 'var(--cc-purple)', soft: 'var(--cc-purple-soft)' })}
      </section>
      <article class="cc-card cc-section">
        <div class="cc-card__head"><div><h3>Safety explanation</h3><p>Read every consequence before applying.</p></div>${statusBadge('Preview only')}</div>
        <ul class="cc-list">${explanations.map((explanation) => `<li class="cc-list-item">
          <span class="cc-list-item__icon">${icon('check')}</span>
          <span class="cc-list-item__body"><strong>Plan impact</strong><span>${escapeHtml(explanation)}</span></span>
        </li>`).join('')}</ul>
        <dl class="cc-review cc-section">
          <div class="cc-review__row"><dt>Current plan update number</dt><dd>${escapeHtml(firstValue(preview, ['expectedVersion', 'currentVersion'], 'Not returned'))}</dd></div>
          <div class="cc-review__row"><dt>Start date</dt><dd>${escapeHtml(formatDate(proposed.effectiveAt, 'Not returned'))}</dd></div>
        </dl>
      </article>
    `;
  }
  const proposed = objectValue(preview.proposed || preview.studentCapacity);
  const impacts = asArray(preview.impacts).map(cleanText).filter(Boolean);
  return `
    ${renderStudentAllowance({ studentCapacity: proposed }, { context: 'capacity-preview' })}
    <article class="cc-card cc-section" data-testid="control-centre-capacity-preview">
      <div class="cc-card__head"><div><h3>Expected impact</h3><p>Review every safety consequence before applying.</p></div>${statusBadge('Preview only')}</div>
      <ul class="cc-list">${impacts.map((impact) => `<li class="cc-list-item">
        <span class="cc-list-item__icon">${icon('info')}</span>
        <span class="cc-list-item__body"><strong>Safety check</strong><span>${escapeHtml(impact)}</span></span>
      </li>`).join('')}</ul>
      <dl class="cc-review cc-section">
        <div class="cc-review__row"><dt>Current account update number</dt><dd>${escapeHtml(firstValue(preview, ['expectedVersion', 'currentVersion'], 'Not returned'))}</dd></div>
      </dl>
    </article>
  `;
}

function renderRetryModal(state) {
  const dialog = state.retryDialog;
  const reasonReady = cleanText(dialog.reason).length >= 10;
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-retry-title"
      aria-describedby="cc-retry-detail"
      data-testid="control-centre-operation-retry-modal"
      data-cc-modal
    >
      <header class="cc-modal__head">
        <div>
          <h2 id="cc-retry-title">Review before trying this change again</h2>
          <p>Tracking reference ${escapeHtml(dialog.operationId)}</p>
        </div>
        ${modalCloseButton(dialog.busy)}
      </header>
      <form data-cc-form="retry-operation" novalidate>
        <div class="cc-modal__body">
          ${dialog.error ? alertBox({
            title: 'The request to try again was not submitted',
            detail: dialog.error,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
          }) : ''}
          ${alertBox({
            title: 'Try again only after reviewing the problem',
            detail: 'TToria repeats only the safe unfinished steps. It does not create a second account change or hide the earlier problem.',
            tone: 'warn',
            iconName: 'retry',
          })}
          <label class="cc-field cc-section">
            <span>Why is it safe to retry now?</span>
            <textarea
              name="retryReason"
              rows="3"
              maxlength="500"
              placeholder="For example: The email service recovered and the failed delivery step can run again."
              data-cc-field="retryReason"
              data-testid="control-centre-operation-retry-reason"
              ${dialog.validationError && !reasonReady ? 'aria-invalid="true"' : ''}
            >${escapeHtml(dialog.reason)}</textarea>
            ${dialog.validationError && !reasonReady
              ? '<span class="cc-field__error">Enter at least 10 characters explaining why the retry is safe.</span>'
              : '<span class="cc-field__hint" id="cc-retry-detail">The reason is saved in the permanent change history.</span>'}
          </label>
          ${alertBox({
            title: 'A fresh passkey may be required',
            detail: 'TToria accepts another attempt only while sensitive changes are unlocked and only when the change can still be safely repeated.',
            tone: 'info',
            iconName: 'key',
          })}
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">Nothing changes until you submit this reviewed request.</span>
          <span class="cc-modal__actions">
            ${actionButton({ label: 'Cancel', action: 'close-modal', disabled: dialog.busy })}
            ${actionButton({
              label: dialog.busy ? 'Submitting…' : 'Try change again',
              type: 'submit',
              tone: 'primary',
              iconName: dialog.busy ? '' : 'retry',
              disabled: dialog.busy,
              testId: 'control-centre-operation-retry-submit',
            })}
          </span>
        </footer>
      </form>
    </section>
  </div>`;
}

function createIncidentDialog(state, mode = 'create', incident = null) {
  const current = objectValue(incident);
  return {
    mode,
    incidentId: cleanText(current.id),
    expectedVersion: safeNumber(current.recordVersion, 0),
    title: cleanText(current.title),
    severity: cleanText(current.severity, 'medium'),
    state: cleanText(current.state, 'investigating'),
    detectionSource: cleanText(current.detectionSource, 'manual_owner_review'),
    containment: cleanText(current.containment),
    recovery: cleanText(current.recovery),
    followUp: cleanText(current.followUp),
    communicationState: cleanText(current.communicationState, 'not_required'),
    securityOrPrivacy: current.securityOrPrivacy === true,
    affectedTenantIds: asArray(current.affectedTenantIds).map(cleanText).filter(Boolean),
    reason: '',
    busy: false,
    error: '',
    validationError: false,
    idempotencyKey: createId(`incident-${mode}`),
  };
}

function renderIncidentModal(state) {
  const dialog = state.incidentDialog;
  const data = sectionData(state, 'incidents');
  const accounts = firstArray(data, ['accountOptions']);
  const resolving = dialog.mode === 'resolve';
  const updating = dialog.mode === 'update';
  const title = resolving
    ? 'Mark this service problem as resolved'
    : updating
      ? 'Update this service problem'
      : 'Record a service problem';
  const reasonReady = cleanText(dialog.reason).length >= 10;
  const detailsReady = resolving
    ? cleanText(dialog.recovery).length >= 10
    : cleanText(dialog.title).length >= 5 && cleanText(dialog.detectionSource).length >= 3;
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal cc-modal--wide"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-incident-title"
      data-testid="control-centre-incident-modal"
      data-cc-modal
    >
      <header class="cc-modal__head">
        <div>
          <h2 id="cc-incident-title">${escapeHtml(title)}</h2>
          <p>Record service facts only. Do not enter student information or teaching content.</p>
        </div>
        ${modalCloseButton(dialog.busy)}
      </header>
      <form data-cc-form="incident" novalidate>
        <div class="cc-modal__body">
          ${dialog.error ? alertBox({
            title: 'The service problem was not saved',
            detail: dialog.error,
            tone: 'danger',
            iconName: 'alert',
            role: 'alert',
          }) : ''}
          ${alertBox({
            title: 'Communication is recorded, not sent',
            detail: 'Changing the customer update status never sends an email. Send any approved message separately, then record its status here.',
            tone: 'info',
            iconName: 'info',
          })}
          ${!resolving ? `
            <label class="cc-field">
              <span>What happened?</span>
              <input name="incidentTitle" maxlength="160" value="${escapeHtml(dialog.title)}"
                data-cc-field="incidentTitle" ${dialog.validationError && cleanText(dialog.title).length < 5 ? 'aria-invalid="true"' : ''}>
              <span class="cc-field__hint">Use a short service description, not a tutor or student name.</span>
            </label>
            <div class="cc-grid cc-grid--two">
              <label class="cc-field">
                <span>Impact level</span>
                <select name="incidentSeverity" data-cc-field="incidentSeverity">
                  ${['low', 'medium', 'high', 'critical'].map((value) => `<option value="${value}" ${dialog.severity === value ? 'selected' : ''}>${escapeHtml(plainStatus(value, 'severity'))}</option>`).join('')}
                </select>
              </label>
              <label class="cc-field">
                <span>Current stage</span>
                <select name="incidentState" data-cc-field="incidentState">
                  ${['investigating', 'identified', 'monitoring'].map((value) => `<option value="${value}" ${dialog.state === value ? 'selected' : ''}>${escapeHtml(value === 'identified' ? 'Cause identified' : plainStatus(value, 'incident'))}</option>`).join('')}
                </select>
              </label>
            </div>
            <label class="cc-field">
              <span>How was it detected?</span>
              <input name="incidentDetectionSource" maxlength="120" value="${escapeHtml(dialog.detectionSource)}"
                data-cc-field="incidentDetectionSource" ${dialog.validationError && cleanText(dialog.detectionSource).length < 3 ? 'aria-invalid="true"' : ''}>
            </label>
          ` : ''}
          ${accounts.length && !resolving ? `<fieldset class="cc-field">
            <legend>Affected tutor accounts</legend>
            <span class="cc-field__hint">Choose only accounts confirmed to be affected. Service problem records store account references, not teaching information.</span>
            <div class="cc-check-grid">${accounts.map((account) => {
              const id = cleanText(account.id);
              return `<label class="cc-check">
                <input type="checkbox" name="incidentAffectedTenant" value="${escapeHtml(id)}"
                  data-cc-field="incidentAffectedTenant"
                  ${dialog.affectedTenantIds.includes(id) ? 'checked' : ''}>
                <span><strong>${escapeHtml(firstValue(account, ['label'], id))}</strong><small>${escapeHtml(id)}</small></span>
              </label>`;
            }).join('')}</div>
          </fieldset>` : ''}
          ${!resolving ? `<label class="cc-field">
            <span>What are we doing right now?</span>
            <textarea name="incidentContainment" rows="3" maxlength="2000" data-cc-field="incidentContainment">${escapeHtml(dialog.containment)}</textarea>
          </label>` : ''}
          <label class="cc-field">
            <span>${resolving ? 'How was service restored?' : 'Recovery update'}</span>
            <textarea name="incidentRecovery" rows="3" maxlength="2000" data-cc-field="incidentRecovery"
              ${dialog.validationError && resolving && cleanText(dialog.recovery).length < 10 ? 'aria-invalid="true"' : ''}>${escapeHtml(dialog.recovery)}</textarea>
            ${resolving ? '<span class="cc-field__hint">Enter at least 10 characters before resolving.</span>' : ''}
          </label>
          <label class="cc-field">
            <span>Follow-up work</span>
            <textarea name="incidentFollowUp" rows="3" maxlength="2000" data-cc-field="incidentFollowUp">${escapeHtml(dialog.followUp)}</textarea>
          </label>
          <label class="cc-field">
            <span>Customer update status</span>
            <select name="incidentCommunicationState" data-cc-field="incidentCommunicationState">
              ${[
                ['not_required', 'No communication needed'],
                ['draft', 'Draft being prepared'],
                ['ready', 'Approved and ready'],
                ['sent', 'Sent separately'],
              ].map(([value, label]) => `<option value="${value}" ${dialog.communicationState === value ? 'selected' : ''}>${label}</option>`).join('')}
            </select>
          </label>
          ${!resolving ? `<label class="cc-check">
            <input type="checkbox" name="incidentSecurityOrPrivacy" data-cc-field="incidentSecurityOrPrivacy" ${dialog.securityOrPrivacy ? 'checked' : ''}>
            <span><strong>This may involve security or privacy</strong><small>Mark this so the protected security response steps are followed.</small></span>
          </label>` : ''}
          <label class="cc-field">
            <span>Why are you making this change?</span>
            <textarea name="incidentReason" rows="3" maxlength="500" data-cc-field="incidentReason"
              ${dialog.validationError && !reasonReady ? 'aria-invalid="true"' : ''}>${escapeHtml(dialog.reason)}</textarea>
            <span class="cc-field__hint">At least 10 characters. This reason is saved in the permanent activity history.</span>
          </label>
        </div>
        <footer class="cc-modal__foot">
          <span class="cc-field__hint">Confirm your identity with a passkey again before saving.</span>
          <span class="cc-modal__actions">
            ${actionButton({ label: 'Cancel', action: 'close-modal', disabled: dialog.busy })}
            ${actionButton({
              label: dialog.busy ? 'Saving…' : resolving ? 'Mark as resolved' : updating ? 'Save update' : 'Record service problem',
              type: 'submit',
              tone: 'primary',
              iconName: dialog.busy ? '' : resolving ? 'check' : 'incidents',
              disabled: dialog.busy,
              testId: 'control-centre-incident-submit',
            })}
          </span>
        </footer>
      </form>
    </section>
  </div>`;
}

async function submitIncident(state, form = null) {
  const dialog = state.incidentDialog;
  if (!dialog || dialog.busy) return;
  const resolving = dialog.mode === 'resolve';
  const detailsReady = resolving
    ? cleanText(dialog.recovery).length >= 10
    : cleanText(dialog.title).length >= 5 && cleanText(dialog.detectionSource).length >= 3;
  if (cleanText(dialog.reason).length < 10 || !detailsReady) {
    dialog.validationError = true;
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Check the highlighted service problem information.');
    return;
  }
  dialog.busy = true;
  dialog.error = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    `incident-${dialog.mode}`,
    dialog.mode === 'resolve' ? 'Resolving…' : dialog.mode === 'update' ? 'Saving…' : 'Recording…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  const payload = {
    title: dialog.title,
    severity: dialog.severity,
    state: dialog.state,
    detectionSource: dialog.detectionSource,
    containment: dialog.containment,
    recovery: dialog.recovery,
    followUp: dialog.followUp,
    communicationState: dialog.communicationState,
    securityOrPrivacy: dialog.securityOrPrivacy,
    affectedTenantIds: dialog.affectedTenantIds,
    reason: dialog.reason,
    ...(dialog.mode === 'create' ? {} : { expectedVersion: dialog.expectedVersion }),
  };
  try {
    const response = dialog.mode === 'create'
      ? await createIncident(state.ctx, payload, dialog.idempotencyKey)
      : resolving
        ? await resolveIncident(
            state.ctx,
            dialog.incidentId,
            payload,
            dialog.idempotencyKey
          )
        : await updateIncident(
            state.ctx,
            dialog.incidentId,
            payload,
            dialog.idempotencyKey
          );
    const normalized = normalizeSectionPayload(response);
    state.incidentDialog = null;
    state.receipt = {
      title: resolving ? 'Service problem resolved' : dialog.mode === 'create' ? 'Service problem recorded' : 'Service problem updated',
      detail: 'TToria saved the service problem and its customer update status. No email was sent automatically.',
      operationId: cleanText(normalized.mutationReference),
      status: cleanText(normalized.incident?.state, 'completed'),
      nextStep: resolving
        ? 'The resolved service problem remains in the Service Problem Centre and permanent activity history.'
        : 'Continue updating what is happening now, recovery, and communication as facts change.',
      expectedAt: '',
    };
    state.sectionData.delete('incidents');
    state.sectionData.delete('today');
    render(state);
    announce(state, state.receipt.title);
  } catch (error) {
    submissionFailed = true;
    dialog.busy = false;
    dialog.error = surfaceErrorMessage(error);
    render(state, { focus: '[role="alert"]' });
    announce(state, dialog.error);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

function normalizeReceipt(payload = {}, fallbackTitle = 'Request accepted') {
  const data = normalizeSectionPayload(payload);
  const operation = objectValue(data.operation || data.receipt || data);
  return {
    title: firstValue(data, ['title'], fallbackTitle),
    detail: firstValue(data, ['message', 'detail', 'summary'], 'TToria recorded the request and will keep its progress visible.'),
    operationId: firstValue(
      data,
      ['operationReference'],
      firstValue(operation, ['operationId', 'id', 'reference'], '')
    ),
    status: firstValue(operation, ['status', 'state'], firstValue(data, ['status'], 'accepted')),
    nextStep: plainSystemMessage(firstValue(data, ['nextStep'], firstValue(operation, ['nextStep'], 'Open Changes & Progress to follow each service confirmation.'))),
    expectedAt: firstValue(
      operation,
      ['cooldownEndsAt', 'expectedAt', 'estimatedCompletionAt'],
      firstValue(data, ['expectedAt'], '')
    ),
  };
}

function renderReceiptModal(state) {
  const receipt = state.receipt;
  return `<div class="cc-modal-backdrop" data-cc-modal-backdrop>
    <section
      class="cc-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-receipt-title"
      data-testid="control-centre-operation-receipt"
      data-cc-modal
    >
      <header class="cc-modal__head">
        <div><h2 id="cc-receipt-title">Change receipt</h2><p>Keep this tracking reference if you need support.</p></div>
        ${modalCloseButton(false)}
      </header>
      <div class="cc-modal__body">
        <div class="cc-receipt">
          <span class="cc-receipt__icon">${icon('check')}</span>
          <h3>${escapeHtml(receipt.title)}</h3>
          <p>${escapeHtml(receipt.detail)}</p>
          ${statusBadge(plainStatus(receipt.status, 'operation'), receipt.status)}
          ${receipt.operationId ? `<span class="cc-code" data-testid="control-centre-operation-reference">${escapeHtml(receipt.operationId)}</span>` : ''}
        </div>
        <dl class="cc-review cc-section">
          <div class="cc-review__row"><dt>What happens next</dt><dd>${escapeHtml(receipt.nextStep)}</dd></div>
          ${receipt.expectedAt ? `<div class="cc-review__row"><dt>Expected update</dt><dd>${escapeHtml(formatDateTime(receipt.expectedAt))}</dd></div>` : ''}
          <div class="cc-review__row"><dt>Where to follow it</dt><dd>Changes & Progress and the tutor account’s Change History</dd></div>
        </dl>
      </div>
      <footer class="cc-modal__foot">
        <span class="cc-field__hint">The receipt does not claim unfinished work has completed.</span>
        <span class="cc-modal__actions">
          ${actionButton({ label: 'Close', action: 'close-modal' })}
          ${actionButton({ label: 'View progress', action: 'receipt-operations', tone: 'primary', iconName: 'operations' })}
        </span>
      </footer>
    </section>
  </div>`;
}

function closeAllModals(state) {
  state.wizard = null;
  state.actionDialog = null;
  state.policyDialog = null;
  state.capacityDialog = null;
  state.retryDialog = null;
  state.incidentDialog = null;
  state.ownerSecurityDialog = null;
  state.receipt = null;
}

function render(state, { focus = '' } = {}) {
  if (state.destroyed) return;
  const root = state.root;
  const activeGuideField = root.ownerDocument.activeElement;
  const retainedGuideFocus = activeGuideField instanceof HTMLElement && root.contains(activeGuideField)
    && (activeGuideField.hasAttribute('data-cc-guide-key') || activeGuideField.hasAttribute('data-cc-guide-field'))
    ? { attribute: activeGuideField.hasAttribute('data-cc-guide-key') ? 'data-cc-guide-key' : 'data-cc-guide-field',
      value: activeGuideField.getAttribute(activeGuideField.hasAttribute('data-cc-guide-key') ? 'data-cc-guide-key' : 'data-cc-guide-field'),
      start: activeGuideField.selectionStart, end: activeGuideField.selectionEnd, scrollTop: activeGuideField.scrollTop } : null;
  const retainedGuideScroll = state.guideContentEditor?.opened ? ['.cc-main', '.cc-guide__groups', '.cc-guide__fields'].map(selector => ({ selector, top: root.querySelector(selector)?.scrollTop || 0 })) : [];
  root.classList.add('ttoria-control-centre');
  root.setAttribute('data-testid', 'control-centre-root');
  root.dataset.manageShellChrome = 'true';
  root.dataset.asyncFeedbackEnabled = state.asyncFeedbackEnabled ? 'true' : 'false';
  root.dataset.menuOpen = state.menuOpen ? 'true' : 'false';
  root.innerHTML = renderAppShell(state);
  state.selectControls?.refresh();
  state.scrollIndicators?.refresh();
  const hasModal = Boolean(
    state.wizard
    || state.actionDialog
    || state.policyDialog
    || state.capacityDialog
    || state.retryDialog
    || state.incidentDialog
    || state.ownerSecurityDialog
    || state.receipt
  );
  root.ownerDocument.body?.classList.toggle('cc-body-locked', hasModal || state.menuOpen);
  root.dataset.menuOpen = state.menuOpen ? 'true' : 'false';
  const appElement = root.querySelector('.cc-app');
  const mainElement = root.querySelector('.cc-main');
  if (appElement instanceof HTMLElement) appElement.inert = hasModal;
  if (mainElement instanceof HTMLElement) mainElement.inert = !hasModal && state.menuOpen;
  const focusSelector = focus
    || (state.focusPageHeading ? '[data-cc-page-heading]' : '')
    || (hasModal ? '[data-cc-modal] input:not([disabled]), [data-cc-modal] select:not([disabled]), [data-cc-modal] textarea:not([disabled]), [data-cc-modal] button:not([disabled])' : '');
  state.focusPageHeading = false;
  for (const saved of retainedGuideScroll) { const element = root.querySelector(saved.selector); if (element) element.scrollTop = saved.top; }
  if (!focusSelector && retainedGuideFocus) {
    const replacement = [...root.querySelectorAll(`[${retainedGuideFocus.attribute}]`)].find(element => element.getAttribute(retainedGuideFocus.attribute) === retainedGuideFocus.value);
    if (replacement && !replacement.disabled) {
      focusControlCentreSelect(replacement, { preventScroll: true });
      if (retainedGuideFocus.start != null && typeof replacement.setSelectionRange === 'function') replacement.setSelectionRange(retainedGuideFocus.start, retainedGuideFocus.end);
      replacement.scrollTop = retainedGuideFocus.scrollTop;
    }
  }
  if (focusSelector) {
    state.root.ownerDocument.defaultView.requestAnimationFrame(() => {
      const target = root.querySelector(focusSelector);
      if (target instanceof HTMLElement) {
        try {
          focusControlCentreSelect(target, { preventScroll: true });
        } catch {
          target.focus();
        }
      }
    });
  }
}

async function loadSection(state, sectionId = state.activeSection, {
  focusHeading = false,
  silent = false,
  control = null,
} = {}) {
  if (state.destroyed) return;
  if (state.loadController) state.loadController.abort();
  const controller = new AbortController();
  const hasUsableContent = state.sectionData.has(sectionId);
  const pendingPresentation = !silent && control instanceof HTMLElement
    ? beginControlCentrePendingPresentation(
        state,
        control,
        { controlId: `section-${sectionId}`, busyLabel: 'Refreshing…' }
      )
    : emptyControlCentrePendingPresentation();
  let loadFailed = false;
  state.loadController = controller;
  state.loadingSection = sectionId;
  state.sectionError = null;
  if (!silent && (!state.asyncFeedbackEnabled || !hasUsableContent)) render(state);
  try {
    const query = sectionId === 'accounts'
      ? {
          q: state.accountQuery,
          status: state.accountStatus,
          plan: state.accountPlan,
          from: state.reportingFrom,
          to: state.reportingTo,
        }
      : sectionId === 'usage'
        ? {
            from: state.reportingFrom,
            to: state.reportingTo,
          }
        : {};
    const raw = await loadControlCentreSection(state.ctx, sectionId, {
      signal: controller.signal,
      query,
      silent,
    });
    if (controller.signal.aborted || state.destroyed) return;
    const payload = normalizeSectionPayload(raw);
    const meta = objectValue(raw?.meta || payload.meta);
    state.sectionData.set(sectionId, payload);
    state.sectionMeta.set(sectionId, {
      ...meta,
      stale: meta.stale === true || payload.stale === true,
      updatedAt: firstValue(meta, ['updatedAt', 'freshAt', 'generatedAt'], firstValue(payload, ['updatedAt'], new Date().toISOString())),
    });
    const accessMode = cleanText(firstValue(payload, ['accessMode', 'controlAccessMode'], '')).toLowerCase();
    state.readOnlyReason = accessMode === 'read_only'
      ? cleanText(firstValue(payload, ['readOnlyReason'], 'This session can review information but cannot request changes.'))
      : '';
    state.canMutate = deriveCanMutate(state.ctx)
      && state.ownerSession?.elevated === true
      && !state.readOnlyReason
      && !activeSectionIsStale(state);
    state.loadingSection = '';
    state.sectionError = null;
    if (focusHeading) state.focusPageHeading = true;
    render(state);
    announce(state, `${currentNavItem(state).label} updated.`);
  } catch (error) {
    if (controller.signal.aborted || state.destroyed || error?.name === 'AbortError') return;
    loadFailed = true;
    state.loadingSection = '';
    state.sectionError = error;
    render(state);
    announce(state, surfaceErrorMessage(error));
  } finally {
    pendingPresentation.restore();
    if (loadFailed) pendingPresentation.focusReplacement();
    if (state.loadController === controller) state.loadController = null;
  }
}

async function refreshOwnerSession(state, {
  renderAfter = true,
  announceError = false,
  silent = false,
} = {}) {
  if (state.destroyed) return null;
  try {
    const raw = await loadOwnerSession(state.ctx, { silent });
    if (state.destroyed) return null;
    const session = normalizeSectionPayload(raw);
    state.ownerSession = {
      ...state.ownerSession,
      ...session,
      passkeyCount: safeNumber(firstValue(session, ['passkeyCount'], state.ownerSession?.passkeyCount), 0),
      elevated: session.elevated === true,
      totpFallbackConfigured: session.totpFallbackConfigured === true,
    };
    state.ownerSessionError = '';
    if (cleanText(session.csrfToken)) {
      state.ctx = {
        ...state.ctx,
        auth: {
          ...objectValue(state.ctx?.auth),
          csrfToken: cleanText(session.csrfToken),
          elevated: session.elevated === true,
          elevatedUntil: firstValue(session, ['elevatedUntil'], ''),
          passkeyCount: safeNumber(session.passkeyCount, 0),
          passkeyReadiness: firstValue(session, ['passkeyReadiness'], ''),
          totpFallbackConfigured: session.totpFallbackConfigured === true,
        },
      };
    }
    state.canMutate = deriveCanMutate(state.ctx)
      && state.ownerSession.elevated === true
      && !state.readOnlyReason
      && !activeSectionIsStale(state);
    if (renderAfter) render(state);
    return state.ownerSession;
  } catch (error) {
    if (state.destroyed || error?.name === 'AbortError') return null;
    state.ownerSessionError = surfaceErrorMessage(error);
    state.canMutate = false;
    if (renderAfter) render(state);
    if (announceError) announce(state, state.ownerSessionError);
    return null;
  }
}

async function navigateTo(state, sectionId = 'today') {
  if (!NAV_ITEMS.some((item) => item.id === sectionId)) return;
  if (state.loadController) state.loadController.abort();
  if (state.accountController) state.accountController.abort();
  state.activeSection = sectionId;
  state.selectedAccountId = '';
  state.accountDetails = null;
  state.accountError = null;
  state.menuOpen = false;
  state.sectionError = null;
  state.focusPageHeading = true;
  rememberRoute(state);
  render(state);
  await loadSection(state, sectionId, { focusHeading: true });
}

async function openAccount(state, id = '', {
  control = null,
  silent = false,
} = {}) {
  const cleanId = cleanText(id);
  if (!cleanId) return;
  if (state.accountController) state.accountController.abort();
  const controller = new AbortController();
  const retainUsableContent = state.asyncFeedbackEnabled
    && state.selectedAccountId === cleanId
    && Boolean(state.accountDetails);
  const pendingPresentation = !silent && control instanceof HTMLElement
    ? beginControlCentrePendingPresentation(
        state,
        control,
        { controlId: `account-${cleanId}`, busyLabel: 'Refreshing…' }
      )
    : emptyControlCentrePendingPresentation();
  let loadFailed = false;
  state.accountController = controller;
  state.selectedAccountId = cleanId;
  if (!retainUsableContent) state.accountDetails = null;
  state.accountError = null;
  state.accountLoading = true;
  if (!retainUsableContent) state.accountTab = 'overview';
  if (!state.asyncFeedbackEnabled || !retainUsableContent) render(state);
  try {
    const payload = await loadTutorAccount(state.ctx, cleanId, {
      signal: controller.signal,
      query: {
        from: state.reportingFrom,
        to: state.reportingTo,
      },
      silent,
    });
    if (controller.signal.aborted || state.destroyed) return;
    state.accountDetails = normalizeSectionPayload(payload);
    state.accountLoading = false;
    state.accountError = null;
    state.focusPageHeading = true;
    render(state);
    announce(state, `${accountDisplayName(state.accountDetails)} opened.`);
  } catch (error) {
    if (controller.signal.aborted || state.destroyed || error?.name === 'AbortError') return;
    loadFailed = true;
    state.accountLoading = false;
    state.accountError = error;
    render(state);
    announce(state, surfaceErrorMessage(error));
  } finally {
    pendingPresentation.restore();
    if (loadFailed) pendingPresentation.focusReplacement();
    if (state.accountController === controller) state.accountController = null;
  }
}

function validateEmail(value = '') {
  const email = cleanText(value).toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validateWizardStep(state, stepIndex = state.wizard?.step || 0) {
  const wizard = state.wizard;
  if (!wizard) return false;
  const stepId = WIZARD_STEPS[stepIndex]?.id || 'business';
  const values = wizard.values;
  const errors = {};
  if (stepId === 'business') {
    if (cleanText(values.businessName).length < 2) errors.businessName = 'Enter the tutor or business name.';
  }
  if (stepId === 'owner') {
    if (!validateEmail(values.ownerEmail)) errors.ownerEmail = 'Enter a valid email address.';
  }
  if (stepId === 'plan') {
    if (!['trial', 'plan', 'none'].includes(values.accessChoice)) {
      errors.accessChoice = 'Choose how the account should start.';
    }
    if (values.accessChoice === 'trial') {
      const days = safeNumber(values.trialDays, 0);
      if (days < 1 || days > 60) errors.trialDays = 'Choose between 1 and 60 days.';
    }
    if (values.accessChoice === 'plan' && !cleanText(values.planId)) {
      errors.planId = 'Choose an approved plan or select a different starting option.';
    }
  }
  if (stepId === 'effective') {
    const date = new Date(`${cleanText(values.effectiveDate)}T12:00:00`);
    if (!values.effectiveDate || Number.isNaN(date.getTime())) errors.effectiveDate = 'Choose a valid start date.';
    if (cleanText(values.reason).length < 10) {
      errors.reason = 'Enter at least 10 characters explaining why you are creating this account.';
    }
  }
  wizard.errors = errors;
  return Object.keys(errors).length === 0;
}

function validateWholeWizard(state) {
  for (let index = 0; index < WIZARD_STEPS.length - 1; index += 1) {
    if (!validateWizardStep(state, index)) {
      state.wizard.step = index;
      return false;
    }
  }
  return true;
}

function nextWizardStep(state) {
  if (!state.wizard || state.wizard.busy) return;
  if (!validateWizardStep(state)) {
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Check the highlighted answer.');
    return;
  }
  state.wizard.step = Math.min(WIZARD_STEPS.length - 1, state.wizard.step + 1);
  state.wizard.errors = {};
  render(state);
}

function previousWizardStep(state) {
  if (!state.wizard || state.wizard.busy) return;
  state.wizard.step = Math.max(0, state.wizard.step - 1);
  state.wizard.errors = {};
  state.wizard.submitError = '';
  render(state);
}

function wizardPayload(wizard) {
  const values = wizard.values;
  return {
    businessName: cleanText(values.businessName),
    ownerEmail: cleanText(values.ownerEmail).toLowerCase(),
    planId: values.accessChoice === 'plan' ? cleanText(values.planId) : null,
    trial: values.accessChoice === 'trial',
    trialDays: values.accessChoice === 'trial' ? safeNumber(values.trialDays, 14) : null,
    complimentary: false,
    features: [...new Set(asArray(values.features).map(cleanText).filter(Boolean))],
    effectiveDate: cleanText(values.effectiveDate),
    reason: cleanText(values.reason),
  };
}

async function submitWizard(state, form = null) {
  const wizard = state.wizard;
  if (!wizard || wizard.busy) return;
  if (!validateWholeWizard(state)) {
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Check the highlighted answer.');
    return;
  }
  wizard.busy = true;
  wizard.submitError = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    'account-wizard',
    'Creating…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  try {
    const response = await createTutorAccount(state.ctx, wizardPayload(wizard), wizard.idempotencyKey);
    state.wizard = null;
    state.receipt = normalizeReceipt(response, 'Tutor account creation started');
    state.sectionData.delete('accounts');
    state.sectionData.delete('today');
    render(state);
    announce(state, 'Tutor account request accepted.');
  } catch (error) {
    submissionFailed = true;
    wizard.busy = false;
    wizard.submitError = surfaceErrorMessage(error);
    render(state, { focus: '[data-testid="control-centre-wizard-error"]' });
    announce(state, wizard.submitError);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

function openAccountAction(state, actionId = '', supportCaseId = '') {
  if (!state.canMutate || !ACCOUNT_ACTIONS[actionId]) return;
  state.actionDialog = {
    actionId,
    supportCaseId: cleanText(supportCaseId),
    reason: '',
    typedConfirmation: '',
    planId: '',
    trialEndsAt: futureDateInput(14),
    endsAt: futureDateInput(30),
    busy: false,
    error: '',
    validationError: false,
    idempotencyKey: createId(`account-${actionId}`),
  };
  render(state);
}

async function submitAccountAction(state, form = null) {
  const dialog = state.actionDialog;
  if (!dialog || dialog.busy) return;
  const config = ACCOUNT_ACTIONS[dialog.actionId];
  const account = normalizeSectionPayload(state.accountDetails);
  const name = accountDisplayName(account);
  const reasonReady = !config.requiresReason || cleanText(dialog.reason).length >= 10;
  const typedReady = !config.requiresTypedName || cleanText(dialog.typedConfirmation) === name;
  const planReady = !config.requiresPlan || Boolean(cleanText(dialog.planId));
  const trialEndReady = !config.requiresTrialEnd
    || new Date(endOfDateIso(dialog.trialEndsAt)).getTime() > Date.now();
  const complimentaryEndReady = !config.requiresComplimentaryEnd
    || new Date(endOfDateIso(dialog.endsAt)).getTime() > Date.now();
  if (!reasonReady || !typedReady || !planReady || !trialEndReady || !complimentaryEndReady) {
    dialog.validationError = true;
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Check the highlighted confirmation information.');
    return;
  }
  dialog.busy = true;
  dialog.error = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    `account-action-${dialog.actionId}`,
    'Submitting…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  try {
    const id = accountId(account) || state.selectedAccountId;
    const hostedBillingAction = dialog.actionId === 'stripe-checkout'
      || dialog.actionId === 'stripe-portal';
    const supportAcknowledgement = dialog.actionId === 'ack-support-case';
    const response = supportAcknowledgement
      ? await acknowledgeTutorSupportCase(
          state.ctx,
          id,
          dialog.supportCaseId,
          cleanText(dialog.reason),
          dialog.idempotencyKey
        )
      : hostedBillingAction
      ? await (
          dialog.actionId === 'stripe-checkout'
            ? createTutorAccountCheckout(
                state.ctx,
                id,
                cleanText(dialog.reason),
                dialog.idempotencyKey
              )
            : createTutorAccountBillingPortal(
                state.ctx,
                id,
                cleanText(dialog.reason),
                dialog.idempotencyKey
              )
        )
      : await performTutorAccountAction(
          state.ctx,
          id,
          dialog.actionId,
          {
            reason: cleanText(dialog.reason),
            typedConfirmation: cleanText(dialog.typedConfirmation),
            ...(config.requiresPlan ? { planId: cleanText(dialog.planId) } : {}),
            ...(config.requiresTrialEnd ? { trialEndsAt: endOfDateIso(dialog.trialEndsAt) } : {}),
            ...(config.requiresComplimentaryEnd ? { endsAt: endOfDateIso(dialog.endsAt) } : {}),
          },
          dialog.idempotencyKey
        );
    if (hostedBillingAction) {
      const hostedUrl = new URL(cleanText(firstValue(normalizeSectionPayload(response), ['url'])));
      const allowedHost = dialog.actionId === 'stripe-checkout'
        ? 'checkout.stripe.com'
        : 'billing.stripe.com';
      if (hostedUrl.protocol !== 'https:' || hostedUrl.hostname !== allowedHost) {
        throw new Error('Stripe did not return a safe hosted billing link.');
      }
      window.location.assign(hostedUrl.toString());
      return;
    }
    state.actionDialog = null;
    state.receipt = normalizeReceipt(response, `${config.label} requested`);
    state.sectionData.delete('today');
    state.sectionData.delete('operations');
    render(state);
    announce(state, `${config.label} request accepted.`);
  } catch (error) {
    submissionFailed = true;
    dialog.busy = false;
    dialog.error = surfaceErrorMessage(error);
    render(state);
    announce(state, dialog.error);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

function policyPayload(dialog) {
  return {
    overrides: Object.values(dialog.features)
      .filter((feature) =>
        feature.protected !== true
        && (
          feature.changed === true
          || ['grant', 'deny'].includes(feature.existingOverrideDecision)
        )
      )
      .map((feature) => ({
        key: feature.id,
        decision: feature.changed === true
          ? feature.enabled === true ? 'grant' : 'deny'
          : feature.existingOverrideDecision,
        expiresAt: feature.changed === true
          ? cleanText(dialog.expiresAt) || null
          : cleanText(feature.existingOverrideExpiresAt) || null,
      })),
    reason: cleanText(dialog.reason),
  };
}

async function submitPolicy(state, form = null) {
  const dialog = state.policyDialog;
  if (!dialog || dialog.busy) return;
  if (
    cleanText(dialog.reason).length < 10
    || !Object.values(dialog.features).some((feature) => feature.changed === true)
  ) {
    dialog.validationError = true;
    dialog.preview = null;
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Change at least one optional feature and enter a clear reason before previewing.');
    return;
  }
  const account = normalizeSectionPayload(state.accountDetails);
  const id = accountId(account) || state.selectedAccountId;
  if (!id) {
    dialog.error = 'The tutor account could not be identified.';
    render(state);
    announce(state, dialog.error);
    return;
  }
  dialog.busy = true;
  dialog.error = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    dialog.preview ? 'policy-save' : 'policy-preview',
    dialog.preview ? 'Saving…' : 'Previewing…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  try {
    if (!dialog.preview) {
      const response = await simulateTutorAccountPolicy(state.ctx, id, policyPayload(dialog));
      dialog.preview = normalizeSectionPayload(response);
      dialog.busy = false;
      render(state, { focus: '[data-testid="control-centre-policy-preview"]' });
      announce(state, 'Feature impact preview ready. Nothing has changed yet.');
      return;
    }
    const response = await saveTutorAccountPolicy(
      state.ctx,
      id,
      {
        ...policyPayload(dialog),
        expectedVersion: firstValue(dialog.preview, ['expectedVersion', 'version'], null),
        previewReference: firstValue(dialog.preview, ['previewReference', 'id'], null),
      },
      dialog.idempotencyKey
    );
    state.policyDialog = null;
    state.receipt = normalizeReceipt(response, 'Feature change requested');
    state.sectionData.delete('today');
    render(state);
    announce(state, 'Feature change request accepted.');
  } catch (error) {
    submissionFailed = true;
    dialog.busy = false;
    dialog.error = surfaceErrorMessage(error);
    render(state);
    announce(state, dialog.error);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

async function submitCapacity(state, form = null) {
  const dialog = state.capacityDialog;
  if (!dialog || dialog.busy) return;
  const limitReady = dialog.mode !== 'custom'
    || (
      Number.isInteger(Number(dialog.maxActiveStudents))
      && Number(dialog.maxActiveStudents) >= 0
      && Number(dialog.maxActiveStudents) <= 100000
    );
  if (cleanText(dialog.reason).length < 10 || !limitReady) {
    dialog.validationError = true;
    dialog.preview = null;
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Check the highlighted allowance information.');
    return;
  }
  const isPlan = dialog.scope === 'plan';
  const account = normalizeSectionPayload(state.accountDetails);
  const id = isPlan
    ? cleanText(dialog.targetId)
    : accountId(account) || state.selectedAccountId;
  if (!id) {
    dialog.error = isPlan ? 'The selected plan could not be identified.' : 'The tutor account could not be identified.';
    render(state);
    announce(state, dialog.error);
    return;
  }
  dialog.busy = true;
  dialog.error = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    dialog.preview ? 'capacity-save' : 'capacity-preview',
    dialog.preview ? 'Saving…' : 'Previewing…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  try {
    if (!dialog.preview) {
      const response = isPlan
        ? await simulatePlanCapacity(state.ctx, id, capacityPayload(dialog))
        : await simulateTutorAccountCapacity(state.ctx, id, capacityPayload(dialog));
      dialog.preview = normalizeSectionPayload(response);
      dialog.busy = false;
      render(state, { focus: '[data-testid="control-centre-capacity-preview"]' });
      announce(state, 'Active student limit preview ready. Nothing has changed yet.');
      return;
    }
    const reviewedPayload = {
      ...capacityPayload(dialog),
      expectedVersion: firstValue(dialog.preview, ['expectedVersion', 'currentVersion'], null),
      previewReference: firstValue(dialog.preview, ['previewReference'], null),
    };
    const response = isPlan
      ? await savePlanCapacity(state.ctx, id, reviewedPayload, dialog.idempotencyKey)
      : await saveTutorAccountCapacity(state.ctx, id, reviewedPayload, dialog.idempotencyKey);
    state.capacityDialog = null;
    state.receipt = normalizeReceipt(
      response,
      isPlan ? 'Plan active student limit change requested' : 'Active student limit change requested'
    );
    state.sectionData.delete('today');
    state.sectionData.delete('operations');
    if (isPlan) state.sectionData.delete('settings');
    render(state);
    announce(state, `${isPlan ? 'Plan active student limit' : 'Active student limit'} request accepted.`);
  } catch (error) {
    submissionFailed = true;
    dialog.busy = false;
    dialog.error = surfaceErrorMessage(error);
    render(state);
    announce(state, dialog.error);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

function openOperationRetry(state, operationId = '') {
  const id = cleanText(operationId);
  if (!id || !state.canMutate) return;
  state.retryDialog = {
    operationId: id,
    reason: '',
    validationError: false,
    busy: false,
    error: '',
    idempotencyKey: createId(`retry-${id}`),
  };
  render(state);
}

async function submitOperationRetry(state, form = null) {
  const dialog = state.retryDialog;
  if (!dialog || dialog.busy) return;
  if (cleanText(dialog.reason).length < 10) {
    dialog.validationError = true;
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Enter a clear reason before trying the change again.');
    return;
  }
  dialog.busy = true;
  dialog.error = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    'operation-retry',
    'Trying again…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  try {
    const response = await retryOperation(
      state.ctx,
      dialog.operationId,
      dialog.idempotencyKey,
      cleanText(dialog.reason)
    );
    state.retryDialog = null;
    state.receipt = normalizeReceipt(response, 'The change will be tried again');
    state.sectionData.delete('operations');
    render(state);
    announce(state, 'The change will be tried again.');
  } catch (error) {
    submissionFailed = true;
    dialog.busy = false;
    dialog.error = surfaceErrorMessage(error);
    render(state);
    announce(state, dialog.error);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

function ownerSecurityErrorMessage(error) {
  if (['AbortError', 'NotAllowedError'].includes(cleanText(error?.name))) {
    return 'The secure passkey prompt was cancelled. Nothing changed; you can try again when ready.';
  }
  if (cleanText(error?.code).toUpperCase() === 'PASSKEY_BROWSER_UNAVAILABLE') {
    return cleanText(error?.message, 'This browser cannot use passkeys.');
  }
  return surfaceErrorMessage(error);
}

function updateOwnerSecurityField(state, target, field) {
  const dialog = state.ownerSecurityDialog;
  if (!dialog) return false;
  if (field === 'ownerPasskeyName') dialog.deviceName = String(target.value || '');
  if (field === 'ownerElevationReason') dialog.reason = String(target.value || '');
  dialog.validationError = false;
  dialog.error = '';
  return false;
}

async function submitOwnerPasskey(state, form = null) {
  const dialog = state.ownerSecurityDialog;
  if (!dialog || dialog.mode !== 'passkey' || dialog.busy) return;
  if (cleanText(dialog.deviceName).length < 3) {
    dialog.validationError = true;
    render(state, { focus: '[aria-invalid="true"]' });
    announce(state, 'Enter a clear name for this passkey.');
    return;
  }
  dialog.busy = true;
  dialog.error = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    'owner-passkey',
    'Preparing…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  try {
    const response = normalizeSectionPayload(
      await registerOwnerPasskey(state.ctx, cleanText(dialog.deviceName))
    );
    const passkeyCount = safeNumber(
      firstValue(response, ['passkeyCount'], safeNumber(state.ownerSession?.passkeyCount, 0) + 1),
      0
    );
    state.ownerSession = {
      ...state.ownerSession,
      passkeyCount,
      passkeyReadiness: passkeyCount >= 2 ? 'ready' : 'second_passkey_required',
    };
    dialog.passkeyCount = passkeyCount;
    dialog.busy = false;
    dialog.success = {
      title: 'Backup passkey added',
      detail: `${cleanText(firstValue(response, ['deviceName'], dialog.deviceName), 'The passkey')} is now registered for the platform owner.`,
      passkeyCount,
      nextStep: passkeyCount >= 2
        ? 'You can now confirm sensitive changes for a protected 15-minute window.'
        : 'Add one more separately protected passkey before sensitive changes can be confirmed.',
    };
    render(state, { focus: '[data-testid="control-centre-owner-security-success"] button' });
    announce(state, 'Backup owner passkey added.');
  } catch (error) {
    submissionFailed = true;
    dialog.busy = false;
    dialog.error = ownerSecurityErrorMessage(error);
    render(state);
    announce(state, dialog.error);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

async function submitOwnerElevation(state, form = null) {
  const dialog = state.ownerSecurityDialog;
  if (!dialog || dialog.mode !== 'elevation' || dialog.busy) return;
  const session = objectValue(state.ownerSession);
  const needsSecondPasskey = safeNumber(session.passkeyCount, 0) < 2
    || cleanText(session.passkeyReadiness) === 'second_passkey_required';
  const reasonReady = cleanText(dialog.reason).length >= 10;
  if (needsSecondPasskey || !reasonReady) {
    dialog.validationError = true;
    render(state, { focus: '[aria-invalid="true"]' });
    announce(
      state,
      needsSecondPasskey
        ? 'Add the required backup passkey first.'
        : 'Check the highlighted security information.'
    );
    return;
  }
  dialog.busy = true;
  dialog.error = '';
  const pendingPresentation = pendingSubmitPresentation(
    state,
    form,
    'owner-elevation',
    'Confirming…'
  );
  let submissionFailed = false;
  if (!state.asyncFeedbackEnabled) render(state);
  try {
    const response = normalizeSectionPayload(
      await elevateOwnerSessionWithPasskey(state.ctx, cleanText(dialog.reason))
    );
    const elevatedUntil = firstValue(response, ['elevatedUntil'], '');
    state.ownerSession = {
      ...state.ownerSession,
      elevated: response.elevated === true,
      elevatedUntil,
    };
    state.ctx = {
      ...state.ctx,
      auth: {
        ...objectValue(state.ctx?.auth),
        elevated: response.elevated === true,
        elevatedUntil,
        passkeyCount: safeNumber(state.ownerSession.passkeyCount, 0),
        passkeyReadiness: firstValue(state.ownerSession, ['passkeyReadiness'], ''),
        totpFallbackConfigured: state.ownerSession.totpFallbackConfigured === true,
      },
    };
    state.canMutate = deriveCanMutate(state.ctx)
      && response.elevated === true
      && !state.readOnlyReason;
    dialog.busy = false;
    dialog.success = {
      title: 'Sensitive changes confirmed',
      detail: 'Your passkey confirmed your identity.',
      elevatedUntil,
      nextStep: 'You can make protected, recorded changes until this confirmation expires. TToria then locks changes again.',
    };
    render(state, { focus: '[data-testid="control-centre-owner-security-success"] button' });
    announce(state, 'Sensitive changes confirmed for 15 minutes.');
  } catch (error) {
    submissionFailed = true;
    dialog.busy = false;
    dialog.error = ownerSecurityErrorMessage(error);
    render(state);
    announce(state, dialog.error);
  } finally {
    pendingPresentation.restore();
    if (submissionFailed) pendingPresentation.focusReplacement();
  }
}

function captureModalReturnFocus(state, target) {
  const element = target instanceof HTMLElement ? target : state.root.ownerDocument.activeElement;
  const testId = element instanceof HTMLElement ? cleanText(element.getAttribute('data-testid')) : '';
  state.modalReturnFocus = testId ? `[data-testid="${escapeSelectorValue(testId)}"]` : '';
}

function modalIsBusy(state) {
  return Boolean(
    state.wizard?.busy
    || state.actionDialog?.busy
    || state.policyDialog?.busy
    || state.capacityDialog?.busy
    || state.retryDialog?.busy
    || state.incidentDialog?.busy
    || state.ownerSecurityDialog?.busy
  );
}

function closeModal(state) {
  if (modalIsBusy(state)) return;
  const returnFocus = state.modalReturnFocus;
  closeAllModals(state);
  state.modalReturnFocus = null;
  render(state, { focus: returnFocus || '' });
}

function onRootClick(state, event) {
  if (state.destroyed) return;
  if (event.target instanceof HTMLElement && event.target.matches('[data-cc-modal-backdrop]')) {
    closeModal(state);
    return;
  }
  const trigger = event.target instanceof Element ? event.target.closest('[data-cc-action]') : null;
  if (!(trigger instanceof HTMLElement) || !state.root.contains(trigger)) return;
  const action = cleanText(trigger.dataset.ccAction);
  if (action === 'guide-elevate') {
    captureModalReturnFocus(state, trigger);
    closeAllModals(state);
    state.ownerSecurityDialog = createOwnerSecurityDialog(state, 'elevation');
    render(state, { focus: '[data-testid="control-centre-owner-elevation-reason"]' });
    return;
  }
  if (action.startsWith('guide-')) {
    void handleControlCentreGuideAction(state, action, trigger, render);
    return;
  }
  if (action === 'navigate') {
    const section = cleanText(trigger.dataset.section);
    void navigateTo(state, section);
    return;
  }
  if (action === 'open-menu') {
    state.menuOpen = true;
    render(state, { focus: '[data-testid="control-centre-nav-today"]' });
    return;
  }
  if (action === 'close-menu') {
    state.menuOpen = false;
    render(state, { focus: '.cc-menu-button' });
    return;
  }
  if (action === 'toggle-theme') {
    setTheme(state, state.theme === 'dark' ? 'light' : 'dark');
    render(state, { focus: '[data-testid="control-centre-theme-toggle"]' });
    announce(state, `${state.theme === 'dark' ? 'Dark' : 'Light'} mode is on.`);
    return;
  }
  if (action === 'logout') {
    const pendingPresentation = beginControlCentrePendingPresentation(state, trigger, {
      controlId: 'logout',
      busyLabel: 'Signing out…',
    });
    if (!state.asyncFeedbackEnabled) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.setAttribute('disabled', 'disabled');
    }
    announce(state, 'Signing out securely.');
    void (async () => {
      try {
        const fetchImpl = typeof state.ctx?.fetch === 'function'
          ? state.ctx.fetch
          : state.root.ownerDocument.defaultView.fetch.bind(state.root.ownerDocument.defaultView);
        await runControlCentreActivity(
          state.ctx,
          {
            action: CONTROL_CENTRE_ASYNC_ACTIONS.logout,
            opClass: 'user-mutation',
            summary: 'Signing out securely',
            successSummary: 'Signed out securely',
            errorSummary: 'Secure sign-out could not be confirmed',
          },
          null,
          async () => {
            const response = await fetchImpl('/auth/logout', {
              method: 'POST',
              credentials: 'same-origin',
              cache: 'no-store',
            });
            if (response && response.ok === false) {
              throw new Error('The secure sign-out request was not accepted.');
            }
            return response;
          }
        );
      } catch {
        // Navigation still follows so an expired or partially ended session can
        // return to the normal authentication boundary safely.
      } finally {
        pendingPresentation.restore();
        state.root.ownerDocument.defaultView.location.assign('/login');
      }
    })();
    return;
  }
  if (action === 'open-owner-passkey') {
    captureModalReturnFocus(state, trigger);
    closeAllModals(state);
    state.ownerSecurityDialog = createOwnerSecurityDialog(state, 'passkey');
    render(state, { focus: '[data-testid="control-centre-owner-passkey-name"]' });
    return;
  }
  if (action === 'open-owner-elevation') {
    captureModalReturnFocus(state, trigger);
    closeAllModals(state);
    state.ownerSecurityDialog = createOwnerSecurityDialog(state, 'elevation');
    render(state, {
      focus: safeNumber(state.ownerSession?.passkeyCount, 0) < 2
        ? '[data-cc-action="owner-security-passkey"]'
        : '[data-testid="control-centre-owner-elevation-reason"]',
    });
    return;
  }
  if (action === 'owner-security-passkey') {
    state.ownerSecurityDialog = createOwnerSecurityDialog(state, 'passkey');
    render(state, { focus: '[data-testid="control-centre-owner-passkey-name"]' });
    return;
  }
  if (action === 'refresh') {
    const pendingPresentation = beginControlCentrePendingPresentation(state, trigger, {
      controlId: 'refresh',
      busyLabel: '',
    });
    void (async () => {
      try {
        await refreshOwnerSession(state, { renderAfter: false, announceError: true });
        if (state.selectedAccountId) await openAccount(state, state.selectedAccountId);
        else await loadSection(state, state.activeSection);
      } finally {
        pendingPresentation.restore();
      }
    })();
    return;
  }
  if (action === 'open-wizard') {
    if (!state.canMutate) return;
    captureModalReturnFocus(state, trigger);
    state.wizard = createWizardState();
    state.actionDialog = null;
    state.policyDialog = null;
    state.capacityDialog = null;
    state.retryDialog = null;
    state.incidentDialog = null;
    state.receipt = null;
    render(state);
    return;
  }
  if (action === 'wizard-next') {
    nextWizardStep(state);
    return;
  }
  if (action === 'wizard-back') {
    previousWizardStep(state);
    return;
  }
  if (action === 'close-modal') {
    closeModal(state);
    return;
  }
  if (action === 'open-account') {
    void openAccount(state, trigger.dataset.accountId, { control: trigger });
    return;
  }
  if (action === 'close-account') {
    if (state.accountController) state.accountController.abort();
    state.selectedAccountId = '';
    state.accountDetails = null;
    state.accountError = null;
    state.accountTab = 'overview';
    state.focusPageHeading = true;
    render(state);
    return;
  }
  if (action === 'reload-account') {
    void openAccount(state, state.selectedAccountId, { control: trigger });
    return;
  }
  if (action === 'account-tab') {
    const tab = cleanText(trigger.dataset.tab);
    if (!ACCOUNT_TABS.some((item) => item.id === tab)) return;
    state.accountTab = tab;
    render(state, { focus: `[data-testid="control-centre-account-tab-${escapeSelectorValue(tab)}"]` });
    return;
  }
  if (action === 'open-account-action') {
    captureModalReturnFocus(state, trigger);
    openAccountAction(
      state,
      cleanText(trigger.dataset.accountAction),
      cleanText(trigger.dataset.supportCaseId)
    );
    return;
  }
  if (
    action === 'open-incident-create'
    || action === 'open-incident-update'
    || action === 'open-incident-resolve'
  ) {
    if (!state.canMutate) return;
    const mode = action === 'open-incident-create'
      ? 'create'
      : action === 'open-incident-resolve'
        ? 'resolve'
        : 'update';
    const incidentId = cleanText(trigger.dataset.incidentId);
    const incident = mode === 'create'
      ? null
      : firstArray(sectionData(state, 'incidents'), ['incidents', 'items'])
          .find((item) => cleanText(item?.id) === incidentId);
    if (mode !== 'create' && !incident) {
      announce(state, 'This service problem changed or is no longer available. Refresh and try again.');
      return;
    }
    captureModalReturnFocus(state, trigger);
    closeAllModals(state);
    state.incidentDialog = createIncidentDialog(state, mode, incident);
    render(state, {
      focus: mode === 'resolve'
        ? '[name="incidentRecovery"]'
        : '[name="incidentTitle"]',
    });
    return;
  }
  if (action === 'open-policy') {
    if (!state.canMutate) return;
    captureModalReturnFocus(state, trigger);
    state.policyDialog = createPolicyDialog(normalizeSectionPayload(state.accountDetails));
    render(state);
    return;
  }
  if (action === 'open-capacity') {
    if (!state.canMutate) return;
    captureModalReturnFocus(state, trigger);
    state.capacityDialog = createCapacityDialog(normalizeSectionPayload(state.accountDetails));
    render(state, { focus: '[name="capacityMode"]:checked' });
    return;
  }
  if (action === 'open-plan-capacity') {
    if (!state.canMutate) return;
    const planId = cleanText(trigger.dataset.planId);
    const plan = firstArray(sectionData(state, 'settings'), ['plans'])
      .find((item) => cleanText(item?.id) === planId);
    if (!plan) {
      announce(state, 'The selected plan is no longer available. Refresh Settings and try again.');
      return;
    }
    captureModalReturnFocus(state, trigger);
    state.capacityDialog = createCapacityDialog(plan, {
      scope: 'plan',
      targetId: planId,
      targetName: firstValue(plan, ['name'], planId),
    });
    render(state, { focus: '[name="capacityMode"]:checked' });
    return;
  }
  if (action === 'capacity-edit') {
    if (!state.capacityDialog) return;
    state.capacityDialog.preview = null;
    state.capacityDialog.error = '';
    render(state, { focus: '[name="capacityMode"]:checked' });
    return;
  }
  if (action === 'policy-edit') {
    if (!state.policyDialog) return;
    state.policyDialog.preview = null;
    state.policyDialog.error = '';
    render(state);
    return;
  }
  if (action === 'receipt-operations') {
    state.receipt = null;
    state.modalReturnFocus = null;
    void navigateTo(state, 'operations');
    return;
  }
  if (action === 'retry-operation') {
    captureModalReturnFocus(state, trigger);
    openOperationRetry(state, trigger.dataset.operationId);
    return;
  }
}

function updateWizardField(state, target, field) {
  const wizard = state.wizard;
  if (!wizard) return false;
  if (field === 'features' && target instanceof HTMLInputElement) {
    const next = new Set(asArray(wizard.values.features));
    if (target.checked) next.add(cleanText(target.value));
    else next.delete(cleanText(target.value));
    wizard.values.features = [...next];
    wizard.errors = {};
    return false;
  }
  if (Object.prototype.hasOwnProperty.call(wizard.values, field)) {
    wizard.values[field] = target instanceof HTMLInputElement && target.type === 'checkbox'
      ? target.checked
      : String(target.value || '');
    delete wizard.errors[field];
    wizard.submitError = '';
    return field === 'accessChoice';
  }
  return false;
}

function updateActionField(state, target, field) {
  const dialog = state.actionDialog;
  if (!dialog) return;
  const map = {
    actionReason: 'reason',
    typedConfirmation: 'typedConfirmation',
    actionPlanId: 'planId',
    actionTrialEndsAt: 'trialEndsAt',
    actionComplimentaryEndsAt: 'endsAt',
  };
  const key = map[field];
  if (!key) return;
  dialog[key] = String(target.value || '');
  dialog.validationError = false;
  dialog.error = '';
}

function updatePolicyField(state, target, field) {
  const dialog = state.policyDialog;
  if (!dialog) return false;
  if (field === 'policyFeature' && target instanceof HTMLInputElement) {
    const feature = dialog.features[cleanText(target.value)];
    if (feature && !feature.protected) {
      feature.enabled = target.checked;
      feature.changed = feature.enabled !== feature.originalEnabled;
    }
    dialog.preview = null;
    dialog.error = '';
    return false;
  }
  if (field === 'policyReason') dialog.reason = String(target.value || '');
  if (field === 'policyExpiresAt') dialog.expiresAt = String(target.value || '');
  dialog.preview = null;
  dialog.validationError = false;
  dialog.error = '';
  return false;
}

function updateCapacityField(state, target, field) {
  const dialog = state.capacityDialog;
  if (!dialog) return false;
  if (field === 'capacityMode') dialog.mode = String(target.value || '');
  if (field === 'capacityLimit') dialog.maxActiveStudents = String(target.value || '');
  if (field === 'capacityEnforcement') dialog.enforcement = String(target.value || '');
  if (field === 'capacityEffectiveAt') dialog.effectiveAt = String(target.value || '');
  if (field === 'capacityReason') dialog.reason = String(target.value || '');
  dialog.preview = null;
  dialog.validationError = false;
  dialog.error = '';
  return field === 'capacityMode';
}

function updateRetryField(state, target, field) {
  const dialog = state.retryDialog;
  if (!dialog || field !== 'retryReason') return;
  dialog.reason = String(target.value || '');
  dialog.validationError = false;
  dialog.error = '';
}

function updateIncidentField(state, target, field) {
  const dialog = state.incidentDialog;
  if (!dialog) return;
  if (field === 'incidentAffectedTenant' && target instanceof HTMLInputElement) {
    const next = new Set(dialog.affectedTenantIds);
    if (target.checked) next.add(cleanText(target.value));
    else next.delete(cleanText(target.value));
    dialog.affectedTenantIds = [...next];
  } else if (field === 'incidentSecurityOrPrivacy' && target instanceof HTMLInputElement) {
    dialog.securityOrPrivacy = target.checked;
  } else {
    const map = {
      incidentTitle: 'title',
      incidentSeverity: 'severity',
      incidentState: 'state',
      incidentDetectionSource: 'detectionSource',
      incidentContainment: 'containment',
      incidentRecovery: 'recovery',
      incidentFollowUp: 'followUp',
      incidentCommunicationState: 'communicationState',
      incidentReason: 'reason',
    };
    const key = map[field];
    if (key) dialog[key] = String(target.value || '');
  }
  dialog.validationError = false;
  dialog.error = '';
}

function onRootInput(state, event) {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return;
  if (handleControlCentreGuideInput(state, event, render)) return;
  const field = cleanText(target.dataset.ccField || target.name);
  if (!field) return;
  const wizardForm = target.closest('[data-cc-form="account-wizard"]');
  const actionForm = target.closest('[data-cc-form="account-action"]');
  const policyForm = target.closest('[data-cc-form="policy"]');
  const capacityForm = target.closest('[data-cc-form="capacity"]');
  const retryForm = target.closest('[data-cc-form="retry-operation"]');
  const incidentForm = target.closest('[data-cc-form="incident"]');
  const ownerPasskeyForm = target.closest('[data-cc-form="owner-passkey"]');
  const ownerElevationForm = target.closest('[data-cc-form="owner-elevation"]');
  let rerender = false;
  if (wizardForm) rerender = updateWizardField(state, target, field);
  else if (actionForm) updateActionField(state, target, field);
  else if (policyForm) rerender = updatePolicyField(state, target, field);
  else if (capacityForm) rerender = updateCapacityField(state, target, field);
  else if (retryForm) updateRetryField(state, target, field);
  else if (incidentForm) updateIncidentField(state, target, field);
  else if (ownerPasskeyForm || ownerElevationForm) {
    rerender = updateOwnerSecurityField(state, target, field);
  }
  else if (field === 'accountQuery') state.accountQuery = String(target.value || '');
  else if (field === 'accountStatus') state.accountStatus = String(target.value || '');
  else if (field === 'accountPlan') state.accountPlan = String(target.value || '');
  else if (field === 'reportingFrom') {
    state.reportingFrom = String(target.value || '');
    state.reportingRangeError = '';
  } else if (field === 'reportingTo') {
    state.reportingTo = String(target.value || '');
    state.reportingRangeError = '';
  }
  if (rerender && event.type === 'change') render(state);
}

function onRootSubmit(state, event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  const formId = cleanText(form.dataset.ccForm);
  if (!formId) return;
  event.preventDefault();
  const submitControl = form.querySelector('button[type="submit"], input[type="submit"]');
  if (formId === 'account-search') {
    state.reportingRangeError = reportingRangeError(state.reportingFrom, state.reportingTo);
    if (state.reportingRangeError) {
      render(state, { focus: '[aria-invalid="true"]' });
      announce(state, state.reportingRangeError);
      return;
    }
    void loadSection(state, 'accounts', { control: submitControl });
    return;
  }
  if (formId === 'reporting-range') {
    state.reportingRangeError = reportingRangeError(state.reportingFrom, state.reportingTo);
    if (state.reportingRangeError) {
      render(state, { focus: '[aria-invalid="true"]' });
      announce(state, state.reportingRangeError);
      return;
    }
    if (state.selectedAccountId) {
      void openAccount(state, state.selectedAccountId, { control: submitControl });
    } else {
      void loadSection(state, state.activeSection, { control: submitControl });
    }
    return;
  }
  if (formId === 'account-wizard') {
    if (state.wizard?.step === WIZARD_STEPS.length - 1) void submitWizard(state, form);
    else nextWizardStep(state);
    return;
  }
  if (formId === 'account-action') {
    void submitAccountAction(state, form);
    return;
  }
  if (formId === 'owner-passkey') {
    void submitOwnerPasskey(state, form);
    return;
  }
  if (formId === 'owner-elevation') {
    void submitOwnerElevation(state, form);
    return;
  }
  if (formId === 'policy') void submitPolicy(state, form);
  if (formId === 'capacity') void submitCapacity(state, form);
  if (formId === 'retry-operation') void submitOperationRetry(state, form);
  if (formId === 'incident') void submitIncident(state, form);
}

function focusableModalElements(modal) {
  return Array.from(modal.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter((element) => element instanceof HTMLElement && element.getAttribute('aria-hidden') !== 'true');
}

function onRootKeyDown(state, event) {
  if (event.key === 'Escape' && state.menuOpen) {
    event.preventDefault();
    state.menuOpen = false;
    render(state, { focus: '.cc-menu-button' });
    return;
  }
  if (
    event.key === 'Escape'
    && (
      state.wizard
      || state.actionDialog
      || state.policyDialog
      || state.capacityDialog
      || state.retryDialog
      || state.incidentDialog
      || state.ownerSecurityDialog
      || state.receipt
    )
  ) {
    if (!modalIsBusy(state)) {
      event.preventDefault();
      closeModal(state);
    }
    return;
  }
  const sidebar = state.root.querySelector('.cc-sidebar');
  if (event.key === 'Tab' && state.menuOpen && sidebar instanceof HTMLElement) {
    const focusable = focusableModalElements(sidebar);
    if (!focusable.length) {
      event.preventDefault();
      sidebar.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = state.root.ownerDocument.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
    return;
  }
  const modal = state.root.querySelector('[data-cc-modal]');
  if (event.key === 'Tab' && modal instanceof HTMLElement) {
    const focusable = focusableModalElements(modal);
    if (!focusable.length) {
      event.preventDefault();
      modal.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = state.root.ownerDocument.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
    return;
  }
  const navButton = event.target instanceof Element ? event.target.closest('.cc-nav__button') : null;
  if (navButton && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
    const buttons = Array.from(state.root.querySelectorAll('.cc-nav__button'));
    const index = buttons.indexOf(navButton);
    if (index < 0) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? buttons.length - 1
        : event.key === 'ArrowDown'
          ? (index + 1) % buttons.length
          : (index - 1 + buttons.length) % buttons.length;
    buttons[nextIndex]?.focus();
    return;
  }
  const tab = event.target instanceof Element ? event.target.closest('.cc-tab') : null;
  if (tab && ['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) {
    const tabs = Array.from(state.root.querySelectorAll('.cc-tab'));
    const index = tabs.indexOf(tab);
    if (index < 0) return;
    event.preventDefault();
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? tabs.length - 1
        : event.key === 'ArrowRight'
          ? (index + 1) % tabs.length
          : (index - 1 + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    tabs[nextIndex]?.click();
  }
}

function bindRoot(state) {
  const click = (event) => onRootClick(state, event);
  const input = (event) => onRootInput(state, event);
  const change = (event) => onRootInput(state, event);
  const submit = (event) => onRootSubmit(state, event);
  const keydown = (event) => onRootKeyDown(state, event);
  state.root.addEventListener('click', click);
  state.root.addEventListener('input', input);
  state.root.addEventListener('change', change);
  state.root.addEventListener('submit', submit);
  state.root.addEventListener('keydown', keydown);
  state.cleanup.push(
    () => state.root.removeEventListener('click', click),
    () => state.root.removeEventListener('input', input),
    () => state.root.removeEventListener('change', change),
    () => state.root.removeEventListener('submit', submit),
    () => state.root.removeEventListener('keydown', keydown),
  );
  const online = () => {
    announce(state, 'Connection restored. Refreshing this page.');
    void loadSection(state, state.activeSection, { silent: true });
  };
  const offline = () => {
    announce(state, 'You are offline. No changes can be submitted.');
  };
  state.root.ownerDocument.defaultView.addEventListener('online', online);
  state.root.ownerDocument.defaultView.addEventListener('offline', offline);
  state.cleanup.push(
    () => state.root.ownerDocument.defaultView.removeEventListener('online', online),
    () => state.root.ownerDocument.defaultView.removeEventListener('offline', offline),
  );
}

function startAutoRefresh(state) {
  const view = state.root.ownerDocument.defaultView;
  state.autoRefreshTimer = view.setInterval(() => {
    if (
      state.destroyed
      || state.wizard
      || state.actionDialog
      || state.policyDialog
      || state.capacityDialog
      || state.retryDialog
      || state.incidentDialog
      || state.ownerSecurityDialog
      || state.receipt
    ) return;
    if (state.root.ownerDocument.visibilityState !== 'visible') return;
    void (async () => {
      await refreshOwnerSession(state, { renderAfter: true, silent: true });
      if (['today', 'operations', 'incidents'].includes(state.activeSection)) {
        await loadSection(state, state.activeSection, { silent: true });
      }
    })();
  }, AUTO_REFRESH_MS);
  state.cleanup.push(() => {
    if (state.autoRefreshTimer) view.clearInterval(state.autoRefreshTimer);
    state.autoRefreshTimer = 0;
  });
}

export async function mount(ctx = {}) {
  const root = resolveRoot(ctx);
  if (!root) throw new Error('Control Centre mount root missing.');
  const existing = ROOT_STATE.get(root);
  if (existing) await unmount(ctx);
  ensureControlCentreStyles(root.ownerDocument || document);
  const state = createState(root, ctx);
  ROOT_STATE.set(root, state);
  setTheme(state, state.theme, { persist: false });
  bindRoot(state);
  state.selectControls = installControlCentreSelects(root);
  state.cleanup.push(() => state.selectControls.destroy());
  state.scrollIndicators = installScrollIndicators(root);
  state.cleanup.push(() => state.scrollIndicators.destroy());
  startAutoRefresh(state);
  render(state);
  await refreshOwnerSession(state, { renderAfter: true, silent: true });
  await loadSection(state, state.activeSection, { focusHeading: true });
}

export async function refresh(ctx = {}) {
  const root = resolveRoot(ctx);
  if (!root) return;
  const state = ROOT_STATE.get(root);
  if (!state) {
    await mount(ctx);
    return;
  }
  state.ctx = ctx;
  state.asyncFeedbackEnabled = isControlCentreAsyncFeedbackEnabled(ctx);
  await refreshOwnerSession(state, { renderAfter: false });
  if (state.selectedAccountId) {
    await openAccount(state, state.selectedAccountId);
    return;
  }
  await loadSection(state, state.activeSection);
}

export async function unmount(ctx = {}) {
  const root = resolveRoot(ctx);
  if (!root) return;
  const state = ROOT_STATE.get(root);
  if (state) {
    state.destroyed = true;
    state.loadController?.abort();
    state.accountController?.abort();
    state.guideContentEditor.controller?.abort();
    for (const cleanup of state.cleanup.splice(0)) {
      try {
        cleanup();
      } catch {}
    }
  }
  root.ownerDocument.body?.classList.remove('cc-body-locked');
  root.classList.remove('ttoria-control-centre');
  root.removeAttribute('data-testid');
  delete root.dataset.manageShellChrome;
  delete root.dataset.menuOpen;
  delete root.dataset.asyncFeedbackEnabled;
  root.innerHTML = '';
  ROOT_STATE.delete(root);
}
